/**
 * @steward-app/steward-types — the canonical steward-app CustomType definitions.
 *
 * Single source of truth for the workspace type schema (document, account, asset,
 * valuation, contact, credential, …). Consumed by:
 *   - `scripts/seed-steward-app.ts` — deploy/dev seeding into a fixed workspace.
 *   - `services/steward-app-api` — seeds these into a NEW workspace at founder
 *     bootstrap / add-workspace (a fresh `custom-data` workspace would otherwise be
 *     typeless and every entity page would 404 "CustomType not found").
 *
 * The shapes match appcore's `CustomTypeInputSchema` (the `POST /w/{ws}/types/seed`
 * request item) — typed loosely here so this package stays free of an `@appcore/core`
 * dependency; appcore is the validating authority on write.
 *
 * ⚠️ **A field added here does not exist until the App Pack is re-seeded.** Appcore
 * validates a write against the STORED `CustomType` row in `APP#steward-app`, not
 * against this file. Under strict mode an undeclared attribute is rejected outright
 * (appcore decisions log §51), so a stale pack 400s every write carrying a new field.
 * `deploy.yml`'s `seed-pack` job does that re-seed (path-filtered on this package, and
 * unconditional on a stage/prod dispatch) and `api`/`eod`/`spa` all wait on it — but
 * still **verify with a real write per environment**, since a green deploy only proves
 * CloudFormation succeeded. See the README's "Adding or renaming a CTYPE field".
 *
 * A second place has to move with this one, and nothing about it fails loudly: the
 * façade's enumerated `AssetAttributesSchema`
 * (`services/steward-app-api/src/handlers/assets.ts`) is a plain `z.object`, so a field
 * missing there is stripped before appcore ever sees it and can never be saved. A guard
 * test in that service asserts the two lists agree.
 */

import { ADVISOR_CLIENT_TYPE_CODE } from './advisorClient.js';
import { ASSET_ADDRESS_FIELDS, ASSET_PROPERTY_DETAIL_FIELDS } from './assetClasses.js';
import { ASSET_EVENT_KINDS, ASSET_EVENT_NOTES_MAX, ASSET_EVENT_PART_MAX, ASSET_EVENT_TITLE_MAX } from './assetEvent.js';
import { CLIENT_ABOUT_TYPE_CODE } from './clientAbout.js';
import { CLIENT_PURGE_STATES, CLIENT_PURGE_TYPE_CODE } from './clientPurge.js';
import { CURRENCY_CODE_PATTERN } from './currency.js';
import { DRAFT_DISMISSAL_TYPE_CODE } from './draftDismissal.js';
import { FIRM_CLIENT_DOCUMENT_TYPE_CODE, FIRM_CLIENT_DOCUMENT_KEY_PREFIX } from './firmClientDocument.js';
import { DOCUMENT_CUSTODY_VERB } from './folderCustody.js';
import { INTAKE_DRAFT_TYPE_CODE } from './intakeDraft.js';
import { INTAKE_INVITATION_TYPE_CODE } from './intakeInvitation.js';
import { ONBOARDING_INVITATION_TYPE_CODE } from './onboardingInvitation.js';
import {
  ONBOARDING_DETAILS_TYPE_CODE, ONBOARDING_PREFILL_TYPE_CODE, ONBOARDING_DETAILS_PACK_FIELDS,
  ONBOARDING_PREFILL_PACK_FIELDS,
} from './onboardingForm.js';
import { ONBOARDING_MARK_TYPE_CODE } from './onboardingMark.js';
import { CLIENT_ACCOUNT_NUMBER_MARK_TYPE_CODE } from './clientAccountNumberMark.js';
import { CLIENT_INFO_REQUESTS_TYPE_CODE, CLIENT_INFO_REQUESTS_LOOKUP_PATTERN } from './clientInfoRequests.js';
import { CLIENT_ARCHIVE_TYPE_CODE } from './clientArchive.js';
import { CLIENT_FORM_CLOSURE_TYPE_CODE } from './clientFormClosure.js';
import { CLIENT_STEWARD_SETUP_TYPE_CODE } from './clientStewardSetup.js';
import { FIRM_NW_TYPE_DEFS } from './firmNetWorthTypeDefs.js';
import { CLIENT_RECORD_TYPE_DEFS } from './clientRecordTypeDefs.js';
import { CASH_FLOW_TYPE_DEFS } from './cashFlowTypeDefs.js';
import {
  ADVISOR_CLIENT_WRITE_CAPABILITY, ONBOARDING_REQUEST_CAPABILITY, ONBOARDING_REQUEST_WRITE_CAPABILITY,
  STEWARD_WRITE_CAPABILITY, ONBOARDING_DETAILS_WRITE_CAPABILITY, ONBOARDING_PREFILL_WRITE_CAPABILITY,
} from './packRoles.js';
import { PEOPLE_REVEAL_TAX_ID_CAPABILITY } from './pillars.js';

export * from './advisorClient.js';
export * from './advisorFirmDirectory.js';
export * from './advisorFirms.js';
export * from './assetClasses.js';
export * from './assetEvent.js';
export * from './assistantAccess.js';
export * from './backfillFloor.js';
export * from './snapshotHistoryMarker.js';
export * from './businessDate.js';
export * from './cashFlowAccess.js';
export * from './cashFlowTypeDefs.js';
export * from './cashFlowAgreement.js';
export * from './clientAbout.js';
export * from './connectionError.js';
export * from './clientPrefillMessage.js';
export * from './clientPurge.js';
export * from './connectRequester.js';
export * from './clientStewardSetup.js';
export * from './aiConsent.js';
export * from './credentialVault.js';
export * from './termsAcceptance.js';
export * from './currency.js';
export * from './displayText.js';
export * from './personName.js';
export * from './hasVisibleContent.js';
export * from './documentFilename.js';
export * from './draftDismissal.js';
export * from './firmClientDocument.js';
export * from './folderCustody.js';
export * from './intakeClientWorkspace.js';
export * from './intakeDraft.js';
export * from './intakeLead.js';
export * from './testerFeedback.js';
export * from './intakeEmailVerification.js';
export * from './intakeFirmView.js';
export * from './intakeInvitation.js';
export * from './intakeLinkCore.js';
export * from './intakePrefill.js';
export * from './personSelfLinkView.js';
export * from './intakeRowId.js';
export * from './intakeSnapshot.js';
export * from './intakeStatements.js';
export * from './intakeRemoval.js';
export * from './intakeKept.js';
export * from './intakeDebtAccounts.js';
export * from './intakeMessage.js';
export * from './intakeSchema.js';
export * from './intakeRouting.js';
export * from './intakeLabels.js';
export * from './firmNetWorthSeries.js';
export * from './firmNetWorthAccess.js';
export * from './liabilityBalance.js';
export * from './liveStatus.js';
export * from './logSafety.js';
export * from './onboarding.js';
export * from './onboardingInvitation.js';
export * from './onboardingForm.js';
export * from './answerPrecedence.js';
export * from './questionCatalogue.js';
export * from './unifiedQuestions.js';
export * from './clientAnswers.js';
export * from './clientLoginBinding.js';
export * from './clientRecordOpen.js';
export * from './clientRecordForm.js';
export * from './clientConnected.js';
export * from './onboardingTrustedContact.js';
export * from './onboardingMark.js';
export * from './clientAccountNumberMark.js';
export * from './clientInfoRequests.js';
export * from './clientAccountNumbers.js';
export * from './clientArchive.js';
export * from './clientFormClosure.js';
export * from './packRoles.js';
export * from './pillars.js';
export * from './planLabels.js';
export * from './areaList.js';
export * from './sponsoredFloor.js';
export * from './sponsoredInvitation.js';
export * from './sharedFolderProvenance.js';
export * from './clientShare.js';
export * from './roles.js';
export * from './sponsoredStart.js';
export * from './keptBringIn.js';
export * from './unfiledFolder.js';
export * from './unsafeCharacters.js';
export * from './latestPrice.js';
export * from './licensedPriceSources.js';
export * from './latestValuation.js';
export * from './valuationInstanceId.js';
export * from './valuationSource.js';

/** A steward-app type definition. A permissive superset of appcore's CustomTypeInput
 *  (the platform validates on write); `schemaMode` defaults to `strict` at the API. */
export interface FoTypeDef {
  typeCode: string;
  name: string;
  schemaMode?: 'strict' | 'schemaless';
  /**
   * appcore's engagement-authorized write (crossmarc/appcore#679, `CustomType.engagementWrite`):
   * the capability a caller's FIRM-tenant memberships must carry to create and update this
   * type's one firm-keyed row in a client workspace. Honoured on a PACK row only, and carried
   * onto it verbatim by the seed (`scripts/lib/packSeed.ts` `packCtypeItem`). Named here rather
   * than left to the index signature so a misspelt key is a compile error, not a silent 403.
   */
  engagementWrite?: { firmCapability: string };
  /**
   * appcore's singleton custom type (crossmarc/appcore#964, `CustomType.singletonInstanceId`):
   * the member-plane `POST /w/{ws}/entities/{type}` creates this type at this fixed instance id
   * rather than minting a ULID, and a second create is 409 `INSTANCE_EXISTS`. Honoured on a PACK
   * row only, carried onto it verbatim by the seed (`packCtypeItem`). appcore refuses it beside
   * per-row visibility (`accessControl.ownership`, `parent`, `readVerb`, `custodyVerb`),
   * `engagementWrite` and `file-backed`. Named here so a misspelt key is a compile error.
   */
  singletonInstanceId?: string;
  [key: string]: unknown;
}

/**
 * Stored in `account-connection.institutionName` when the provider's link flow did not
 * name the institution (Plaid's `onSuccess` metadata carries `institution: null` in some
 * flows). The field is `required: true` and is the type's `displayField`, so the
 * connections list needs *something* renderable — but this is a UI placeholder, NOT a
 * bank, and consumers must not treat it as one.
 *
 * In particular the account sync must not stamp it onto synced accounts as their
 * institution: two unrelated banks whose metadata was missing would then merge into a
 * single bogus "Connected account" group with combined subtotals. Falling back to the
 * per-account name instead leaves them ungrouped, which is untidy but not *wrong*.
 * Exported so the producers (SPA link handlers) and the consumer (account-sync) agree
 * on one literal instead of drifting apart on a copy-pasted string.
 */
export const UNNAMED_INSTITUTION = 'Connected account';

/**
 * The `onboarding-request` type code (steward-app#1426). One exported home, because the
 * façade will name it in an appcore route path and a typo there is a 403
 * `ENGAGEMENT_WRITE_NOT_DECLARED`, not a compile error.
 */
export const ONBOARDING_REQUEST_TYPE_CODE = 'onboarding-request';

/**
 * The `onboarding-confirmation` type code (steward-app#1426): the family's "Done, send to my
 * advisor" answer to a firm's `onboarding-request`. One exported home, for the reason above.
 */
export const ONBOARDING_CONFIRMATION_TYPE_CODE = 'onboarding-confirmation';

/**
 * App Pack ids (decisions log §PACK). One exported home for both, so a second pack
 * doesn't repeat the hand-typed-string mistake the first one made (steward-app#734):
 * `'steward-app'` used to be a local `const` re-declared in six files across
 * `services/` and `scripts/`.
 *
 * steward-app deliberately does not depend on `@appcore/core` (see the package doc
 * comment above), so these are hand-typed here rather than imported — appcore's
 * `PACK_IDS` (`packages/core/src/entities/pack.ts`) is the authority on the value;
 * keep this in sync with it by hand.
 */
export const PACK_IDS = {
  STEWARD_APP: 'steward-app',
  STEWARD_ADVISOR_FIRM: 'steward-advisor-firm',
} as const;

/**
 * The CAPABILITY-shaped disclosure tier: for a type whose `sensitive` fields are released
 * by a dedicated ROUTE rather than by a container ACL, the capability that route requires.
 *
 * ## Why this table exists at all
 *
 * appcore's reveal tier, as built, is VAULT-shaped: a `sensitive` field is protected by
 * `accessControl.parent` plus `accessControl.custodyVerb`, and the custody verb is checked
 * against an ACL on the parent container. `credential` has one; `person` has no parent and
 * must not grow one, and `assertSensitiveParentHasCustody` (`entities/custom-type.ts`)
 * returns EARLY for a parentless type — so a root-level type declaring `sensitive` passes
 * appcore's config time with no complaint and nothing enforced (crossmarc/appcore#658).
 *
 * That left "the disclosure tier for `person` is a capability on a route in another
 * repository" as a claim no test in this one could reach. This table is what makes it
 * checkable HERE: `encryptionClaims.test.ts` requires every type declaring a `sensitive`
 * field to declare a tier in one of exactly two shapes, and an entry here is half of the
 * second shape (the other half is the type's own `accessControl.readCapability`, which is
 * what makes the reveal require read as well).
 *
 * ## What an entry does and does not assert
 *
 * It asserts that SOMEWHERE a route gates this type's disclosure on this capability, and it
 * names the string so the two repos cannot drift silently apart. It does NOT enforce the
 * gate — appcore's `revealPersonTaxId` does that — and adding a row here for a type whose
 * route does not exist would be a false claim, which is why the test also requires the row
 * to belong to a type that actually declares a `sensitive` field.
 *
 * ## When this folds away
 *
 * appcore#658 step 2 proposes `accessControl.revealCapability` as a first-class field on the
 * type declaration. On the day that exists, this table becomes the declaration and the test
 * reads it off the type instead. Noted on #658.
 *
 * Point-in-time, 2026-09-15 (this branch): one entry. `credential` and `credential-vault`
 * are deliberately absent — they are the vault shape, and listing them here would say the
 * opposite of what is true about them.
 */
export const REVEAL_CAPABILITY_BY_TYPE: Readonly<Record<string, string>> = {
  person: PEOPLE_REVEAL_TAX_ID_CAPABILITY,
};

/**
 * ── Arming the sharing pillars (steward-app#739 phase 2b, #1260 phase 2) ──────────────
 *
 * Fourteen of the type definitions below declare `accessControl.readCapability` with one
 * of the FIVE pillar capability strings from `pillars.ts` (counted at this branch's HEAD on
 * 2026-09-14; `pillars.test.ts` pins the exact list as a literal, so that census is the
 * authority and this sentence is a pointer to it). That declaration is what makes
 * a pillar bite: appcore's `screen()` (`services/platform-api/src/access.ts`) requires
 * every `readCapability` on the access-parent chain, on TOP of `inst:read` and any Tier-3
 * ACL, before a caller may list, get, download, write or destroy an instance. An advisor
 * whose membership does not carry the matching `steward-share-*` role therefore cannot
 * reach the area at all, and a list degrades to an empty 200 rather than a 403.
 *
 * **All FIVE `SHARING_PILLARS` entries are armed below as of 2026-09-14.** The fifth,
 * `people`, reached the table one release EARLIER carrying no declaration on its type
 * (`person`), which was phase 1 of the two-deploy ordering this section's point 1
 * describes, not a gap: `pillars.ts`'s `PILLAR_GRANTED_NOT_YET_ARMED` named it for the
 * window and `pillars.test.ts` asserted, in the opposite direction, that nothing here
 * declared `people:read` while it did. That list is empty again, which is its steady state.
 * So a future pillar appearing in `pillars.ts` with nothing armed here is the sequence
 * working, not a missing line to helpfully add.
 *
 * The `credential`/`credential-vault` pair is the worked example this copies, NOT a fifth
 * pillar: `credential:read` is withheld from `steward-advisor` by a fixed rule (#639/#643)
 * and must never become a client toggle. It is untouched here.
 *
 * `chat-session` is the SECOND fixed rule and reads the same way: it declares
 * `assistant:read` (steward-app#932 phase 2, `assistantAccess.ts`), granted to the three
 * family roles and withheld from `steward-advisor` unconditionally. So a `readCapability`
 * on a type below is not evidence of a pillar — check `pillars.ts` before assuming a
 * string is client-toggleable, and check `PILLAR_UNGATED_TYPE_CODES` before assuming an
 * ungated-by-pillar type is ungated at all.
 *
 * ## Four things a later author has to know before editing one of these lines
 *
 * 1. **This gate binds EVERY reader, not just advisors — and since appcore#603 that
 *    includes every ADMIN.** The role half of the 2026-08-27 source reading in
 *    `appcore-stage` and `appcore-prod` still holds and is what this point rests on:
 *    `steward-admin`, `steward-member` and `steward-viewer` each hold all four strings and
 *    `steward-advisor` holds none.
 *
 *    ⚠️ **The second half of that reading was a mechanism this gate no longer has.** It
 *    said every membership row carried `workspace-admin` or `steward-admin`, each holding
 *    the literal `mem:manage` that cleared `screen()` before any capability check — a
 *    second cover behind the grants. appcore#603 removed `mem:manage` from
 *    `UNCONDITIONAL_CAPABILITIES`, leaving `['*:*']`, and #605 deployed it (read at source
 *    in the crossmarc/appcore checkout at `0d18c78` on 2026-09-08, for steward-app#1254).
 *    So there is one cover now, not two: **these four declarations bind an admin exactly
 *    as they bind a viewer.** `mem:manage` still exempts its holder from Tier-3 ACL
 *    exclusion (`ACL_ADMINISTRATOR_CAPABILITY`), which is a different tier and cannot
 *    satisfy a `readCapability` — that constant's own first stated limit.
 *
 *    ⚠️ The old note went on to say `hasBreakGlass` was an exact `includes` with no
 *    wildcard expansion, so `tenant-admin`'s `mem:*` did not clear it. **Do not carry that
 *    reasoning over to the ACL tier**, where it is false: `isAclAdministrator` matches with
 *    `matchCapability`, deliberately, so `mem:*` DOES administer ACLs. Exact matching
 *    survives only on `hasUnconditionalCapability`, whose set is now `['*:*']`.
 *    `tenant-admin` is safe here for the reason the old note already gave as the better
 *    one: it holds no `inst:*` at all, so a tenant-admin-only membership could never read
 *    an instance here in the first place.
 *
 *    `workspace-editor`, `workspace-operator`, `workspace-viewer` and now `workspace-admin`
 *    are the hazard: they hold `inst:read` (three of them `inst:write` too) and no pillar
 *    string, so a workspace granting one loses all four areas for that member. The SPA's
 *    role picker cannot mint the first three — but the façade's `POST /invites` and
 *    `PUT /members/{id}` take an unallowlisted `roleIds` array, so the API can, and
 *    `workspace-admin` is ordinary. **A membership census is a dated reading, not a
 *    property** — steward-app#1221 refuted the broad version of the one this point used to
 *    carry — so re-read at source before relying on one.
 *
 * 2. **A pillar must be declared on every type in its domain, container included.** The
 *    lesson of `credential`: gating the child alone still lets a caller list the parent,
 *    and the parent is often the row whose mere existence discloses the thing. So
 *    `document-folder` carries `documents:read` alongside `document`, and `account` /
 *    `account-connection` carry `portfolio:read` alongside the rows they hold.
 *
 * 3. **`readCapability` is required on WRITE too.** appcore's position is "you may not
 *    touch what you may not read", so `requiredCapabilities` adds it under both intents.
 *    Withholding a pillar from a firm withholds their ability to WRITE into it as well —
 *    which for Documents means the "Shared with «firm»" drop-box, not only the family's
 *    files.
 *
 * 4. **Nothing stops these declarations being dropped again, so the test is the guard.**
 *    appcore has a drop-ratchet (`assertReadCapabilityRatchet`) that refuses a seed which
 *    removes a stored `readCapability` — but it runs only in `CustomTypeRepository.update`,
 *    i.e. the API plane (`PUT /types/{code}`, `POST /types/seed`), and **this pack does not
 *    use it.** `scripts/seed-steward-app.ts` writes CTYPE rows straight to DynamoDB through
 *    `scripts/lib/packSeed.ts`'s `upsertCtype`, which Puts the WHOLE row; a definition that
 *    no longer carries the key simply overwrites one that did. So reverting these lines and
 *    re-running `seed-pack` really does disarm all 13 types, silently and completely.
 *    `pillars.test.ts`'s exact-literal assertion is the only thing standing in the way.
 *    (Corrected in this PR's review — an earlier draft of this paragraph claimed the
 *    ratchet applied and that rollback needed an operator's direct write. Both wrong, and
 *    wrong in the reassuring direction.)
 *
 * ⚠️ **The `/system/*` SigV4 plane is NOT gated by any of this** — `handlers/
 * system-entities.ts` runs nothing from `access.ts`. The honest claim is "your advisor
 * cannot see this", never "nothing can see this". The background Lambdas (account-sync,
 * snapshot-calculator, the AVM refresher) write these types over that plane and are
 * unaffected.
 *
 * The per-pillar type lists live in ONE place, `pillars.ts`'s `SHARING_PILLARS`, and
 * `pillars.test.ts` asserts that this file's declarations agree with it exactly in both
 * directions. Adding a `readCapability` here without adding the type to the table reds
 * that suite, and so does the reverse.
 *
 * ── Arming the write axis (steward-app#742 steps 2 and 3) ─────────────────────────────
 *
 * Two declarations land together below, and they are two different controls with two
 * different arming conditions. Neither is a pillar, and neither is client-toggleable.
 *
 * ## 1. `custodyVerb: DOCUMENT_CUSTODY_VERB` on `document` and `document-folder` (step 2)
 *
 * The ELEVATED tier of the folder's own ACL. Once declared, two operations stop being
 * satisfied by `readVerb` and require an ACL entry carrying `custody` instead: DESTROYING
 * an instance, and CHANGING its access parent in either direction, INCLUDING detaching it
 * to a root (appcore `services/platform-api/src/access.ts`, `assertWritable`'s custody
 * block and `assertDestroyable`). Everything else — list, get, download, metadata edit —
 * stays at `readVerb`. `credential`/`credential-vault` are the worked example this copies:
 * a child that is not itself an ACL target, declaring `parent` + `custodyVerb` and no
 * `readVerb`, inheriting its custody verdict from the container.
 *
 * **The whole reason #742 needs it**: an advisor who is later given a write grant inside
 * the "Shared with «firm»" folder must not be able to delete the family's documents out of
 * it, nor re-file them to an unrestricted root and read them there.
 *
 * **Arming condition, and it is about live DATA, not about code.** appcore matches
 * per-verb against a per-target opt-in, so the moment a type declares a new verb every
 * EXISTING entry that lacks it stops satisfying the two operations above — the family
 * would lose delete and move inside their own shared folder. steward-app#1147 shipped the
 * grant half (every family ROLE entry this app writes now carries the verb, plus a repair
 * for entries seeded earlier); this declaration may only reach an environment whose
 * `scripts/backfill-folder-custody-verb.ts` dry run reads `safeToArm: true` THERE. See
 * `folderCustody.ts` for the full argument and appcore docs/06 §18.5 for the general rule.
 *
 * ⚠️ **One live behaviour change beyond the two gates, and it is silent.** appcore's
 * `carryForwardAccessParent` (`packages/core/src/entities/custom-type.ts`) fires only for a
 * `custodyVerb` type: from now on an update that OMITS the parent field preserves the
 * stored value instead of clearing it, and only an explicit empty/null value detaches.
 * That is deliberate on appcore's side (a metadata edit must not read as a move), but it
 * inverts what "move to the top level" has to look like on the wire. The SPA's
 * `moveFolder` used to express it by omitting `parentFolderId`; it now sends `''`
 * explicitly (`src/components/folders/useFolderTree.ts`), which every read site in this
 * repo already normalises to "no parent". Documents are unaffected: the façade merges
 * `folderId` from the stored record and refuses an explicit clear
 * (`handlers/documents.ts`), so a document's parent is never omitted and never emptied.
 *
 * ## 2. `writeCapability: STEWARD_WRITE_CAPABILITY` on every OTHER type (step 3)
 *
 * The write-axis mirror of `readCapability` (crossmarc/appcore#449): a Tier-1 capability
 * required IN ADDITION to `inst:write` and every Tier-3 verdict, on create, update, entity
 * action and destroy. It can only ever SHRINK the set of principals who pass a write gate;
 * it adds nothing to any read path (`requiredCapabilities`, access.ts:305-316, adds it
 * under the `'write'` intent only).
 *
 * **It is the PRIMARY control for the advisor contributor channel, not defence in depth.**
 * appcore's `screen()` short-circuits to ALLOW for a type declaring neither `readVerb` nor
 * `parent` (`confinementCovers`, access.ts:109-120), so appcore#553's per-role write
 * confinement reaches only four of the types below; and `advisor-connection` declared no
 * `accessControl` at all, leaving it writable by any holder of `inst:write`. Withholding
 * this string from the contributor role (step 4) is what closes the other types.
 *
 * **Declared on EVERY type except the two document types and `onboarding-request`** (which
 * declares `onboarding-request:write`, a string no role holds, so the family cannot move its
 * rungs on the generic plane: steward-app#1426), including the three that
 * carried no `accessControl` before (`feedback`, `advisor-intake`, `advisor-connection`)
 * and `intake-draft` in the advisor-firm pack. The document pair is excluded because it is
 * exactly the surface a contributor is MEANT to write into; the custody verb above is what
 * bounds them there. `stewardWriteCapability.test.ts` pins that census in both directions.
 *
 * **The `/system/*` plane is untouched by this, verified at source rather than assumed.**
 * `handlers/system-entities.ts` imports nothing from `access.ts` and calls no gate; its
 * authorization is SigV4 plus the actor allowlist, and `SYSTEM_CAPABILITIES` is the fixed
 * exact-match set `{inst:read, inst:write}` (appcore `packages/core/src/auth/
 * system-plane.ts:395-411`, which records this decision in as many words). So the types
 * written ONLY by background actors — `advisor-intake` and `intake-draft` by the intake
 * writer, `market-price`/`net-worth-snapshot`/`valuation` by the schedulers — are armed on
 * the same uniform rule as the rest, with no exclusion list, because arming them
 * constrains nothing those actors do. It closes the USER plane on them, which is the only
 * plane a principal reaches.
 *
 * **Grant before arm, and here it is MECHANISED.** `steward:write` was granted to the
 * ADMIN and MEMBER roles by steward-app#1127 (`packRoles.ts`); those are the only two roles
 * in either pack holding `inst:write`, and `stewardWriteCapability.test.ts` asserts that
 * pairing over the role table rather than by hand. Across environments the ordering is
 * enforced by `scripts/lib/packArming.ts`: a release that arms a capability an EXISTING
 * role row in the target does not yet grant produces a `armed-capability-not-granted`
 * finding, which is BLOCKING in both the pre-flight and the verify pass of `deploy.yml`.
 * An environment whose pack has not been re-seeded since #1127 therefore refuses this
 * release rather than serving a stale cached role against an armed type.
 *
 * ⚠️ **Both of those guards reason over PACK roles only, and the same hazard point 1 of the
 * read-axis section above records applies here — harder, and since appcore#603 it reaches
 * one more role.** `workspace-editor`, `workspace-operator` and `workspace-admin` are
 * appcore built-ins holding `inst:write`, and being built-ins none of them can ever hold
 * `steward:write`. A workspace granting one loses every write on the 18 armed types.
 * `check-pack-arming.ts` cannot see it — it GetItems only the role ids the release
 * declares, and reads no membership row — so this is verified by looking, not by CI.
 *
 * ⚠️ **`workspace-admin` is on that list because appcore#603 put it there, and the text it
 * replaces argued the opposite.** The old version excluded the first two on the strength of
 * `hasBreakGlass` being an exact `includes` that `mem:*` did not satisfy, and closed with
 * "every prod principal that reaches `inst:write` also holds the literal `mem:manage`" —
 * which read as reassurance and would now be a statement of the exposure. `mem:manage` is
 * no longer in `UNCONDITIONAL_CAPABILITIES`; it is `['*:*']` (read at source in the
 * crossmarc/appcore checkout at `0d18c78` on 2026-09-08, for steward-app#1254). So an admin
 * needs `steward:write` spelled out like anyone else. `steward-admin` grants it
 * (`packRoles.ts`); a membership holding only appcore platform roles does not.
 *
 * The 2026-09-02 role-id census that stood here is removed rather than re-dated:
 * steward-app#1221 refuted the membership claim it fed, in prod, and a census left in place
 * reads as an invariant long after it stops being one. **Re-read the memberships at source
 * before arming a NEW environment, drain the pagination, and record whose reading it was** —
 * and remember the façade's `POST /invites` and `PUT /members/{id}` both take an
 * unallowlisted `roleIds` array, so one API call is all it takes to create the case.
 */
export const STEWARD_APP_TYPE_DEFS: readonly FoTypeDef[] = [
  {
    typeCode: 'document',
    name: 'Document',
    // Versioned (DOC1/DE1): each new file upload snapshots the prior head into a
    // #ver# row, retaining prior S3 objects. The s3Key is server-authoritative
    // (set by the appcore file-backed handler), never client-supplied.
    // file-backed (OBJ1): opts into S3 object storage via the generic appcore
    // documents/files handler. keyPrefix `documents` preserves every pre-OBJ1 key
    // (`documents/<entityId>/<version>/<file>`).
    behaviors: ['versioned', 'file-backed'],
    fileBacked: { keyPrefix: 'documents' },
    // crossmarc/steward-app#2439 (founder decision 2026-10-16): an advisor can now put a file
    // into a client's vault, and "an audit row naming the advisor is enough" record of who
    // did. As read at appcore d6923c6 on 2026-10-16: under `standard`, a create, update or
    // delete writes an AUDIT row naming `ctx.userId` as `actorUserId` AFTER the write commits
    // (`entity-instance-repository.ts` `auditPlan` -> `fireStandard`, `audit.ts`
    // `emitAuditStandard`), and a download writes one too. The row is best-effort: the
    // mutation path `void`s it, so a failed put (and its retries) or a Lambda freeze can lose
    // it, and a lost row never fails the action.
    // Deliberately `standard`, not `strict` (coordinator decision 2026-10-16, provisional for
    // Matt): this level is global to EVERY document in every workspace, and `strict` would make
    // every family's download await a synchronous audit put that fails the download when it
    // fails, and bundle a row into every create, update and delete transaction. The founder
    // asked for a row naming the advisor, not a guaranteed one. Revisit if that changes.
    // What a row carries IN THE CLEAR: appcore's platform allow-list (`audit-redaction.ts`
    // `PLATFORM_AUDIT_SAFE_FIELDS`) applies to custom types too and names `title`,
    // `category`, `folderId`, `filename`, `mimeType` and `fileSize`, so a mutation delta
    // records those values raw and redacts every other field (this type marks none
    // `auditSafe` of its own); a download row carries the S3 key, whose last segment is the
    // filename. Both stay in the same workspace's audit trail and outlive a deleted document.
    // Only the literal is pinned here (`auditLevel.test.ts`); the appcore behaviour is
    // appcore's to test.
    auditLevel: 'standard',
    // Folder access control: documents are not grant targets of their own — they
    // inherit visibility entirely from their containing folder (`folderId`). The
    // generic appcore access engine gates list/get/download/versions accordingly.
    // steward-app#739 phase 2b — `documents:read` armed, and declared on BOTH this type
    // and `document-folder` below. The folder is the access parent, so one string would
    // already gate the documents inside it — but a folder is also the row whose mere
    // NAME discloses what the family keeps, exactly as a vault names an institution
    // (#643). See "Arming the sharing pillars" above.
    // steward-app#742 step 2 — the custody tier, ARMED. The child half of the pair: a
    // document is not an ACL target of its own, so its custody verdict is the folder's,
    // evaluated at `custody` up the whole chain. Destroying a document, and moving it
    // between folders (or out of one entirely), now need an entry carrying that verb;
    // reading, downloading and editing its metadata still need only `read`. The constant,
    // never the literal — the string the façade WRITES onto ACL entries and the string
    // declared here must be the same bytes, and nothing type-checks that pair.
    // Deliberately NO `writeCapability`: this and `document-folder` are the one surface an
    // advisor contributor is meant to write into (see the arming docblock above).
    accessControl: {
      parent: { field: 'folderId', typeCode: 'document-folder' },
      custodyVerb: DOCUMENT_CUSTODY_VERB,
      readCapability: 'documents:read',
    },
    fields: [
      { name: 'title',       type: 'string', widget: 'text',     required: true,  label: 'Title' },
      { name: 'category',    type: 'string', widget: 'text',     required: true,  label: 'Category' },
      { name: 'description', type: 'text',   widget: 'textarea', required: false, label: 'Description' },
      { name: 'filename',    type: 'string', widget: 'text',     required: true,  label: 'Filename' },
      { name: 'mimeType',    type: 'string', widget: 'text',     required: true,  label: 'MIME type' },
      { name: 'fileSize',    type: 'number', widget: 'number',   required: true,  label: 'File size (bytes)' },
      { name: 's3Key',       type: 'string', widget: 'text',     required: true,  label: 'S3 object key' },
      { name: 'tags',        type: 'array',  widget: 'tags',     required: false, label: 'Tags' },
      { name: 'expiresAt',   type: 'date',   widget: 'date',     required: false, label: 'Expires at' },
      // `required: true` since steward-app#811 step c. The three things that had to be
      // true first are true: the Unfiled backfill has run against dev, stage and prod
      // (#813) and left zero unfoldered documents in any of them; the façade refuses a
      // create with no folder and refuses an explicit clear on update (#815, #838); and
      // every SPA write path sets it (#814).
      //
      // WHAT THIS FLAG ACTUALLY DOES. appcore compiles a pack's field defs to a Zod
      // object — `field.required ? base : base.optional()`, appcore
      // packages/core/src/db/zod-compiler.ts:100 — and runs it in `validateAttributes`
      // on create and on any update that SENDS an attributes bag, on the user plane
      // (`createEntity`/`updateEntity`, handlers/entities.ts) and the `/system/*`
      // machine plane alike (`systemCreateEntity`/`systemUpdateEntity`,
      // handlers/system-entities.ts). A write missing the key is a 400
      // `VALIDATION_ERROR`. It is a real gate on both planes, not a declaration.
      // Cited by function rather than by line: these are another repo's line numbers,
      // which drift silently from here, and an earlier draft of this very comment
      // shipped four that matched no revision of either file.
      //
      // It is never checked on READ. No read or list path in appcore touches `required`,
      // so a stored row that lacks the key is still returned exactly as stored. The
      // comment this replaces said the flip "rejects every stored row that has not been
      // backfilled" — that was wrong about reads, and right only about the NEXT WRITE to
      // such a row.
      //
      // ⚠️ IT DOES NOT CATCH `folderId: ''`. A `reference` compiles to a bare
      // `z.string()` (zod-compiler.ts:80-82; the `minLength` branch covers only
      // `string`/`text`), and Zod accepts the empty string — so an empty `folderId`
      // satisfies `required: true` while `isUnfoldered` counts it as unfiled and
      // appcore's access engine normalises it to an unrestricted parentless root
      // (`access.ts`: "Falsy covers null/undefined/''"). The empty string is a REAL
      // unfiled shape and this flag does not stop it.
      //
      // `usableFolderId` in the façade's handlers/documents.ts rejects it — but only
      // for callers who go THROUGH the façade, and the façade is not a boundary. The
      // reason it is not has moved (steward-app#1278) and the ruling has not: it used to
      // be that the façade forwarded the member's own bearer token verbatim and appcore's
      // user plane was publicly addressable, so a member holding `inst:write` could send
      // `folderId: ''` straight to appcore. Under `memberPlane: "relay-only"` appcore
      // binds those routes to the IAM authorizer and takes no browser token — the state of
      // dev, stage and prod as read at source on 2026-09-08 (`docs/02-facade-api.md` §5).
      // That is per-instance config one deploy reverses, and the fix still belongs upstream
      // either way. Closing that needs a `z.string().min(1)` for a required
      // `reference` in appcore's compiler, which is where the fix belongs and is filed
      // as crossmarc/appcore#453 — this line cannot reach it. Do not read this flag as
      // covering the empty string.
      //
      // WHAT WOULD MAKE IT UNSAFE AGAIN: a stored document with no folder reappearing —
      // a restore from before the backfill, a replayed write, or a direct-to-DynamoDB
      // writer (`scripts/seed-sample-data.ts` is the one in this repo, and its
      // pack-alignment guard now enforces this field for exactly that reason).
      //
      // Be precise about what then happens, because the façade's update path carries a
      // comment that reads as if it survives this. It does not, and steward-app#742 step 2
      // does not rescue it. `carryForwardAccessParent` now fires for this type (it declares
      // both an access `parent` and a `custodyVerb`), but its second guard is
      // `!(field in current)` — a row that never had a `folderId` has nothing to carry
      // forward, so the merged bag is still folderless and still 400s at appcore. A legacy
      // row's TITLE EDIT therefore fails too, not just an attempt to clear its folder.
      // (What the carry-forward DID change is the ordinary case: an update that omits
      // `folderId` on a row that HAS one now preserves it rather than detaching. The façade
      // merges the stored value anyway, so no façade caller could reach that path.)
      // That fails closed and is the right direction, but it is a real behaviour change and
      // nobody should rediscover it from a support ticket. Keeping the façade's refusal
      // narrow is still correct: an explicit clear is the harmful gesture and deserves the
      // specific message. It just no longer buys the legacy row an editable title.
      { name: 'folderId',    type: 'reference', widget: 'reference', required: true,  label: 'Folder' },
    ],
    accessPatterns: [
      { name: 'by_category', fields: ['category'] },
      { name: 'by_folder',   fields: ['folderId'] },
    ],
    presentation: {
      displayField: 'title',
      listColumns: ['title', 'category', 'filename', 'fileSize', 'updatedAt'],
      defaultSort: { field: 'updatedAt', direction: 'desc' },
    },
  },
  {
    // DOC3/Q3 — document folders. A hierarchy of plain containers; nesting is via
    // `parentFolderId` (null/absent = top level). Documents point at one via
    // `document.folderId`. Just a generic entity type — appcore CRUD handles it.
    typeCode: 'document-folder',
    name: 'Document Folder',
    // Access-controlled (folder fast-follow): a folder's own ACL (verb `read`) hides
    // the folder, and restriction cascades down `parentFolderId` to subfolders and
    // their documents. Opt-in (a folder with no ACL stays open).
    //
    // ⚠️ This line used to end "admins break-glass", which since appcore#603/#605 says
    // more than the code does (steward-app#1254). What an admin gets is the Tier-3 ACL
    // exemption only — `ACL_ADMINISTRATOR_CAPABILITY`, "you cannot be locked out of an
    // ACL you administer" — so a `mem:manage` holder is not EXCLUDED by an ACL that does
    // not name them. It is not a bypass: it cannot satisfy the `readCapability` below, so
    // the caller must still hold `documents:read` on their own. `steward-admin` does
    // (`packRoles.ts` grants every `PILLAR_CAPABILITIES` member); appcore's platform
    // `workspace-admin` does not, and what that means for a family-only folder is the
    // open question recorded on `familyRoleIds` in `handlers/advisor.ts`.
    // ACLs are managed via /w/{ws}/acl/document-folder/{folderId}.
    behaviors: ['access-controlled'],
    // steward-app#739 phase 2b — `documents:read` armed; the container half of the pair
    // described on `document` above.
    // steward-app#742 step 2 — the custody tier, ARMED. The CONTAINER half, and the one
    // that actually holds the entries: deleting a folder, and re-parenting one (including
    // detaching it to the top level), now require an entry carrying `custody` at every
    // access-controlled ancestor, not merely at the nearest. `readVerb` stays `read`, so
    // seeing a folder and renaming it are unchanged. Deliberately NO `writeCapability` —
    // see `document` above and the arming docblock.
    accessControl: {
      readVerb: 'read',
      parent: { field: 'parentFolderId', typeCode: 'document-folder' },
      custodyVerb: DOCUMENT_CUSTODY_VERB,
      readCapability: 'documents:read',
    },
    fields: [
      { name: 'name',           type: 'string',    widget: 'text',      required: true,  label: 'Folder name' },
      { name: 'parentFolderId', type: 'reference', widget: 'reference', required: false, label: 'Parent folder' },
      // The provenance marker for folders this app creates for its own purposes rather
      // than at a family's request. TWO values, and this field is where they meet — each
      // has its own home, cited below, and neither may be compared loosely:
      //   - `'unfiled'` — the hidden folder that backs the root of the Documents page so
      //     that every document has a folder and is therefore reachable by a folder ACL
      //     (steward-app#811). `packages/steward-types/src/unfiledFolder.ts`;
      //   - `'advisor-shared:<firmId>'` — the "Shared with «firm»" folder, firm-scoped so
      //     one firm's backfill cannot adopt another firm's folder (steward-app#944).
      //     `services/steward-app-api/src/handlers/advisor.ts`.
      // ⚠️ Resolution is by THIS field and never by `name`, for BOTH: a family can rename,
      // move or delete any folder; a family who makes their own folder called "Unfiled"
      // must not collide with ours; and a name that merely RENDERS like ours is not one
      // this app can tell apart from the real thing. Compare it EXACTLY — a loose
      // comparison would also let the two values above collide with each other.
      { name: 'systemKey',      type: 'string',    widget: 'text',      required: false, label: 'System folder key' },
    ],
    accessPatterns: [{ name: 'by_parent', fields: ['parentFolderId'] }],
    presentation: {
      displayField: 'name',
      listColumns: ['name', 'updatedAt'],
      defaultSort: { field: 'name', direction: 'asc' },
    },
  },
  {
    typeCode: 'account',
    name: 'Account',
    // steward-app#739 phase 2b — armed. `pillars.ts` `SHARING_PILLARS` is the
    // authority on which pillar owns this type; see "Arming the sharing pillars" above.
    accessControl: { readCapability: 'portfolio:read', writeCapability: STEWARD_WRITE_CAPABILITY },
    fields: [
      { name: 'name',          type: 'string',    widget: 'text',      required: true,  label: 'Account name' },
      { name: 'institution',   type: 'string',    widget: 'text',      required: true,  label: 'Institution' },
      { name: 'accountType',   type: 'enum',      widget: 'select',    required: true,  label: 'Account type' },
      { name: 'currency',      type: 'string',    widget: 'text',      required: true,  label: 'Currency (ISO 4217)', validation: { pattern: CURRENCY_CODE_PATTERN } },
      // steward-app#424 (decided 2026-09-07): there is NO full `accountNumber` field. Only
      // credential secrets and the Plaid access token are field-encrypted, so a full account
      // number would sit in plaintext; nothing in the SPA, facade or account-sync ever wrote
      // one (a 2026-09-07 scan of dev, stage and prod found zero), and the hint below is what
      // every read path displays. Reintroduce a full value only as an encrypted field with an
      // audited reveal (#1260's pattern). `encryptionClaims.test.ts` pins the field's absence.
      { name: 'lastFourHint',  type: 'string',    widget: 'text',      required: false, label: 'Last 4 digits hint' },
      { name: 'notes',         type: 'text',      widget: 'textarea',  required: false, label: 'Notes' },
      // The aggregator's account id from the intake snapshot this row was derived from, stamped
      // by the onboarding prefill (`planPrefill`) on a row derived from a snapshot account and on
      // no other row. Server-managed: the façade's account routes drop a client's value and carry
      // the stored one forward (`SYNC_OWNED_ACCOUNT_KEYS`, `services/steward-app-api/src/syncProvenance.ts`).
      { name: 'intakeAccountId', type: 'string',  widget: 'text',      required: false, label: 'Intake snapshot account id' },
      { name: 'tags',          type: 'array',     widget: 'tags',      required: false, label: 'Tags' },
      { name: 'ownerEntityId', type: 'reference', widget: 'reference', required: false, label: 'Owner entity' },
      // steward-app#1936. The family member (a `person` record) who owns this, as an
      // alternative to `ownerEntityId`: one owner, never both, which the façade enforces
      // (`services/steward-app-api/src/personOwnerGate.ts`). `person` is read-gated on
      // `people:read` and this type is not, so the façade also withholds this value from a
      // caller without that capability. A plain reference, on `ownerEntityId`'s pattern.
      { name: 'ownerPersonId', type: 'reference', widget: 'reference', required: false, label: 'Owner (family member)' },
      { name: 'source',        type: 'string',    widget: 'text',      required: false, label: 'Source (manual, plaid, …)' },
      { name: 'externalId',    type: 'string',    widget: 'text',      required: false, label: 'Provider external id (aggregation key)' },
      { name: 'connectionId',  type: 'reference', widget: 'reference', required: false, label: 'Owning account-connection' },
      // steward-app#1428 — the account-side half of the tier-3 model docs/29:329-338
      // describes. Beneficiary designations belong HERE, not on `person`: an IRA has
      // beneficiaries, a person does not, and putting them on the person would fight
      // Portfolio the first time someone asks who inherits a given account.
      //
      // ⚠️ NO WRITER POPULATES THIS FIELD YET. The only truthful designation is the
      // custodian's own, mirrored read-only by #1018 (the Altruist Open API spike,
      // P3), which does not exist. Onboarding intent (what a family says they WANT)
      // is captured on the intake request artifact instead (docs/29 §"The second
      // form, split") and is never written here — a maintained copy Steward cannot
      // act on is exactly the drift this restructure exists to avoid. This field is
      // built now, ahead of #1018, so that landing the mirror is a writer, not a
      // schema migration. `src/components/BeneficiaryOfPanel.tsx` is the read-only
      // "Beneficiary of" roll-up meant to display it — built and tested, but NOT YET
      // WIRED into any route, because the person page it belongs on is #1425, still
      // in flight in parallel at the time this landed. Whoever lands #1425 imports it.
      //
      // ⚠️ steward-app#1444 (forgery via the account write route's fully permissive
      // attribute bag — `handlers/accounts.ts` had no field allowlist, unlike
      // `assets.ts`) was raised on this same PR's code review and closed in the same
      // PR: `handlers/accounts.ts` now runs this key through `carryProvenance`'s
      // `extraKeys` (`SYNC_OWNED_ACCOUNT_KEYS`, `syncProvenance.ts`), so a client's
      // own value is always discarded in favour of whatever is stored, in both
      // directions (a forged value is overwritten AND an omitted one is restored,
      // rather than silently deleted on the next edit). No writer sets this key
      // today, so that is presently a no-op in the common case — it only bites an
      // attempted forgery or a client that naively omits the field. This was fixed
      // ahead of the panel being wired to a route, not after.
      { name: 'beneficiaries', type: 'array', widget: 'textarea', required: false, label: 'Beneficiary designations ({personId, kind, sharePercent, asOfDate}), custodian-mirrored, no writer yet' },
    ],
    accessPatterns: [],
    presentation: {
      displayField: 'name',
      listColumns: ['name', 'institution', 'accountType', 'currency'],
    },
  },
  {
    typeCode: 'asset',
    name: 'Asset',
    // steward-app#739 phase 2b — armed. `pillars.ts` `SHARING_PILLARS` is the
    // authority on which pillar owns this type; see "Arming the sharing pillars" above.
    accessControl: { readCapability: 'portfolio:read', writeCapability: STEWARD_WRITE_CAPABILITY },
    fields: [
      { name: 'accountId',     type: 'reference', widget: 'reference', required: false, label: 'Account' },
      { name: 'name',          type: 'string',    widget: 'text',      required: true,  label: 'Asset name' },
      { name: 'assetClass',    type: 'string',    widget: 'text',      required: true,  label: 'Asset class' },
      { name: 'ticker',        type: 'string',    widget: 'text',      required: false, label: 'Ticker symbol' },
      { name: 'currency',      type: 'string',    widget: 'text',      required: true,  label: 'Currency (ISO 4217)', validation: { pattern: CURRENCY_CODE_PATTERN } },
      { name: 'ownerEntityId', type: 'reference', widget: 'reference', required: false, label: 'Owner entity' },
      // steward-app#1936: see `account.ownerPersonId`.
      { name: 'ownerPersonId', type: 'reference', widget: 'reference', required: false, label: 'Owner (family member)' },
      { name: 'notes',         type: 'text',      widget: 'textarea',  required: false, label: 'Notes' },
      { name: 'tags',          type: 'array',     widget: 'tags',      required: false, label: 'Tags' },
      { name: 'source',          type: 'string',    widget: 'text',      required: false, label: 'Source (manual, plaid, …)' },
      { name: 'externalId',      type: 'string',    widget: 'text',      required: false, label: 'Provider external id (aggregation key)' },
      { name: 'connectionId',    type: 'reference', widget: 'reference', required: false, label: 'Owning account-connection' },
      { name: 'valuationMode',   type: 'string',    widget: 'text',      required: false, label: 'Valuation mode (avm | stated) — real estate only' },
      // Position facts as the PROVIDER reports them (#392). Sync-owned, exactly like the
      // provenance triple above: account-sync writes them over `/system/*`, and the façade's
      // `AssetAttributesSchema` deliberately does not accept them, so the PORTAL can neither
      // set nor clear them (`carryProvenance` restores them on every update). That is a rail,
      // not a control — appcore's member plane still accepts them from an authenticated
      // member, exactly as it does the provenance triple (#192). See syncProvenance.ts for
      // why `sensitive: true` cannot be used to close it.
      //
      // They exist because quantity used to live ONLY in `asset-lot` rows, which the sync
      // does not write — so every synced holding read as quantity 0 and a market-priced one
      // was valued at `0 x price`. Lots are the family's own transaction history; a
      // provider's current position is a different fact and gets its own field rather than a
      // fabricated daily "purchase" row.
      //
      // ⚠️ `totalCostBasis` is the WHOLE position's cost (Plaid's `cost_basis`), not per unit
      // — deliberately NOT named `costBasis`, because `asset-lot.costBasis` is per-unit and
      // `computeAverageCostBasis` divides by quantity. Two fields one word apart meaning
      // different things is how a 100x cost-basis error gets shipped.
      { name: 'quantity',        type: 'number',    widget: 'number',    required: false, label: 'Quantity (provider-reported position)' },
      { name: 'totalCostBasis',  type: 'number',    widget: 'number',    required: false, label: 'Total cost basis of the position (provider-reported)' },
      // Display-only provenance (#393): the provider's own last price, in the PROVIDER'S
      // units — dollars per unit for most instruments, percent of par for a bond. It exists
      // so a holding no market source can quote still shows the family where its number came
      // from; a bare figure is how a value that is off by a third goes unnoticed for months.
      // ⚠️ Never multiply it by `quantity`: the units are not uniform. `resolveHoldingValue`
      // is the only place allowed to reason about them.
      { name: 'providerPrice',     type: 'number', widget: 'number', required: false, label: 'Provider-reported price' },
      { name: 'providerPriceAsOf', type: 'date',   widget: 'date',   required: false, label: 'Provider price as of' },
      // The latest valuation's inputs, kept on the asset row so a list read does not have to
      // drain every valuation in the workspace to find each asset's newest one
      // (steward-app#2321). SERVER-managed, like `providerPrice` above: declared here only so
      // appcore's strict mode accepts the stored attribute, never accepted from a client
      // (`AssetAttributesSchema` omits them) and carried forward on update by
      // `LATEST_VALUATION_ASSET_KEYS`. Not in `presentation.listColumns` or any form.
      // Deliberately NOT named `currentValue`: that is the enrichment's computed answer.
      // As of 2026-10-12 the writers are the façade (`assetLatestValuation.ts`), the nightly reconcile
      // (`snapshot-calculator/src/reconcile.ts`), account-sync, the real-estate refresher
      // (`@steward-app/appcore-system` `latestValuation.ts`) and the intake prefill planner
      // (`intakePrefill.ts`, at create). The façade reads them for display in the asset list and live net worth
      // (`storedLatestValuation.ts`); the writers read the stored value to decide whether to write.
      { name: 'latestValuationValue', type: 'number',    widget: 'number', required: false, label: 'Latest valuation value (server-managed)' },
      { name: 'latestValuationDate',  type: 'date',      widget: 'date',   required: false, label: 'Latest valuation date (server-managed)' },
      { name: 'latestValuationId',    type: 'string',    widget: 'text',   required: false, label: 'Latest valuation id (server-managed)' },
      // When account-sync first found this synced holding missing from the provider's feed
      // (steward-app#2813). SERVER-managed like the three above: written only by account-sync's
      // absence guard and cleared only by account-sync (the guard, or the sync update that
      // re-reports the holding), carried forward on member edits by `SYNC_OWNED_ASSET_KEYS`,
      // never accepted from a client. A holding is deleted only when this is 20-48h old and the
      // holding is still missing.
      { name: 'missingSince',         type: 'string',    widget: 'text',   required: false, label: 'Missing from provider feed since (server-managed)' },
      // Structured property address (F5), generated from the one registry the SPA, the
      // façade and the AVM refresher all read, so a part cannot exist in the form and be
      // missing from the CTYPE. This REPLACES the former free-text `address` string, which
      // was modelled as an AVM input rather than an attribute of the property. Deliberately
      // no dual-read and no backfill: the only real data is being re-entered by hand.
      ...ASSET_ADDRESS_FIELDS.map(f => ({
        name: f.name, type: 'string', widget: 'text', required: false,
        label: `Property ${f.label.toLowerCase()}`,
      })),
      // Property details beyond the address (steward-app#287). `propertyType` is the
      // dwelling kind, stored as our own code and mapped to the provider's spelling at
      // the AVM boundary. Generated from the same registry the SPA and the façade read,
      // so a field cannot exist in the form and be missing from the CTYPE.
      // `type` comes from the field's own `storedType` rather than being hardcoded:
      // these are numbers (square footage, year built, last sale price) and appcore
      // validates against the declared type, so emitting `string` for all of them would
      // reject every numeric write.
      // The CTYPE `widget` deliberately tracks `storedType` and NOT the SPA widget. They
      // answer different questions: the SPA widget is how the family captures the value
      // (`lastSaleDate` is picked from a calendar), while this one describes the stored
      // attribute to appcore. Declaring `date` here would change what appcore accepts on
      // the attribute, which would reject the values already stored that are not clean
      // ISO dates — the exact rows the picker is being added to help someone fix.
      ...ASSET_PROPERTY_DETAIL_FIELDS.map(f => ({
        name: f.name,
        type: f.storedType ?? 'string',
        widget: f.storedType === 'number' ? 'number' : 'text',
        required: false,
        label: f.label,
      })),
    ],
    accessPatterns: [{ name: 'by_account', fields: ['accountId'] }],
    presentation: {
      displayField: 'name',
      listColumns: ['name', 'assetClass', 'ticker', 'currency'],
    },
  },
  {
    typeCode: 'valuation',
    name: 'Valuation',
    // steward-app#739 phase 2b — armed. `pillars.ts` `SHARING_PILLARS` is the
    // authority on which pillar owns this type; see "Arming the sharing pillars" above.
    accessControl: { readCapability: 'portfolio:read', writeCapability: STEWARD_WRITE_CAPABILITY },
    fields: [
      { name: 'assetId',     type: 'reference', widget: 'reference', required: true,  label: 'Asset' },
      { name: 'date',        type: 'date',      widget: 'date',      required: true,  label: 'Valuation date' },
      { name: 'marketValue', type: 'number',    widget: 'number',    required: true,  label: 'Market value' },
      { name: 'currency',    type: 'string',    widget: 'text',      required: true,  label: 'Currency (ISO 4217)', validation: { pattern: CURRENCY_CODE_PATTERN } },
      { name: 'source',      type: 'string',    widget: 'text',      required: false, label: 'Data source' },
      { name: 'notes',       type: 'text',      widget: 'textarea',  required: false, label: 'Notes' },
    ],
    accessPatterns: [
      { name: 'by_asset', fields: ['assetId'] },
      { name: 'by_date',     fields: ['date'] },
    ],
    presentation: {
      displayField: 'date',
      listColumns: ['date', 'marketValue', 'currency', 'source'],
      defaultSort: { field: 'date', direction: 'desc' },
    },
  },
  {
    typeCode: 'legal-entity',
    name: 'Entity',
    // steward-app#739 phase 2b — armed. `pillars.ts` `SHARING_PILLARS` is the
    // authority on which pillar owns this type; see "Arming the sharing pillars" above.
    accessControl: { readCapability: 'entities:read', writeCapability: STEWARD_WRITE_CAPABILITY },
    fields: [
      { name: 'name',         type: 'string', widget: 'text',      required: true,  label: 'Entity name' },
      { name: 'entityType',   type: 'string', widget: 'text',      required: true,  label: 'Entity type' },
      { name: 'jurisdiction', type: 'string', widget: 'text',      required: false, label: 'Jurisdiction' },
      // steward-app#424 (decided 2026-09-07): there is NO full `taxId` field, for the same reason
      // `account` has no `accountNumber` (see that CTYPE). The only values ever written were two
      // fake EINs from `scripts/seed-sample-data.ts` in dev, removed with this change. An
      // individual's SSN goes on the person record (#1260), encrypted, never here.
      { name: 'taxIdHint',    type: 'string', widget: 'text',      required: false, label: 'Tax ID last-4 hint' },
      { name: 'status',       type: 'string', widget: 'text',      required: true,  label: 'Status' },
      { name: 'notes',        type: 'text',   widget: 'textarea',  required: false, label: 'Notes' },
    ],
    accessPatterns: [],
    presentation: {
      displayField: 'name',
      listColumns: ['name', 'entityType', 'jurisdiction', 'status'],
    },
  },
  {
    // CT1 — the family's important contacts (doctors, lawyers, CPAs, advisors, …).
    // A plain record type: generic EntityInstance CRUD handles it, deep-linkable like
    // an asset/account, with the same user-owned notes/tags annotation shape. No Plaid
    // sync, so no SyncProvenance fields and no SYNC_OWNED_KEYS entry. Contacts join the
    // document-association LINK graph via the façade (see contactLinks).
    typeCode: 'contact',
    name: 'Contact',
    // steward-app#739 phase 2b — armed. `pillars.ts` `SHARING_PILLARS` is the
    // authority on which pillar owns this type; see "Arming the sharing pillars" above.
    accessControl: { readCapability: 'contacts:read', writeCapability: STEWARD_WRITE_CAPABILITY },
    fields: [
      { name: 'name',         type: 'string', widget: 'text',     required: true,  label: 'Name' },
      { name: 'category',     type: 'enum',   widget: 'select',   required: true,  label: 'Category' },
      { name: 'organization', type: 'string', widget: 'text',     required: false, label: 'Organization / firm' },
      { name: 'phone',        type: 'string', widget: 'text',     required: false, label: 'Phone' },
      { name: 'email',        type: 'string', widget: 'text',     required: false, label: 'Email' },
      { name: 'address',      type: 'text',   widget: 'textarea', required: false, label: 'Address' },
      { name: 'website',      type: 'string', widget: 'text',     required: false, label: 'Website' },
      { name: 'notes',        type: 'text',   widget: 'textarea', required: false, label: 'Notes' },
      { name: 'tags',         type: 'array',  widget: 'tags',     required: false, label: 'Tags' },
    ],
    accessPatterns: [{ name: 'by_category', fields: ['category'] }],
    presentation: {
      displayField: 'name',
      listColumns: ['name', 'category', 'organization', 'phone', 'email'],
      defaultSort: { field: 'name', direction: 'asc' },
    },
  },
  {
    // PR1 — a member of the household. The Family half of the People pillar
    // (steward-app#1260, #1425): who the family is, as records, beside `contact` above,
    // which is who they deal with. The two are deliberately different types — a contact is
    // an address book entry, a person is somebody the estate is FOR — and they sit under
    // one pillar in the SPA as two tabs.
    //
    // ⚠️ **ARMED 2026-09-14 (steward-app#1260 phase 2). It was NOT armed on the release
    // that introduced it, and that gap was deliberate.** The argument is kept in full here
    // because nothing about an arm PR makes it obvious and the NEXT pillar needs it:
    // `auth/permissions.ts` and the pack CTYPE resolver hold independent 60-second
    // in-process caches, so ONE artifact carrying both the `people:read` grant and this
    // declaration lets a warm Lambda serve the stale role (no capability) against the armed
    // type and deny every family member for up to a minute per container, while the seed
    // reports complete success. So the grant shipped first (`pillars.ts`, `packRoles.ts`),
    // `PILLAR_GRANTED_NOT_YET_ARMED` held `people` for the window with `pillars.test.ts`
    // asserting the inverse, and this line landed a release later, after the seeded roles
    // were read back at source. **Do not "simplify" a future pillar by granting and arming
    // in one PR** — the suite is written to red if you try, and that red is the design
    // working, not an obstacle.
    //
    // ⚠️ **Until this line existed, `person` had no read gate AT ALL** — which is why the
    // arm is the prerequisite for #1425's `/people` routes rather than a follow-up to them.
    // appcore's `screen()` (`services/platform-api/src/access.ts`) computes
    // `isReadGated = !!readVerb || !!parent` and returns ALLOW before any capability check
    // when neither is set. `person` has neither, so any `inst:read` holder could read a
    // `person` row — including every connected firm's advisor, regardless of the family's
    // Family switch. The only thing that made that safe was that the façade exposed no
    // route, which `services/steward-app-api/src/__tests__/inFlightPillarSurface.test.ts`
    // held shut. (appcore read at source for this branch's coordinator on 2026-09-14;
    // relayed here rather than claimed as this file author's own scan.)
    //
    // ⚠️ **This gate is a MEMBER-plane control. It says nothing about appcore's SYSTEM
    // plane.** Verified at source in appcore, 2026-09-14: the member-plane handler
    // (`services/platform-api/src/handlers/entities.ts`) imports `../access` and calls
    // `requireEntityAccess`/`filterAccessibleEntities`/`assertEntityAccessible`/
    // `filterAccessibleResolved`; the SIGv4-only system handler
    // (`services/platform-api/src/handlers/system-entities.ts`) has NO import from
    // `../access` and calls none of those helpers — its own docblock (:60-68) says the
    // `/system/*` routes deliberately skip the Tier-3 instance-ACL gates. Per-actor
    // bounding there is the optional `SystemActorScope`
    // (`packages/core/src/auth/system-plane.ts:57-62`), and five actors currently carry
    // none (`system-plane.ts:230-234`): `system:market-price-fetcher`,
    // `system:snapshot-calculator`, `system:workspace-enumerator`,
    // `system:realestate-valuation-refresher`, `system:account-sync`. An unscoped actor
    // may address any `{typeCode}` in any workspace, `person` included — so a future
    // background Lambda reading `person` rows would not be governed by this
    // `readCapability` at all. Not advisor-reachable today (an advisor holds no IAM role),
    // so this bounds the claim above rather than weakening it.
    //
    // `writeCapability` IS declared, and is independent of the above: `steward:write` was
    // granted to ADMIN and MEMBER by #1127 and is already armed on every type in both packs
    // except the document pair and `onboarding-request`, so it locks nobody out who is not already locked out.
    // Omitting it is what would be the anomaly. Stated as the rule rather than as a count
    // because the count moves whenever a type is added: it went from 18 to 19 across BOTH
    // packs when `person` arrived, and the phase-2 arm left it at 19, because a
    // `readCapability` is the other axis (re-counted at this branch's HEAD on 2026-09-14 —
    // 18 in `STEWARD_APP_TYPE_DEFS`, every type but the `document` pair, plus `intake-draft`
    // in the advisor-firm pack). `stewardWriteCapability.test.ts` pins the census over both
    // packs in both directions, which is where the real guarantee lives.
    //
    // ── The tax-identification tier (steward-app#1260, PR 3 of 4) ────────────────
    // The `auditLevel: 'strict'` line below and the seven fields at the end of `fields` are
    // the encrypted tax identification tier. FOUR declarations have to be exactly right,
    // and TypeScript checks none of them: `FoTypeDef` carries an index signature, so
    // `sensitive`, `derived` and `auditLevel` are all just extra keys as far as the
    // compiler is concerned.
    //
    //   1. all six envelope fields `sensitive: true`
    //   2. `taxIdHint` `derived: true`
    //   3. `taxIdHint` NOT `sensitive`
    //   4. `auditLevel: 'strict'`, at TYPE level
    //
    // These are not a convention this repo has to remember. appcore's
    // `assertPersonTaxIdDeclared` (`services/platform-api/src/handlers/people.ts`,
    // crossmarc/appcore#668, merged `e9a946c`) reads all four off the stored CTYPE and
    // refuses `PUT /w/{ws}/people/{entityId}/tax-id` with a 422 `TAX_ID_TYPE_NOT_DECLARED`
    // AFTER the authorization gates and BEFORE `encryptField`, so a declaration that drops
    // a flag stops the feature dead rather than silently un-protecting it. That is the
    // design working; do not route around a 422 by relaxing a flag.
    //
    // ⚠️ MINT-TIME ONLY, which is a real limit and not a turn of phrase: nothing re-checks
    // the declaration once an envelope exists, so a principal holding `type:write` can
    // declare correctly, let an envelope mint, then clear a flag. Only ciphertext escapes
    // (no `kms:Decrypt` outside appcore's reveal Lambda) and that principal can already
    // delete the person, which is why it is not a blocker; the durable fix is a class-wide
    // flag ratchet, tracked as crossmarc/appcore#667. RELAYED from that function's own
    // docblock at `e9a946c`, not re-read in the appcore tree by this branch.
    //
    // What each flag buys on appcore's GENERIC entity plane:
    //   • `sensitive` — (1) stripped from every read, (2) 400 on a client-supplied write,
    //     (3) carried forward when a write omits it. All six or none: a partial set leaves
    //     the omitted fields both leakable and zeroable, which is the `credential` block
    //     below's own warning applied to a smaller set of columns.
    //   • `derived` — rules 2 and 3 of `sensitive` and deliberately NOT rule 1, because a
    //     mask nobody can fetch masks nothing. Rule 3 is what keeps the already-shipped
    //     People edit form from dropping the hint; rule 2 is what stops a write-tier caller
    //     forging `•••• 0000` over a stored number that only a reveal would contradict.
    //     ⚠️ `taxIdHint` must NEVER also be `sensitive`. The pair reads as belt-and-braces
    //     and is the opposite: `sensitive` wins on read, so the mask becomes unfetchable,
    //     the record reads as "nothing on file" over a stored, revealable number, and
    //     appcore's reveal-tier destroy gate — which keys on ENVELOPE presence, unaffected
    //     by any of this — then refuses both the overwrite and the clear to an ordinary
    //     editor. `assertPersonTaxIdDeclared` refuses that combination by name.
    //   • `auditLevel: 'strict'` — appcore's `resolveAuditLevel` defaults to `off`, so
    //     without it a set or a clear is written with NO audit row at all. TYPE level
    //     specifically: resolution is most-strict-wins across tenant/workspace/type, so a
    //     workspace-level setting would satisfy a resolved check while leaving every other
    //     workspace on this type untrailed.
    //
    // ⚠️ **The number never appears as a field of its own name.** It lives in
    // `secretCiphertext`, sealed under appcore's own `personIdentityKey` CMK, and the only
    // thing that decrypts it is `GET /w/{ws}/people/{entityId}/tax-id/reveal`, served by a
    // dedicated Lambda that writes the AUDIT row and awaits it before the decrypt. The key
    // isolation — that Lambda's role holding `kms:Decrypt` on that CMK alone, with `apiFn`
    // holding only `GenerateDataKey` on it — is appcore's to keep and appcore's CDK test to
    // assert; RELAYED here from `handlers/people.ts` at `e9a946c`, not verified from this
    // repo, which can see none of it. The capability that route requires is
    // `people:reveal-tax-id` (`pillars.ts`), a ROUTE requirement that is declared on no
    // type and must not be: arming it as a `readCapability` here would withhold the whole
    // person record from everyone lacking it, a family's own VIEWER included.
    //
    // ⚠️ **This type has NO access parent and must not grow one**, so the vault-shaped
    // reveal tier does not transfer. appcore's `assertSensitiveParentHasCustody`
    // (`custom-type.ts`) returns early for a parentless type, which means config time asks
    // for no custody verb and a `sensitive` field here would LOOK armed while nothing
    // enforced a thing (crossmarc/appcore#658). What arms it instead is the capability on
    // the dedicated route, plus this type's own `readCapability` underneath it:
    // `assertEntityAccessible` runs the read screen on the reveal path, so a reveal needs
    // `people:read` AND `people:reveal-tax-id`.
    //
    // ⚠️ **These are SECURITY claims and NONE of them is held up by this comment.**
    // `encryptionClaims.test.ts` is where they go red — the banned plaintext names stay
    // absent, the envelope is complete with every field flagged, the hint is plaintext and
    // not `sensitive`, and the general two-shape rule over BOTH packs: every type declaring
    // a `sensitive` field declares a disclosure tier, either the VAULT shape
    // (`accessControl.parent` + `custodyVerb`, which is `credential`) or the CAPABILITY
    // shape (`accessControl.readCapability` + an entry in {@link REVEAL_CAPABILITY_BY_TYPE},
    // which is `person`). `auditLevel.test.ts` carries the fourth as a rule rather than a
    // census: a `sensitive` field anywhere in either pack requires `auditLevel: 'strict'`.
    // Mutation-verified on 2026-09-15 (this branch): dropping `sensitive` from one envelope
    // field, dropping `auditLevel`, removing the hint, flagging the hint `sensitive` as
    // well, and removing `person` from `REVEAL_CAPABILITY_BY_TYPE` each red at least one of
    // those cases. A flag removed from this block is a red, not a silent downgrade.
    typeCode: 'person',
    name: 'Person',
    // Declaration 4 of the four listed above. Not decoration and not copied from
    // `credential` out of habit: without it appcore writes NO audit row for a set or a
    // clear of the tax id, and `assertPersonTaxIdDeclared` refuses to mint an envelope.
    auditLevel: 'strict',
    accessControl: { readCapability: 'people:read', writeCapability: STEWARD_WRITE_CAPABILITY },
    fields: [
      // steward-app#1564 (Matt's request, 2026-09-19): first and last name, captured
      // separately, joining the intake's own split (which the prefill used to discard —
      // see `intakePrefill.ts`). First and last ONLY: no middle name, no suffix.
      //
      // Both `required: false`, deliberately, though every NEW write from the SPA form and
      // the prefill sets both. Marking them required would 400 every existing prod row that
      // has only `legalName` the moment it goes through a read-modify-write it never
      // touched the name on (an annotation-only save, for one) — there is no backfill here,
      // by design (see the issue). `legalName` stays the one field the type actually
      // requires, and stays true to its name: the record's LEGAL name, not a display label.
      //
      // ⚠️ **`legalName` is CLIENT-derived here, never server `derived: true`.** Unlike
      // `taxIdHint` below, appcore does not compute this: the SPA's People form and
      // `intakePrefill.ts` compute `` `${firstName} ${lastName}`.trim() `` themselves and
      // send it alongside the split fields on every write that touches identity. That is
      // why `legalName` keeps `required: true` while carrying no `derived` flag — omitting
      // it here would be a normal missing-required-field 400, not the DERIVED_ATTRIBUTES
      // rejection `taxIdHint` gets for a client daring to set it.
      { name: 'firstName',       type: 'string',    widget: 'text',      required: false, label: 'First name' },
      { name: 'lastName',        type: 'string',    widget: 'text',      required: false, label: 'Last name' },
      { name: 'legalName',       type: 'string',    widget: 'text',      required: true,  label: 'Legal name' },
      // The PERSON's preferred name, and deliberately a DIFFERENT field from the preferred
      // name on the signed-in user's own account in Settings (#1260). Two records, one
      // label, never merged: a household names a four-year-old "Tuck" here whether or not
      // he will ever have a login, and an adult who has one may want a different answer in
      // each place. Anything that "unifies" them is deleting one of the two answers.
      { name: 'preferredName',   type: 'string',    widget: 'text',      required: false, label: 'Preferred name' },
      { name: 'dateOfBirth',     type: 'date',      widget: 'date',      required: false, label: 'Date of birth' },
      // ⚠️ FREE TEXT, never an enum — #1260 is explicit, and `contact.category` above is
      // an enum for a reason that does NOT transfer. A fixed list is exactly what forces
      // somebody to misfile a chosen family member: a stepson, a ward, a godmother, the
      // aunt who raised you. The SPA offers suggestions beside the box; suggestions are
      // not the same control as a `select`, and swapping one for the other is a product
      // decision, not a tidy-up.
      { name: 'relationship',    type: 'string',    widget: 'text',      required: false, label: 'Relationship' },
      // Optional contact details (#1957), for a family member the household has only a person row
      // for (today: a spouse or adult child who is the onboarding trusted contact, #1954). Plain
      // strings on `contact.phone` / `contact.email`'s shape, with no format rule: there is no
      // shared validator. Never written for the account holder's own row: the account email is a
      // login identifier and stays off the person (People.tsx).
      //
      // ⚠️ **Do not remove these two fields once rows carry them.** appcore's strict mode 400s an
      // attribute the type does not declare, and `PersonForm` (like every person write) resends
      // every stored key, so after a revert each person row that already holds a phone or an
      // email would fail EVERY save, notes and tags included. Same revert hazard as `taxIdHint`
      // and the encryption envelope below. Retiring them needs a sweep that strips them from
      // stored rows first.
      { name: 'phone',           type: 'string',    widget: 'text',      required: false, label: 'Phone' },
      { name: 'email',           type: 'string',    widget: 'text',      required: false, label: 'Email' },
      { name: 'legalAddress',    type: 'text',      widget: 'textarea',  required: false, label: 'Legal address' },
      // Captured only when it differs from the legal address (#1260). Left empty otherwise
      // rather than mirrored, so "these are the same" stays a fact the record states once.
      { name: 'mailingAddress',  type: 'text',      widget: 'textarea',  required: false, label: 'Mailing address (if different)' },
      // A reference to a `contact` record OR to another `person` row (#1954: a spouse or adult
      // child who is the trusted contact is pointed at, not copied into Contacts as well; the
      // field declares no target type, and PersonDetail resolves Family people first, then
      // contacts). NOT a second free-text address block, so People
      // does not grow its own address book alongside the one the family already keeps
      // (#1260). A plain reference field, on `credential.ownerEntityId`'s pattern — NOT an
      // `accessControl.parent`: an access parent would make the trusted contact gate reads
      // of the person record, so withholding Contacts from a firm would silently withhold
      // half the roster, and deleting a contact would orphan a person.
      { name: 'trustedContactId', type: 'reference', widget: 'reference', required: false, label: 'Trusted contact' },
      // ── The self link (steward-app#1851) ──
      //
      // The user id of the login this household record IS, when someone has said so. Written
      // only by appcore's three self-link routes (`POST`/`DELETE /w/{ws}/people/{id}/link-self`,
      // `POST /w/{ws}/people/link-self/from-onboarding`, crossmarc/appcore#790, docs/06 §26),
      // and the value they write is always the caller's own user id.
      //
      // ⚠️ **A LABEL. Nothing may authorize on it.** It names a record; access comes only from a
      // membership or an advisor connection (docs/04-navigation-ia.md §3.3).
      //
      // ⚠️ **Both flags are required, and both are only as good as the appcore that reads them.**
      // appcore's routes answer 422 `SELF_LINK_NOT_DECLARED` unless the field is a string with
      // BOTH. `routeOnly`: the generic member plane refuses the key on presence and carries it
      // forward on omission. `unique`: one row per value per workspace. An appcore that predates
      // #790 enforces neither, and this field would then be an ordinary string any editor could
      // aim at any user id. `deploy/lib/appcore-feature-requirements.ts` covers one path to that:
      // in the deploy workflow's seed job, the pairing step refuses to seed an environment
      // whose appcore `/version` commit is not at or past #790. It does NOT cover a seed run
      // by hand (the seed script does not call it), nor an appcore rolled back after the
      // seed (the check reads `/version` once). `scripts/lib/packFieldFlags.ts` reads the
      // seeded pack row back in the same job; its header says what that does not prove.
      //
      // A plain id rather than a `reference`, because a user is an appcore row and not an
      // instance of any pack type. `person` declares no `ttl` field, which appcore requires of a
      // type with a `unique` field (pinned in `selfLinkDeclaration.test.ts`).
      //
      // It replaces `memberId` (declared with the type in PR #1439; no code in this repo or appcore wrote it,
      // by grep of both on 2026-10-01). A row that somehow still stores `memberId` would fail
      // strict validation on a generic write that resends it, so the SPA
      // (`SERVER_OWNED_PERSON_KEYS`) and the façade (`router.ts` `dropInputAttributes`) both
      // drop that key from every person write.
      { name: 'linkedUserId',    type: 'string',    widget: 'text',      required: false, routeOnly: true, unique: true, label: 'Linked login' },
      { name: 'notes',           type: 'text',      widget: 'textarea',  required: false, label: 'Notes' },
      { name: 'tags',            type: 'array',     widget: 'tags',      required: false, label: 'Tags' },
      // ── Tax identification (steward-app#1260) — read the header block above first ──
      //
      // The masked hint: the last four digits, DERIVED server-side by
      // `PUT /w/{ws}/people/{entityId}/tax-id` from the normalized plaintext it just
      // sealed, so `•••• 6789` is the last four of what is actually stored. Plaintext and
      // deliberately NOT `sensitive`; `derived: true` is the whole protection, and the
      // header says what each half of it buys.
      //
      // Named on the house `<thing>Hint` pattern, beside `account.lastFourHint` and
      // `legal-entity.taxIdHint`. That the other copy holds an EIN and this one an SSN or
      // an ITIN is what the LABEL is for; one vocabulary across the pack is worth more than
      // a bespoke name here. It is also why `encryptionClaims.test.ts`'s banned-name list
      // does not trip on it: that list is exact-match on `taxId`, not a prefix match.
      { name: 'taxIdHint',       type: 'string',    widget: 'text',      required: false, derived: true, label: 'Social security number last 4' },
      // The encryption envelope — written server-side by appcore's dedicated tax-id route,
      // never client-set, and stripped from every generic read.
      //
      // Exactly appcore's `ENCRYPTED_FIELD_KEYS`, unprefixed and in that order, because
      // `asEncryptedField` narrows an attribute bag by those fixed literals and
      // `encryptField`/`decryptField` take and return that shape. The same six names
      // `credential` uses below; a prefixed set would need a mapping layer at every call
      // site for no gain.
      //
      // ⚠️ ALL SIX must carry `sensitive: true` — a partial set leaves the omitted fields
      // both leakable and zeroable. All six are `required: false` and `person` is not
      // `versioned`, so an omitting write would destroy the envelope with no snapshot to
      // recover from and the audit delta scrubs it; rule 3 (carry forward) is the only
      // thing standing between the already-shipped People edit form and exactly that.
      // Clearing a number therefore needs its own route (`DELETE .../tax-id`), because no
      // `PUT` can express it.
      { name: 'secretCiphertext', type: 'string', widget: 'text', required: false, sensitive: true, label: 'Secret ciphertext' },
      { name: 'encryptedDataKey', type: 'string', widget: 'text', required: false, sensitive: true, label: 'Wrapped data key' },
      { name: 'iv',               type: 'string', widget: 'text', required: false, sensitive: true, label: 'IV' },
      { name: 'authTag',          type: 'string', widget: 'text', required: false, sensitive: true, label: 'GCM auth tag' },
      { name: 'keyId',            type: 'string', widget: 'text', required: false, sensitive: true, label: 'KMS key id' },
      { name: 'scheme',           type: 'string', widget: 'text', required: false, sensitive: true, label: 'Envelope scheme' },
      // ── Onboarding stamps (crossmarc/appcore#739) ──
      //
      // Which onboarding answer this row stands for, so the client's own sign-in can copy the
      // tax id they typed into a firm's onboarding form onto THIS row
      // (`POST /w/{ws}/people/{id}/tax-id/copy-from-onboarding`). Written by the client
      // prefill (`system:client-prefill`) at CREATE and never again:
      //   • `onboardingSubject` — `client` or `spouse`, the onboarding identity subject;
      //   • `onboardingSource` — `<firmWorkspaceId>#<clientId>`, the firm whose form it is and
      //     that firm's `advisor-client` id for the client (`intakePrefill.ts`).
      // appcore's copy route requires BOTH, plus `_cb === 'system:client-prefill'`, and 422s
      // `ONBOARDING_SUBJECT_NOT_DECLARED` unless both are declared `createOnly` here.
      //
      // What `createOnly` buys (appcore `FieldDefinition.createOnly`): the member plane refuses
      // the key on create and update alike (400 `CREATE_ONLY_ATTRIBUTES`) and carries it
      // forward when a write omits it; on `/system/*` only a CREATE by `system:client-prefill`
      // may set it, and appcore checks the source's firm segment against the workspace's live
      // sponsored setup there (`ONBOARDING_SOURCE_MISMATCH`). The SPA strips both from every
      // person write (`src/lib/personRecord.ts`) and the façade refuses both on its person
      // create and update (`router.ts`), so no member can SET or change them even on an appcore
      // that predates the flag. That older appcore does not carry them forward either, so there
      // an edit of the row (which omits them) drops them: the row is then no copy target, which
      // is the safe direction. Plaintext and readable: the SPA finds the copy target by them.
      { name: 'onboardingSubject', type: 'string', widget: 'text', required: false, createOnly: true, label: 'Onboarding subject' },
      { name: 'onboardingSource',  type: 'string', widget: 'text', required: false, createOnly: true, label: 'Onboarding source' },
    ],
    // `by_linked_user` answers "which person record is this login?" (#1851). `by_relationship`
    // is free text, so it groups the roster rather than enumerating it.
    accessPatterns: [
      { name: 'by_linked_user', fields: ['linkedUserId'] },
      { name: 'by_relationship', fields: ['relationship'] },
    ],
    presentation: {
      // `legalName` and not `preferredName`: the display field must always be present, and
      // only the legal name is `required: true`. The roster renders the preferred name
      // where it has one; that is a rendering choice the SPA makes, not a storage default.
      displayField: 'legalName',
      listColumns: ['legalName', 'preferredName', 'relationship', 'dateOfBirth'],
      defaultSort: { field: 'legalName', direction: 'asc' },
    },
  },
  {
    // CV1 — family credential vault. Secrets are envelope-encrypted server-side by the
    // dedicated /credentials handler (KMS AES-256-GCM); only the ciphertext envelope
    // fields below are stored. There is intentionally NO `secret` field: in strict mode
    // a stray plaintext `secret` attribute is REJECTED with a 400 by validateAttributes
    // (appcore decisions log §51), so the vault cannot be bypassed into storing
    // cleartext. It was silently *stripped* before that; the control held either way,
    // because the secret never reached the row, but rejecting also tells a caller that
    // thought it was storing a secret that it was not. `auditLevel: strict` → every
    // reveal/mutation is atomically audited. See appcore CV1–CV3 + docs/07-credential-vault.md.
    typeCode: 'credential',
    name: 'Credential',
    auditLevel: 'strict',
    // #290 — a credential inherits its visibility from its vault. Without this the
    // vault ACL gated `reveal` and nothing else, so a member excluded from a vault
    // could still list and read its credentials (incl. cleartext `username`/`notes`),
    // edit them, overwrite the secret, and delete them — unrecoverably, since
    // `credential` is not `versioned`, delete is hard, and AUDIT redacts the envelope.
    // Gating keys off THIS config, never off the `access-controlled` behavior flag.
    // #295 — `custodyVerb` puts the two operations that take an envelope out of reach
    // on the reveal tier: DESTROYING a credential, and CHANGING its vault in either
    // direction (including detaching it to no vault at all). Metadata edit stays on
    // the read tier, per #290's ratified split.
    //
    // Without this the GENERIC `/entities/credential/...` plane reached the same rows
    // with only the read tier: a grantee trusted merely to know a credential exists
    // could hard-delete it, or move it into a vault they CAN open and reveal it there
    // — the encryption context binds `{workspaceId, entityId}` with no vault
    // component, so the unwrap survives the move and only refusing the move stops it.
    //
    // `reveal` and not a new verb like `write`: matching is per-verb against a
    // per-target opt-in, so a NEW verb silently denies every existing grantee whose
    // ACL entry predates it. Every `['read','reveal']` entry already satisfies this.
    //
    // steward-app#643 — `readCapability: 'credential:read'` arms appcore#411's
    // whole-type gate: a caller must hold `credential:read` (Tier-1) in ADDITION to
    // `inst:read` and Tier-3/vault ACL to reach a `credential` instance at all. This
    // is what lets `steward-advisor` read every other portal type while credentials
    // stay invisible to it. Declared on BOTH `credential` and `credential-vault`
    // (below) — declaring it only here would still let an advisor list vault names,
    // and the vault is the thing whose existence names the institution.
    accessControl: {
      parent: { field: 'vaultId', typeCode: 'credential-vault' },
      custodyVerb: 'reveal',
      readCapability: 'credential:read',
      writeCapability: STEWARD_WRITE_CAPABILITY,
    },
    fields: [
      // Plaintext metadata — safe to list/search; never the secret itself.
      { name: 'label',         type: 'string',    widget: 'text',     required: true,  label: 'Label' },
      { name: 'category',      type: 'enum',      widget: 'select',   required: true,  label: 'Category' },
      { name: 'username',      type: 'string',    widget: 'text',     required: false, label: 'Username / hint' },
      { name: 'url',           type: 'string',    widget: 'text',     required: false, label: 'Website' },
      { name: 'notes',         type: 'text',      widget: 'textarea', required: false, label: 'Notes (non-secret)' },
      { name: 'tags',          type: 'array',     widget: 'tags',     required: false, label: 'Tags' },
      { name: 'ownerEntityId', type: 'reference', widget: 'reference', required: false, label: 'Owner entity' },
      { name: 'vaultId',       type: 'reference', widget: 'reference', required: false, label: 'Vault / folder' },
      { name: 'folderId',      type: 'reference', widget: 'reference', required: false, label: 'Folder' },
      // Encrypted envelope — written server-side, never client-set.
      //
      // #295 — `sensitive: true` makes that a PLATFORM rule instead of a convention
      // the dedicated façade happened to follow. On the generic entity plane appcore
      // now (1) strips these from every response, (2) rejects them with a 400 if a
      // client sends one, and (3) CARRIES THEM FORWARD when a write omits them.
      //
      // Rule 3 is the one that matters. `attributes` is a wholesale replace, and all
      // six are `required: false` — so `PUT {label, category}` used to validate
      // cleanly and silently ZERO the envelope. The credential still looked healthy
      // until someone hit `/reveal` and got `409 NO_SECRET`, with no way back:
      // `credential` is not `versioned`, delete is hard, and AUDIT redacts the
      // envelope. (`document` escaped the identical bug only because its `s3Key` is
      // `required: true`, so an omitting write 400s.)
      //
      // ⚠️ ALL SIX must carry the flag — they are exactly appcore's
      // `ENCRYPTED_FIELD_KEYS`. A partial set leaves the omitted fields both leakable
      // and zeroable, which is the whole bug for a smaller set of columns.
      { name: 'secretCiphertext', type: 'string', widget: 'text', required: false, sensitive: true, label: 'Secret ciphertext' },
      { name: 'encryptedDataKey', type: 'string', widget: 'text', required: false, sensitive: true, label: 'Wrapped data key' },
      { name: 'iv',               type: 'string', widget: 'text', required: false, sensitive: true, label: 'IV' },
      { name: 'authTag',          type: 'string', widget: 'text', required: false, sensitive: true, label: 'GCM auth tag' },
      { name: 'keyId',            type: 'string', widget: 'text', required: false, sensitive: true, label: 'KMS key id' },
      { name: 'scheme',           type: 'string', widget: 'text', required: false, sensitive: true, label: 'Envelope scheme' },
    ],
    accessPatterns: [{ name: 'by_category', fields: ['category'] }],
    presentation: {
      displayField: 'label',
      // List columns are plaintext-only; envelope fields must never be projected.
      listColumns: ['label', 'category', 'username', 'url', 'updatedAt'],
      defaultSort: { field: 'label', direction: 'asc' },
    },
  },
  {
    // CV4 — credential vault: an access-controlled collection that groups credentials
    // (a credential references one via `vaultId`). `access-controlled` activates the
    // platform Tier-3 ACL: `credential:reveal` alone no longer reveals a credential in
    // a restricted vault — the caller must also be on the vault's ACL (a vault with no
    // ACL entries stays open). The parenthesis used to end "workspace admins with
    // `mem:manage` break-glass"; post-appcore#603/#605 that is the wrong tier's name for
    // a rule that did survive (steward-app#1254). `mem:manage` is
    // `ACL_ADMINISTRATOR_CAPABILITY`: it exempts its holder from Tier-3 EXCLUSION, so a
    // restricted vault is not hidden from an ACL administrator — and nothing more. The
    // `readCapability` below is a separate tier it cannot satisfy, which is fine here
    // because appcore's `workspace-admin` grants `credential:read` and
    // `credential:reveal` as literals (appcore made that grant load-bearing for exactly
    // this reason; read in the crossmarc/appcore checkout at `0d18c78` on 2026-09-09).
    // Vault ACLs are managed via /w/{ws}/acl/credential-vault/{vaultId}.
    typeCode: 'credential-vault',
    name: 'Credential Vault',
    behaviors: ['access-controlled'],
    // #290 — `readVerb` makes the vault's own ACL gate its VISIBILITY, not just the
    // bespoke `reveal` check. Verbs name disclosure TIERS, not operations:
    //   `read`   — trusted to know the credential exists: list, get, metadata edit
    //   `reveal` — trusted with envelope custody: reveal, secret overwrite, delete,
    //              re-home in either direction
    // ⚠️ Matching is per-VERB while the opt-in fallback is per-TARGET: a vault with
    // ANY entries is restricted, so an entry granting only `reveal` denies `read` to
    // its own grantee. Grant `['read','reveal']`, never `['reveal']` alone. A verb no
    // gate checks (e.g. `delete`) is INERT, not protective — `AclEntry.verbs` has no
    // enum. New verbs require a new disclosure tier, not a new operation.
    // #295 — deleting a VAULT is a custody act for the same reason deleting a
    // credential is: it puts every envelope inside it out of reach. Orphaned
    // credentials keep a `vaultId` pointing at nothing, and a dangling access parent
    // fails CLOSED, so they become invisible and unrevealable to everyone but a `*:*`
    // holder.
    //
    // ⚠️ That last clause read "to everyone below break-glass" until steward-app#1254,
    // and here the CONCLUSION moved rather than just the label — the same correction
    // `folderVisibility.test.ts` records for a `parentFolderId` cycle, and this is the
    // copy of it that governs CREDENTIALS. Pre-appcore#603 a `mem:manage` holder
    // short-circuited `screen()` ahead of the access graph and did reach an orphan; #603
    // left only `*:*` doing that (coordinator's reading of appcore's deployed commit
    // `0d18c788` on 2026-09-09, live in dev, stage and prod), and the Tier-3
    // ACL-administrator exemption that survived explicitly does NOT weaken a structural
    // fail-closed rule — the third of the three limits appcore states on
    // `ACL_ADMINISTRATOR_CAPABILITY`. So an admin no longer reveals an orphaned
    // credential either, and the recovery path is a re-home by a `*:*` holder rather
    // than anything a family admin can do for themselves.
    //
    // ⚠️ This does NOT protect an UNRESTRICTED vault. The opt-in fallback is per
    // TARGET, so a vault with no ACL entries passes at every verb — including this
    // one. Deleting an empty-ACL container out from under its children is a separate,
    // verb-independent problem tracked in crossmarc/appcore#311.
    // steward-app#643 — `readCapability` mirrors the `credential` CTYPE above: the
    // vault must be gated the same way its contents are, or an advisor without
    // `credential:read` could still list vault names/descriptions (an institution
    // named by its vault is not "non-credential" data).
    accessControl: {
      readVerb: 'read', custodyVerb: 'reveal', readCapability: 'credential:read',
      writeCapability: STEWARD_WRITE_CAPABILITY,
    },
    auditLevel: 'strict',
    fields: [
      { name: 'name',        type: 'string', widget: 'text',     required: true,  label: 'Vault name' },
      { name: 'description', type: 'text',   widget: 'textarea', required: false, label: 'Description' },
    ],
    accessPatterns: [],
    presentation: {
      displayField: 'name',
      listColumns: ['name', 'updatedAt'],
      defaultSort: { field: 'name', direction: 'asc' },
    },
  },
  {
    typeCode: 'asset-lot',
    name: 'Asset Lot',
    // steward-app#739 phase 2b — armed. `pillars.ts` `SHARING_PILLARS` is the
    // authority on which pillar owns this type; see "Arming the sharing pillars" above.
    accessControl: { readCapability: 'portfolio:read', writeCapability: STEWARD_WRITE_CAPABILITY },
    fields: [
      { name: 'assetId',       type: 'reference', widget: 'reference', required: true,  label: 'Asset' },
      { name: 'quantity',      type: 'number',    widget: 'number',    required: true,  label: 'Quantity (signed; negative for sale)' },
      { name: 'effectiveDate', type: 'date',      widget: 'date',      required: true,  label: 'Effective date' },
      { name: 'costBasis',     type: 'number',    widget: 'number',    required: false, label: 'Cost basis per unit' },
      { name: 'lotType',       type: 'enum',      widget: 'select',    required: true,  label: 'Lot type' },
      { name: 'source',        type: 'string',    widget: 'text',      required: false, label: 'Source (manual, broker-import, etc.)' },
      { name: 'externalId',    type: 'string',    widget: 'text',      required: false, label: 'External lot ID (broker idempotency)' },
      { name: 'notes',         type: 'text',      widget: 'textarea',  required: false, label: 'Notes' },
    ],
    accessPatterns: [
      { name: 'by_asset', fields: ['assetId'] },
      { name: 'by_date',     fields: ['effectiveDate'] },
    ],
    presentation: {
      displayField: 'effectiveDate',
      listColumns: ['effectiveDate', 'quantity', 'costBasis', 'lotType'],
      defaultSort: { field: 'effectiveDate', direction: 'desc' },
    },
  },
  {
    // One dated thing that happened to one asset (steward-app#2568, docs/40): a roof replaced,
    // an air conditioner serviced, a coat cleaned. A child row of `asset`, like `valuation` and
    // `asset-lot`, rather than an array on the asset, so it never contends for the asset row's
    // one `version` with the other editors on the asset page (docs/40 §Why a child row).
    //
    // Hand-entered only: no `source`, `externalId` or `connectionId`, and account-sync neither
    // reads nor writes it. It is not a value and feeds no snapshot. `cost` is in the PARENT
    // asset's currency, so there is no `currency` field. `assetEvent.ts` holds the limits and
    // the date rule the façade enforces; the `validation` entries below repeat them so appcore
    // refuses the same values from any other writer.
    typeCode: 'asset-event',
    name: 'Asset Event',
    // Portfolio pillar, like its parent. `pillars.ts` `SHARING_PILLARS` is the authority.
    accessControl: { readCapability: 'portfolio:read', writeCapability: STEWARD_WRITE_CAPABILITY },
    fields: [
      { name: 'assetId',     type: 'reference', widget: 'reference', required: true,  label: 'Asset' },
      // `YYYY-MM` or `YYYY-MM-DD` (`ASSET_EVENT_DATE_RE`); appcore's `date` accepts both.
      { name: 'date',        type: 'date',      widget: 'date',      required: true,  label: 'Date' },
      { name: 'kind',        type: 'enum',      widget: 'select',    required: true,  label: 'What happened',
        validation: { values: [...ASSET_EVENT_KINDS] } },
      { name: 'part',        type: 'string',    widget: 'text',      required: false, label: 'Part',
        validation: { maxLength: ASSET_EVENT_PART_MAX } },
      { name: 'title',       type: 'string',    widget: 'text',      required: true,  label: 'Title',
        validation: { minLength: 1, maxLength: ASSET_EVENT_TITLE_MAX } },
      { name: 'notes',       type: 'text',      widget: 'textarea',  required: false, label: 'Notes',
        validation: { maxLength: ASSET_EVENT_NOTES_MAX } },
      { name: 'cost',        type: 'number',    widget: 'number',    required: false, label: 'Cost',
        validation: { min: 0 } },
      // Real estate only (the façade refuses it on any other class). Repair or improvement.
      { name: 'improvement', type: 'boolean',   widget: 'checkbox',  required: false, label: 'Improvement' },
    ],
    accessPatterns: [
      { name: 'by_asset', fields: ['assetId'] },
    ],
    presentation: {
      displayField: 'title',
      listColumns: ['date', 'kind', 'part', 'title'],
      defaultSort: { field: 'date', direction: 'desc' },
    },
  },
  {
    typeCode: 'market-price',
    name: 'Market Price',
    // steward-app#739 phase 2b — armed. `pillars.ts` `SHARING_PILLARS` is the
    // authority on which pillar owns this type; see "Arming the sharing pillars" above.
    accessControl: { readCapability: 'portfolio:read', writeCapability: STEWARD_WRITE_CAPABILITY },
    fields: [
      { name: 'ticker',       type: 'string', widget: 'text',   required: true,  label: 'Ticker symbol' },
      { name: 'date',         type: 'date',   widget: 'date',   required: true,  label: 'Price date (YYYY-MM-DD)' },
      { name: 'pricePerUnit', type: 'number', widget: 'number', required: true,  label: 'Price per unit' },
      { name: 'currency',     type: 'string', widget: 'text',   required: true,  label: 'Currency (ISO 4217)', validation: { pattern: CURRENCY_CODE_PATTERN } },
      { name: 'source',       type: 'string', widget: 'text',   required: true,  label: 'Data source (finnhub, etc.)' },
    ],
    accessPatterns: [
      { name: 'by_ticker', fields: ['ticker'] },
      { name: 'by_date',   fields: ['date'] },
    ],
    presentation: {
      displayField: 'ticker',
      listColumns: ['ticker', 'date', 'pricePerUnit', 'currency', 'source'],
      defaultSort: { field: 'date', direction: 'desc' },
    },
  },
  {
    typeCode: 'liability',
    name: 'Liability',
    // steward-app#739 phase 2b — armed. `pillars.ts` `SHARING_PILLARS` is the
    // authority on which pillar owns this type; see "Arming the sharing pillars" above.
    accessControl: { readCapability: 'portfolio:read', writeCapability: STEWARD_WRITE_CAPABILITY },
    fields: [
      { name: 'name',              type: 'string',    widget: 'text',      required: true,  label: 'Liability name' },
      { name: 'liabilityType',     type: 'enum',      widget: 'select',    required: true,  label: 'Liability type' },
      { name: 'currentBalance',    type: 'number',    widget: 'number',    required: true,  label: 'Outstanding balance' },
      { name: 'currency',          type: 'string',    widget: 'text',      required: true,  label: 'Currency (ISO 4217)', validation: { pattern: CURRENCY_CODE_PATTERN } },
      { name: 'currentBalanceAsOf', type: 'date',     widget: 'date',      required: true,  label: 'Balance as of date' },
      { name: 'notes',             type: 'text',      widget: 'textarea',  required: false, label: 'Notes' },
      { name: 'tags',              type: 'array',     widget: 'tags',      required: false, label: 'Tags' },
      { name: 'accountId',         type: 'reference', widget: 'reference', required: false, label: 'Account' },
      { name: 'linkedAssetId',     type: 'reference', widget: 'reference', required: false, label: 'Linked asset (encumbered)' },
      { name: 'obligorEntityId',   type: 'reference', widget: 'reference', required: false, label: 'Obligor entity' },
      // steward-app#1936: see `account.ownerPersonId`.
      { name: 'obligorPersonId',   type: 'reference', widget: 'reference', required: false, label: 'Obligor (family member)' },
      { name: 'lender',            type: 'string',    widget: 'text',      required: false, label: 'Lender' },
      { name: 'interestRate',      type: 'number',    widget: 'number',    required: false, label: 'Interest rate (annual %)' },
      { name: 'originalPrincipal', type: 'number',    widget: 'number',    required: false, label: 'Original principal' },
      { name: 'maturityDate',      type: 'date',      widget: 'date',      required: false, label: 'Maturity date' },
      // When the debt was taken out (steward-app#254). With `originalPrincipal` it is a real
      // data point in the balance history: on this day the balance WAS the original principal.
      // Without it a recompute before the first recorded balance leaves the debt out. See
      // `liabilityBalance.ts` for the whole rule.
      { name: 'originationDate',   type: 'date',      widget: 'date',      required: false, label: 'Taken out on' },
      { name: 'source',            type: 'string',    widget: 'text',      required: false, label: 'Source (manual, plaid, …)' },
      { name: 'externalId',        type: 'string',    widget: 'text',      required: false, label: 'Provider external id (aggregation key)' },
      { name: 'connectionId',      type: 'reference', widget: 'reference', required: false, label: 'Owning account-connection' },
    ],
    accessPatterns: [
      { name: 'by_type', fields: ['liabilityType'] },
    ],
    presentation: {
      displayField: 'name',
      listColumns: ['name', 'liabilityType', 'currentBalance', 'currency', 'currentBalanceAsOf'],
      defaultSort: { field: 'currentBalanceAsOf', direction: 'desc' },
    },
  },
  {
    // A liability's balance on one date (steward-app#254). The dated child of `liability`,
    // as `valuation` is of `asset`: the parent's `currentBalance`/`currentBalanceAsOf` is the
    // denormalized cache of the latest row, and the snapshot calculator reads the rows so a
    // recompute of a past day finds what was owed THEN. `liabilityBalance.ts` states the rule.
    //
    // Unlike `valuation`, this declares `externalId`/`connectionId`: account-sync writes them
    // on the rows it owns, which is what lets the connection cascade and the synced-row guard
    // recognise a provider row without trusting free text.
    typeCode: 'liability-balance',
    name: 'Liability Balance',
    // Portfolio pillar, like its parent. `pillars.ts` `SHARING_PILLARS` is the authority.
    accessControl: { readCapability: 'portfolio:read', writeCapability: STEWARD_WRITE_CAPABILITY },
    fields: [
      { name: 'liabilityId',  type: 'reference', widget: 'reference', required: true,  label: 'Liability' },
      { name: 'date',         type: 'date',      widget: 'date',      required: true,  label: 'Balance date' },
      { name: 'balance',      type: 'number',    widget: 'number',    required: true,  label: 'Balance owed' },
      { name: 'currency',     type: 'string',    widget: 'text',      required: true,  label: 'Currency (ISO 4217)', validation: { pattern: CURRENCY_CODE_PATTERN } },
      { name: 'source',       type: 'string',    widget: 'text',      required: false, label: 'Source (plaid, …; absent when entered by hand)' },
      { name: 'notes',        type: 'text',      widget: 'textarea',  required: false, label: 'Notes' },
      { name: 'externalId',   type: 'string',    widget: 'text',      required: false, label: 'Provider external id' },
      { name: 'connectionId', type: 'reference', widget: 'reference', required: false, label: 'Owning account-connection' },
    ],
    accessPatterns: [
      { name: 'by_liability', fields: ['liabilityId'] },
      { name: 'by_date',      fields: ['date'] },
    ],
    presentation: {
      displayField: 'date',
      listColumns: ['date', 'balance', 'currency', 'source'],
      defaultSort: { field: 'date', direction: 'desc' },
    },
  },
  {
    typeCode: 'net-worth-snapshot',
    name: 'Net Worth Snapshot',
    // steward-app#739 phase 2b — armed. `pillars.ts` `SHARING_PILLARS` is the
    // authority on which pillar owns this type; see "Arming the sharing pillars" above.
    accessControl: { readCapability: 'portfolio:read', writeCapability: STEWARD_WRITE_CAPABILITY },
    schemaMode: 'schemaless',
    fields: [
      { name: 'date',             type: 'date',   widget: 'date',   required: true,  label: 'Snapshot date (YYYY-MM-DD)' },
      { name: 'netWorth',         type: 'number', widget: 'number', required: true,  label: 'Net worth' },
      { name: 'totalAssets',      type: 'number', widget: 'number', required: true,  label: 'Total assets' },
      { name: 'totalLiabilities', type: 'number', widget: 'number', required: true,  label: 'Total liabilities' },
      { name: 'equitiesValue',    type: 'number', widget: 'number', required: false, label: 'Equities value' },
      { name: 'realEstateValue',  type: 'number', widget: 'number', required: false, label: 'Real estate value' },
      { name: 'cashValue',        type: 'number', widget: 'number', required: false, label: 'Cash value' },
      { name: 'otherAssetsValue', type: 'number', widget: 'number', required: false, label: 'Other assets value' },
      // Not required, matching the writer: `snapshot-calculator` derives this from the
      // holdings it summed and OMITS it when nothing resolvable voted, rather than naming
      // a currency the household does not hold (steward-app#484). Inert today — this type
      // is schemaless, so appcore returns from `validateAttributes` before `required` is
      // ever read — but `schemaMode` is immutable after creation, so a future re-seed as
      // strict would 400 the nightly write for exactly the workspaces that already have no
      // honest label. Left disagreeing with the writer, it is a trap with a fuse.
      { name: 'currency',         type: 'string', widget: 'text',   required: false, label: 'Currency (ISO 4217)' },
      { name: 'computedAt',       type: 'string', widget: 'text',   required: true,  label: 'ISO timestamp when computed' },
      { name: 'lastTradingDate',  type: 'date',   widget: 'date',   required: true,  label: 'Last trading date used for prices' },
    ],
    accessPatterns: [
      { name: 'by_date', fields: ['date'] },
    ],
    presentation: {
      displayField: 'date',
      listColumns: ['date', 'netWorth', 'totalAssets', 'totalLiabilities', 'currency'],
      defaultSort: { field: 'date', direction: 'desc' },
    },
  },
  {
    // The household's net-worth STARTING POINT (steward-app#397). A workspace-level
    // singleton: at most one row, holding the date before which net-worth history is
    // hidden from every read path.
    //
    // It exists because the daily snapshot series records DATA-ENTRY ORDER during
    // onboarding, not market movement — each night's snapshot totals whatever had been
    // connected or typed in by then, so a family sees a staircase from ~0 up to their
    // real net worth, and the change chips read in the thousands of percent.
    //
    // Why a separate row and not a flag on the snapshot itself: `net-worth-snapshot` is
    // schemaless, so an `isBaseline` boolean would need no schema change at all — and
    // would be silently erased. `SystemDb.update` is a whole-row PUT and
    // `snapshot-calculator`'s force path writes only freshly computed attributes, so a
    // recompute drops any field it doesn't know about. That recompute is not rare: it
    // fires automatically from `triggerSnapshotBackfill` on a dated valuation, lot, or
    // liability create, update or delete; on an asset create or delete that touches a dated
    // row or a synced quantity (steward-app#470); and on the two cascading deletes — an
    // account cascade and a connection disconnect — each once from the earliest date it
    // destroys (steward-app#482). Not on every write that moves a snapshot: `services/
    // netWorthBaseline.ts` names the remaining update-side gaps. The flag would survive every
    // night and die the first time someone touched a historical value.
    //
    // The baseline HIDES history, it never deletes it. Pre-baseline snapshots stay in
    // the table, so setting one is reversible and can be re-run. Deleting them instead
    // would not even be stable: the same auto-fired backfill force-writes every date
    // from `fromDate`, so deleted rows would silently reappear.
    typeCode: 'net-worth-baseline',
    name: 'Net Worth Baseline',
    // steward-app#739 phase 2b — armed. `pillars.ts` `SHARING_PILLARS` is the
    // authority on which pillar owns this type; see "Arming the sharing pillars" above.
    accessControl: { readCapability: 'portfolio:read', writeCapability: STEWARD_WRITE_CAPABILITY },
    schemaMode: 'schemaless',
    // Explicit, because appcore's `resolveAuditLevel` defaults to `off` and this is the
    // one steward write whose effect is workspace-WIDE and destructive-looking: setting a
    // starting point changes what every member sees, and clearing it deletes the row that
    // recorded who set it. Without an AUDIT trail, "why did our history change?" would
    // have no answer at all once the row is gone.
    auditLevel: 'standard',
    fields: [
      { name: 'baselineDate', type: 'date',   widget: 'date',     required: true,  label: 'History starts on (YYYY-MM-DD)' },
      { name: 'setAt',        type: 'string', widget: 'text',     required: true,  label: 'ISO timestamp when set' },
      { name: 'setBy',        type: 'string', widget: 'text',     required: false, label: 'User id who set it' },
      { name: 'reason',       type: 'text',   widget: 'textarea', required: false, label: 'Why the starting point was set' },
    ],
    presentation: {
      displayField: 'baselineDate',
      listColumns: ['baselineDate', 'setAt', 'setBy'],
      defaultSort: { field: 'setAt', direction: 'desc' },
    },
  },
  {
    // AI assistant conversation thread (docs/13-ai-assistant.md). One item holds the
    // whole transcript in `messages` (schemaless — an array of {role, text, ts}). Chats
    // are retained on a rolling window: the `ttl` config below maps `expiresAt` onto the
    // table TTL (`_ttl`), so DynamoDB self-deletes an idle thread; the façade slides
    // `expiresAt` forward on every new message so active chats persist and only abandoned
    // ones age out. Users can also delete a thread outright.
    // See services/steward-app-api/src/handlers/assistantThreads.ts.
    //
    // The `ttl` config is REQUIRED for that to happen (appcore decisions log §55). Row
    // expiry used to be implicit on any `expiresAt`, which is why `document` — where
    // `expiresAt` means a passport's expiry date, not a retention policy — was quietly
    // deleting people's files (#322). This type is the one that genuinely wants it.
    typeCode: 'chat-session',
    name: 'Chat Session',
    // steward-app#932 phase 2 — ARMED. `assistant:read` (the literal of
    // `ASSISTANT_READ_CAPABILITY`, `assistantAccess.ts`) is granted to `steward-admin`,
    // `steward-member` and `steward-viewer` and withheld from `steward-advisor`, so an
    // advisor's workspace-wide `inst:read` no longer reaches a family's transcripts
    // through appcore's generic entities route. This is the `credential:read` shape
    // (#639/#643), NOT a fifth sharing pillar: `pillars.ts` is the authority on why, and
    // `PILLAR_UNGATED_TYPE_CODES` keeps naming this type for that reason.
    //
    // Written as a LITERAL rather than the exported constant, matching `credential:read`
    // and the four pillar strings above, so that `assistantAccess.test.ts` comparing this
    // against `ASSISTANT_READ_CAPABILITY` is a real cross-check of two independently
    // written strings instead of a tautology. Point 4 of "Arming the sharing pillars"
    // above applies verbatim: a seed run with this line deleted really does disarm the
    // type, and that test is the only thing in the way.
    //
    // The grant shipped first (#933, 2026-08-27) and was verified at source in all three
    // environments before this line existed — the cache-ordering hazard `pillars.ts`
    // documents at length is why those are two releases and not one.
    //
    // CREATOR OWNERSHIP (steward-app#590, crossmarc/appcore#248, appcore decisions log §101).
    // `ownership: 'creator'` makes appcore enforce, on the member plane and its relay, that
    // every read and write of an instance comes from its `_cb`, and lets a create through on
    // `inst:read` + `assistant:read` alone, so `steward-viewer` (which holds no `inst:write`)
    // can save its own conversations. Armed on top of appcore rel-2026.10.26.
    //
    // `writeCapability` is GONE on purpose, not by tidying: appcore refuses it alongside
    // ownership (`assertOwnershipShape`, 422 `OWNERSHIP_WITH_WRITE_CAPABILITY` on its own
    // seed path, a 500 at request time on this repo's raw-Put pack row), because ownership
    // IS this type's write rule.
    //
    // What its removal DOES widen, stated exactly. Who may create a conversation becomes
    // every principal holding `inst:read` (or `inst:write`) AND `assistant:read`: in this
    // repo's packs that is `steward-admin`, `steward-member` and `steward-viewer`, in any
    // workspace those roles are held in (family or firm), plus anything holding `*:*`. The
    // new member of that set is `steward-viewer`, and only for its own rows, which is the
    // point of #590. The set never includes `steward-advisor`, the contributor, the pillar
    // roles, the shared-folder role, appcore's built-in roles or the advisor-firm pack's
    // roles, none of which hold `assistant:read`. The pack roles are pinned by
    // `chatSessionOwnership.test.ts`; appcore's built-ins are code constants at appcore and
    // are not pinned here (`assistantAccess.ts` and `AssistantUnavailable.tsx` say why they
    // lack it).
    // Premise, not pinned by any test: no advisor membership ALSO carries a family role.
    // The product never mints one (`ADVISOR_SCOPE_ROLE_IDS` is disjoint from the
    // `assistant:read` holders, which IS pinned), but `FAMILY_ROLE_IDS` in `roles.ts`
    // records that such a membership is possible; a person holding one could create
    // conversations of their own, and still read nobody else's. Update and delete need the
    // caller to be the row's creator.
    //
    // ⚠️ Do NOT drop `ownership`. appcore's drop-ratchet does not cover pack rows (§101), so
    // one seed with the key deleted silently hands every member's transcripts to every other
    // holder of `assistant:read` in the workspace. `CREATOR_OWNED_TYPE_CODES`
    // (`assistantAccess.ts`) names this type, the seed refuses to write the pack without it
    // (`assertCreatorOwnershipDeclared`, `scripts/lib/packSeed.ts`), and
    // `chatSessionOwnership.test.ts` pins this exact object in both directions.
    accessControl: { readCapability: 'assistant:read', ownership: 'creator' },
    schemaMode: 'schemaless',
    fields: [
      { name: 'title',         type: 'string', widget: 'text',     required: true,  label: 'Title' },
      { name: 'messages',      type: 'array',  widget: 'textarea', required: true,  label: 'Messages ({role, text, ts})' },
      { name: 'lastMessageAt', type: 'string', widget: 'text',     required: true,  label: 'Last message at (ISO timestamp)' },
      { name: 'expiresAt',     type: 'date',   widget: 'date',     required: false, label: 'Expires at (rolling TTL)' },
    ],
    ttl: { attribute: 'expiresAt' },
    accessPatterns: [],
    presentation: {
      displayField: 'title',
      listColumns: ['title', 'lastMessageAt'],
      defaultSort: { field: 'lastMessageAt', direction: 'desc' },
    },
  },
  {
    // ⚠️ VESTIGIAL. Product feedback moved to appcore's own `/w/{ws}/feedback` capability
    // (appcore docs/23-feedback-capability.md): member reads are author-scoped upstream by
    // `_cb` and operator triage lives in crossmarc-ops' `/admin/feedback`. This façade's
    // `handlers/feedback.ts` is a thin passthrough that never touches `/entities/feedback`,
    // so nothing writes this type any more. The definition stays because rows written by
    // the PRE-migration handler still exist — and that handler had no author scoping, so
    // those legacy rows remain readable through the generic plane by anything holding
    // `inst:read`. Do not read this comment as saying the type is inert.
    //
    // (It used to say "a future admin-only triage UI reads every item via GET /feedback",
    // describing a design that shipped differently. steward-app#932's rationale for
    // leaving `feedback` outside the assistant-transcript rule originally leaned on that
    // stale sentence; corrected in its code review.)
    typeCode: 'feedback',
    name: 'Feedback',
    // steward-app#742 step 3 — no `readCapability` (feedback is deliberately readable by
    // anyone who can read the workspace at all; it carries no family financial data), but
    // the write axis is armed like every other non-document type.
    //
    // ⚠️ **Be honest about what this reaches, because an earlier draft of this comment was
    // not** (found in this PR's security review). It said this string was "the ONLY thing
    // that will keep a future contributor role from writing it". It is not: real feedback
    // does not go through appcore's generic entity plane at all. The façade's
    // `POST|GET|PUT /w/{ws}/feedback` routes reach appcore's DEDICATED
    // `handlers/feedback.ts`, which imports nothing from `access.ts`, runs no capability
    // check of any kind ("author-scoping IS the gate", enforced by `FeedbackRepository` on
    // `_cb`), and writes `_et: 'FEEDBACK'` rows rather than entity instances.
    //
    // So this declaration closes exactly one surface: `PUT|DELETE /w/{ws}/entities/feedback/
    // {id}` on appcore's publicly addressable generic plane, which nothing in this app uses
    // and which was open to any `inst:write` holder. That is worth closing and is why the
    // uniform rule has no exception here — but a contributor role will still be able to
    // submit and edit its own feedback, and if that is not wanted it has to be closed in
    // appcore's feedback handler, not here.
    accessControl: { writeCapability: STEWARD_WRITE_CAPABILITY },
    fields: [
      { name: 'message',     type: 'text',   widget: 'textarea', required: true,  label: 'Feedback' },
      { name: 'category',    type: 'string', widget: 'text',     required: false, label: 'Category (idea | question | issue | compliment | other)' },
      { name: 'source',      type: 'string', widget: 'text',     required: false, label: 'Source (assistant | feedback-page | …)' },
      { name: 'context',     type: 'string', widget: 'text',     required: false, label: 'Context (page or thread it came from)' },
      { name: 'status',      type: 'string', widget: 'text',     required: false, label: 'Triage status (new | reviewed | archived)' },
      { name: 'submittedAt', type: 'string', widget: 'text',     required: true,  label: 'Submitted at (ISO timestamp)' },
    ],
    accessPatterns: [
      { name: 'by_status', fields: ['status'] },
    ],
    presentation: {
      displayField: 'submittedAt',
      listColumns: ['submittedAt', 'category', 'source', 'status'],
      defaultSort: { field: 'submittedAt', direction: 'desc' },
    },
  },
  {
    // A linked aggregation provider connection (e.g. one Plaid Item). Holds NO balances.
    // The Plaid access token is envelope-encrypted (KMS AES-256-GCM) and stored FLAT on
    // this entity in the six `*Ciphertext`/`*DataKey`/… fields below — mirroring the
    // credential vault — so there is no per-connection Secrets Manager secret. See
    // docs/04-plaid-integration-plan.md and packages/secret-crypto.
    typeCode: 'account-connection',
    name: 'Account Connection',
    // steward-app#739 phase 2b — armed. `pillars.ts` `SHARING_PILLARS` is the
    // authority on which pillar owns this type; see "Arming the sharing pillars" above.
    accessControl: { readCapability: 'portfolio:read', writeCapability: STEWARD_WRITE_CAPABILITY },
    // billable-connection (appcore PSA1): opts this type into appcore's
    // connection-usage lookup partition. On every create/delete, appcore's generic
    // EntityInstanceRepository atomically maintains a `CONN#<tenantId>` existence
    // pointer (+ a `PLAT#CONNTENANTS` tenant registry row) inside the same
    // TransactWriteItems as the entity write, so the nightly Stripe connection-overage
    // reconcile can Query "a tenant's connections" instead of Scanning the whole table.
    // WITHOUT this behavior the reconcile reads an empty partition and under-bills, so
    // this declaration is what actually activates the feature end to end. See appcore
    // docs/08-write-inventory.md op 45.
    behaviors: ['billable-connection'],
    fields: [
      { name: 'provider',             type: 'string', widget: 'text', required: true,  label: 'Provider (plaid, …)' },
      { name: 'providerItemId',       type: 'string', widget: 'text', required: true,  label: 'Provider item id' },
      { name: 'institutionName',      type: 'string', widget: 'text', required: true,  label: 'Institution name' },
      { name: 'status',               type: 'enum',   widget: 'select', required: true, label: 'Status' },
      { name: 'lastSyncedAt',         type: 'string', widget: 'text', required: false, label: 'Last successful sync (ISO timestamp)' },
      // Set when a sync is DISPATCHED, cleared by whichever terminal write ends it
      // (success or error). Present ⇒ a sync was started and has not reported back, which
      // is what lets the connections list say "Syncing…" as a recorded fact rather than
      // inferring it from an absent `lastSyncedAt` (#357).
      //
      // Deliberately NOT a `status` value. `status` is load-bearing elsewhere: the billing
      // path counts `status === 'active'` connections (`activeConnectionCount`, CB6), the
      // dashboard attention list keys off `error`/`disconnected`, and account-sync skips
      // `disconnected`. A transient `syncing` status would silently drop every mid-sync
      // connection out of the billable count.
      { name: 'syncStartedAt',        type: 'string', widget: 'text', required: false, label: 'Sync in progress since (ISO timestamp)' },
      // LEGACY since steward-app#2062: account-sync no longer writes it, and the façade strips
      // it from what it returns to the browser (`toConnectionResource`). Stored text goes away
      // only when one of three writes lands: account-sync's next error write, its next success
      // write, or the façade's relink (`connections.ts`). The marker-clearing write for a
      // `disconnected` row keeps whatever is there, and the façade's `writeSyncMarker` rewrites
      // a row with every stored attribute copied across, so it keeps the text too. Text can
      // therefore stay on such a row indefinitely. ⚠️ **Do not remove this field
      // while any row may still hold it.** Strict mode 400s an undeclared attribute and the
      // façade's relink resends every stored key, so a row that kept raw text would fail that
      // write. Retiring it needs a sweep that strips it from stored rows first.
      { name: 'lastError',            type: 'text',   widget: 'textarea', required: false, label: 'Last sync error (legacy)' },
      // Why the connection is in `error`, as a short code: `reconnect_required`, `shared_login`,
      // `provider_unavailable` or `sync_failed` (`CONNECTION_ERROR_CODES`, connectionError.ts in
      // this package; account-sync classifies, the SPA owns the sentence for each). A plain string rather than an enum so a value a later
      // account-sync adds cannot 400 a write on a pack that has not been re-seeded yet. Same
      // revert hazard as `lastError` once rows carry it: do not remove it without a sweep.
      { name: 'lastErrorCode',        type: 'string', widget: 'text', required: false, label: 'Last sync error code' },
      // When the family's permission for this connection ends at the bank, as an ISO timestamp
      // (steward-app#2265). Written by account-sync from the provider on each sync whose read of
      // it succeeds, and absent when the provider reports no end date or it was never read. It is the BANK's term on its own permission, not
      // a retention period of ours, and it is advisory: nothing gates on it. Same revert hazard
      // as `lastErrorCode` once rows carry it: do not remove it without a sweep.
      { name: 'consentExpiresAt',     type: 'string', widget: 'text', required: false, label: 'Bank permission ends (ISO timestamp)' },
      // Access-token envelope (written server-side, stripped by the façade, never client-set).
      { name: 'secretCiphertext',     type: 'string', widget: 'text', required: false, label: 'Access-token ciphertext' },
      { name: 'encryptedDataKey',     type: 'string', widget: 'text', required: false, label: 'Wrapped data key' },
      { name: 'iv',                   type: 'string', widget: 'text', required: false, label: 'IV' },
      { name: 'authTag',              type: 'string', widget: 'text', required: false, label: 'GCM auth tag' },
      { name: 'keyId',                type: 'string', widget: 'text', required: false, label: 'KMS key id' },
      { name: 'scheme',               type: 'string', widget: 'text', required: false, label: 'Envelope scheme' },
    ],
    accessPatterns: [
      { name: 'by_provider', fields: ['provider'] },
      // Resolve a provider webhook's item_id → connection without a scan (BG1/D2a).
      // Additive, single-field ⇒ appcore materializes an ENTITY_LOOKUP for it.
      { name: 'by_provider_item', fields: ['providerItemId'] },
    ],
    presentation: {
      displayField: 'institutionName',
      listColumns: ['institutionName', 'provider', 'status', 'lastSyncedAt'],
      defaultSort: { field: 'lastSyncedAt', direction: 'desc' },
    },
  },
  {
    // Advisor-firm channel (docs/25 §3) — one public "Financial Snapshot" submission,
    // stored in the FIRM's workspace. Written ONLY by the intake-writer background
    // Lambda over `/system/*` (the public façade route validates + enqueues, never
    // writes); read via read-only resource routes gated by ordinary firm-workspace
    // membership. No write routes are mounted on the façade for this type.
    //
    // Schemaless, deliberately: the payload is the nested 11-section object that the
    // shared zod `IntakeSubmissionSchema` (intakeSchema.ts) validates at the façade
    // BEFORE enqueue — appcore's flat strict-mode field validation cannot express a
    // nested shape, and re-declaring a lossy flat projection here would just invite
    // drift. The zod schema is the validation authority for this type; the field list
    // below exists for the flat display columns and the list view.
    typeCode: 'advisor-intake',
    name: 'Client Intake',
    // steward-app#742 step 3 — armed on the SAME uniform rule as every other non-document
    // type. The intake WRITER is untouched: `/system/*` runs no gate from `access.ts`
    // (appcore `handlers/system-entities.ts` imports nothing from it; `SYSTEM_CAPABILITIES`
    // is the fixed exact-match set `{inst:read, inst:write}`, `auth/system-plane.ts`).
    //
    // ⚠️ **This type DOES have a live user-plane write route, and an earlier draft of this
    // comment said it had none** (found in this PR's security review). `PUT /intakes/{id}/
    // status` (`router.ts`) reaches `advisor.setIntakeStatus`, which calls
    // `appcore.updateEntity` with the CALLER'S bearer token — the user plane, through
    // `screen(ctx, metas, typeCode, 'write')`. So this declaration is a live gate on a real
    // route, not only on appcore's publicly addressable generic plane.
    //
    // ⚠️ **The route keeps working for at most ONE reason now, where this comment used to
    // give two.** The second was that firm staff hold `STEWARD_ROLE_IDS.ADMIN`, whose
    // literal `mem:manage` cleared `screen()` before any capability check; appcore#603
    // removed `mem:manage` from `UNCONDITIONAL_CAPABILITIES` (deployed by #605; read at
    // source in the crossmarc/appcore checkout at `0d18c78` on 2026-09-08) and that reason
    // is gone. What is left is the grant itself: `steward-admin` and `steward-member` both
    // hold `steward:write` outright (`packRoles.ts`, read here — this half IS checked, by
    // `stewardWriteCapability.test.ts`).
    //
    // ⚠️ **That is a conditional, and this comment previously stated it as a conclusion.**
    // "The route is unaffected" holds only for a caller whose firm-workspace membership
    // actually carries one of those two roles, and WHICH roles firm staff hold is
    // steward-app#1245's open question rather than a settled one. Nothing here establishes
    // the antecedent: no membership census was taken for this note, and no test in this
    // repo pins it either — `advisor.test.ts`' `setIntakeStatus` cases drive a mocked
    // appcore and never evaluate a real role set against this declaration. So read this as
    // "the route survives #603 IF firm staff hold `steward-admin`/`steward-member`", with
    // the `if` live. The coordinator is filing a separate issue for the antecedent. Do not
    // restore a "two independent reasons" reading, and do not promote the survivor to an
    // unconditional one either.
    //
    // Reads are unaffected — `writeCapability` is added only under the `'write'` intent
    // (appcore `access.ts` `requiredCapabilities`).
    accessControl: { writeCapability: STEWARD_WRITE_CAPABILITY },
    // steward-app#1080 — explicit, because appcore's `resolveAuditLevel` defaults to
    // `off` and every input it resolves from (this CTYPE, the RIPL tenant head row,
    // the firm workspace head row) was verified in prod to set nothing, so the
    // resolved level for this type was `off`. That matters because the intake
    // retention sweeper (#1033, report-only by default) is a straight `delete` of a
    // family's financial disclosure once flipped to destructive mode — with no AUDIT
    // row, "who deleted this, and under what policy" would be unanswerable and
    // unreconstructable, since the deleted item is gone rather than updated. `strict`,
    // not `standard`, because `standard` is `void emitAuditStandard(...)` — fire-and-
    // forget, so a failed write is silently swallowed — while `strict` puts the AUDIT
    // row inside the same transactWrite as the delete, where it cannot be lost. See
    // the `credential` (:519) and `credential-vault` (:632) types above for the same
    // choice made the same way.
    auditLevel: 'strict',
    schemaMode: 'schemaless',
    fields: [
      { name: 'firmId',      type: 'string', widget: 'text',     required: true, label: 'Firm' },
      { name: 'clientName',  type: 'string', widget: 'text',     required: true, label: 'Client name' },
      { name: 'clientEmail', type: 'string', widget: 'text',     required: true, label: 'Client email' },
      { name: 'submittedAt', type: 'string', widget: 'text',     required: true, label: 'Submitted at (ISO timestamp)' },
      // The full validated IntakeSubmission object (every section). Schemaless mode
      // means this declaration is presentational; the stored value is a nested object.
      { name: 'intake',      type: 'array',  widget: 'textarea', required: true, label: 'Intake sections' },
    ],
    accessPatterns: [],
    presentation: {
      displayField: 'clientName',
      listColumns: ['clientName', 'clientEmail', 'submittedAt'],
      defaultSort: { field: 'submittedAt', direction: 'desc' },
    },
  },
  {
    // Advisor-firm channel — sponsored client setup WITHOUT a submitted intake
    // (crossmarc/steward-app#1543 part (b)). See `sponsoredInvitation.ts` for the full
    // design: the proof model (accept-from-the-typed-inbox, appcore's own founding-invite
    // mechanism, no appcore change needed), the two-phase write (this row's id becomes
    // appcore's `sourceRef` BEFORE the workspace exists, so `state` starts absent and is
    // set only once appcore confirms), and why an absent `state` must be treated as
    // non-live everywhere a duplicate or roster check reads this type.
    //
    // Armed on the SAME uniform rule as `advisor-intake` and `advisor-connection`
    // (steward-app#742 step 3): every non-document type in this pack declares
    // `accessControl.writeCapability`, held by `steward-admin` and `steward-member`
    // (`packRoles.ts`).
    typeCode: 'sponsored-invitation',
    name: 'Sponsored Invitation',
    accessControl: { writeCapability: STEWARD_WRITE_CAPABILITY },
    // `strict`, matching `advisor-intake` and for the same reason: this row is the only
    // record of a firm inviting a named family by email, and a retention/cleanup sweep
    // that deleted it with no AUDIT row would leave "who invited this address, and when"
    // unreconstructable.
    auditLevel: 'strict',
    fields: [
      { name: 'firmId',       type: 'string', widget: 'text',   required: true,  label: 'Firm' },
      { name: 'email',        type: 'string', widget: 'text',   required: true,  label: 'Invited email' },
      { name: 'familyName',   type: 'string', widget: 'text',   required: true,  label: 'Family name (as the advisor typed it)' },
      { name: 'createdAt',    type: 'string', widget: 'text',   required: true,  label: 'Created at (ISO timestamp)' },
      { name: 'createdBy',    type: 'string', widget: 'text',   required: true,  label: 'Created by (user id)' },
      // Absent until appcore confirms the workspace — see the type-level comment above.
      { name: 'workspaceId',  type: 'string', widget: 'text',   required: false, label: 'Client workspace id' },
      { name: 'tenantId',     type: 'string', widget: 'text',   required: false, label: 'Firm tenant id' },
      { name: 'linkedAt',     type: 'string', widget: 'text',   required: false, label: 'Linked at (ISO timestamp)' },
      { name: 'linkedBy',     type: 'string', widget: 'text',   required: false, label: 'Linked by (user id)' },
      { name: 'state',        type: 'enum',   widget: 'select', required: false, label: 'State (invited | active | cancelled | ended)' },
      { name: 'lastInvitedAt', type: 'string', widget: 'text',  required: false, label: 'Last invited at (ISO timestamp)' },
      { name: 'cancelledAt',  type: 'string', widget: 'text',   required: false, label: 'Cancelled at (ISO timestamp)' },
      { name: 'cancelledBy',  type: 'string', widget: 'text',   required: false, label: 'Cancelled by (user id)' },
      { name: 'endedAt',      type: 'string', widget: 'text',   required: false, label: 'Ended at (ISO timestamp)' },
      { name: 'endedBy',      type: 'string', widget: 'text',   required: false, label: 'Ended by (user id)' },
    ],
    accessPatterns: [],
    presentation: {
      displayField: 'email',
      listColumns: ['familyName', 'email', 'state', 'createdAt'],
      defaultSort: { field: 'createdAt', direction: 'desc' },
    },
  },
  {
    // Advisor-firm channel — an advisor types a prospective client's email once, and the
    // intake form never asks for it again (crossmarc/steward-app#1555). See
    // `intakeInvitation.ts` for the full design: the two-phase write (mirrors
    // `sponsored-invitation` above), the `linkGeneration` field that makes a resend or
    // cancel actually stop an already-mailed link from saving or submitting, and why
    // there is no `opened`/`submitted` state.
    //
    // Modelled on `sponsored-invitation` immediately above (not identical — 10 fields here
    // against that type's 15; this one carries `draftId`/`linkGeneration`, that one carries
    // `familyName`/`workspaceId`/`tenantId`/`linkedAt`/`endedAt`), and it lives in the SAME
    // pack for the SAME reason: `createIntakeInvitation` / `resendIntakeInvitation` /
    // `cancelIntakeInvitation` all write this row via the façade under the FIRM caller's
    // own bearer token (`services/steward-app-api/src/handlers/intakeInvitation.ts`), the
    // same user-plane write `sponsored-invitation` makes.
    //
    // `intake-draft` (below, in `STEWARD_ADVISOR_FIRM_TYPE_DEFS`) is NOT the model to copy
    // here, and its own comment there says why it sits where it does: it is written only
    // over `/system/*`, by background actors appcore bounds with `requiredPack:
    // 'steward-advisor-firm'` (appcore `packages/core/src/auth/system-plane.ts`'s
    // `INTAKE_WRITER_SCOPE` et al.) — a bound that would be vacuous in a pack every family
    // workspace mounts, which is the actual reason that type is NOT in `steward-app`. This
    // type's create/resend/cancel path is an authenticated advisor write on the USER
    // plane, which that system-plane bound does not govern at all, so the reasoning that
    // keeps `intake-draft` out of this pack has nothing to say about this type.
    //
    // The one real cost of putting it here anyway, named rather than missed: this type is
    // now mounted into every family workspace in the fleet, same as `advisor-intake`,
    // `sponsored-invitation` and `advisor-connection` already are, for the identical
    // reason. Harmless on inspection: a family admin could create a row of this type in
    // their OWN workspace holding `steward:write`, but the only thing that ever MAILS an
    // invitation is the façade route, which requires `steward-firm-staff` and resolves the
    // target firm server-side, and the intake-writer can only enter a workspace that
    // mounts `steward-advisor-firm` in the first place.
    //
    // Armed on the same steward-app#742 step 3 rule the comment above states:
    // `steward-admin` and `steward-member` are what carry `STEWARD_WRITE_CAPABILITY`; no
    // role in the `steward-advisor-firm` pack does (`packRoles.ts` / `roles.ts`'s
    // `NO_WRITE_ROLE_IDS`). The one role this excludes that otherwise holds `inst:write` is
    // `steward-advisor-contributor` (`packRoles.ts`, "THE CONTRIBUTOR" block) — deliberately,
    // per that role's own docblock, not a side effect of this declaration. A firm caller who
    // clears `mayEmailIntakeLink`'s gate (`steward-firm-staff`) still writes this row under
    // whatever base Admin/Member role their own membership independently carries — a firm
    // workspace mounts BOTH `steward-app` and `steward-advisor-firm`
    // (appcore `seed-scripts/seed-advisor-firm-lib.ts`, `packages/core/src/entities/pack.ts`'s
    // `PACK_ADMIN_ROLE_IDS` comment), and appcore resolves a caller's capabilities as the flat
    // union of every role on their membership, over every pack the workspace mounts, never
    // scoped to the pack that declares the type being written
    // (`packages/core/src/auth/permissions.ts`'s `resolveRolePermissions` / `loadRole`, read
    // at source in the appcore checkout, 2026-09-19). The same union is what already lets
    // `sponsored-invitation` work: no `steward-advisor-firm` role carries `steward:write`
    // either, and `steward-firm-provision` cannot be granted through the façade's member
    // routes (`assertFirmProvisionHasBaseRole`, `services/steward-app-api/src/handlers/
    // members.ts:24-30`, called from `add`/`update` at :85/:104) without also granting
    // `steward-admin` or `steward-member` — that guard binds only those two routes, not
    // appcore's own admin plane or `seed-advisor-firm`, which write memberships directly.
    // `steward-firm-staff` (this type's gate) carries no such guarantee on any plane, which
    // is tracked separately (crossmarc/steward-app#1588) and is not this declaration's to
    // close.
    //
    // The background intake-writer also reads this row (steward-app#1555 slice 3, GET
    // only, over `/system/*`) — see the type's own file for that half. Declaring the type
    // here is what makes the façade's user-plane writes above possible; it says nothing
    // about the writer's separate, already-shipped IAM grant.
    typeCode: INTAKE_INVITATION_TYPE_CODE,
    name: 'Intake Invitation',
    accessControl: { writeCapability: STEWARD_WRITE_CAPABILITY },
    // `strict`, matching `sponsored-invitation` and `advisor-intake`: a resend or cancel
    // MUTATES this row (`linkGeneration`), and while there is no delete path today
    // (retention is an open question per the type's own docblock), the audit trail across
    // those updates is exactly what an AUDIT-level write is for.
    auditLevel: 'strict',
    fields: [
      { name: 'firmId',         type: 'string', widget: 'text',   required: true,  label: 'Firm' },
      { name: 'email',          type: 'string', widget: 'text',   required: true,  label: 'Invited email (as the advisor typed it)' },
      // Not written since 2026-10-07 (a draft now names its invitation, not the reverse). Still
      // declared because this type is `strict` and a row stored before then carries it.
      { name: 'draftId',        type: 'string', widget: 'text',   required: false, label: 'Draft id (ULID, no longer written)' },
      { name: 'linkGeneration', type: 'number', widget: 'number', required: true,  label: 'Link generation' },
      { name: 'createdAt',      type: 'string', widget: 'text',   required: true,  label: 'Created at (ISO timestamp)' },
      { name: 'createdBy',      type: 'string', widget: 'text',   required: true,  label: 'Created by (user id)' },
      { name: 'state',          type: 'enum',   widget: 'select', required: false, label: 'State (invited | cancelled)' },
      { name: 'lastInvitedAt',  type: 'string', widget: 'text',   required: false, label: 'Last invited at (ISO timestamp)' },
      // 🚨 Declared because this type sets no `schemaMode` and so is appcore's default
      // `strict` (appcore `packages/core/src/db/pack-repository.ts`, `t.schemaMode ??
      // 'strict'`), which REFUSES an undeclared attribute with a 400. Unlike `intake-draft`
      // (explicitly `schemaless`), a façade write carrying this field cannot land until the
      // pack seed has this line. `.github/workflows/deploy.yml` orders that correctly: the
      // `api` CDK job declares `needs: [..., seed-pack, ...]`, so the seed runs before the
      // Lambda that starts sending it. Read as a DISPLAY figure only — see the field's own
      // doc in `intakeInvitation.ts` for why it is not an authorization input.
      { name: 'linkTtlSeconds', type: 'number', widget: 'number', required: false, label: 'Mailed link lifetime (seconds)' },
      // Declared for the same strict-mode reason as `linkTtlSeconds` just above, and ordered
      // by the same `seed-pack` → `api` dependency (crossmarc/steward-app#1753 follow-up). See
      // the field's own doc in `intakeInvitation.ts`.
      { name: 'reusedIntakeId', type: 'string', widget: 'text',   required: false, label: 'Reused submission (advisor-intake id)' },
      { name: 'cancelledAt',    type: 'string', widget: 'text',   required: false, label: 'Cancelled at (ISO timestamp)' },
      { name: 'cancelledBy',    type: 'string', widget: 'text',   required: false, label: 'Cancelled by (user id)' },
    ],
    accessPatterns: [],
    presentation: {
      displayField: 'email',
      listColumns: ['email', 'state', 'createdAt'],
      defaultSort: { field: 'createdAt', direction: 'desc' },
    },
  },
  {
    // Advisor-firm channel — a firm hides one of its own unclaimed prospect drafts from
    // its Prospects list (crossmarc/steward-app#1570). See `draftDismissal.ts` for the
    // full design: why this is a NEW row rather than a field on `intake-draft` (that type
    // is written only by the intake-writer/intake-reader background Lambdas over the
    // IAM `/system/*` plane, per this repo's appcore-docs/26-workstream-D invariant, and
    // widening either role's scope to accept a firm-initiated write was rejected on
    // #1570), and why it is one row PER DISMISSAL rather than one row per draft
    // (`createEntity` mints its own id; the read side matches by the `draftId`
    // attribute, the same "attribute filter over a listed collection" shape
    // `draftProspects()` already uses for `intake-draft`'s own `firmId`).
    //
    // Modelled on `intake-invitation` and `sponsored-invitation` immediately above:
    // written only by the façade, under the firm caller's own bearer token, into the
    // firm's own workspace — never a background role, and appcore is never touched
    // directly. Armed on the same steward-app#742 step 3 rule those two types state:
    // `steward-admin` and `steward-member` carry `STEWARD_WRITE_CAPABILITY`, and every
    // firm workspace's real staff already hold one of those two roles, so no new pack
    // arming and no IAM change is needed for this write.
    //
    // No `auditLevel` declaration: this type has no `sensitive` field (`auditLevel.test.ts`'s
    // #1260 rule only requires `strict` where one exists), and a dismissal carries no
    // disclosure to protect — only which draft a firm chose to stop seeing.
    typeCode: DRAFT_DISMISSAL_TYPE_CODE,
    name: 'Draft Dismissal',
    accessControl: { writeCapability: STEWARD_WRITE_CAPABILITY },
    fields: [
      { name: 'firmId',      type: 'string', widget: 'text', required: true, label: 'Firm' },
      { name: 'draftId',     type: 'string', widget: 'text', required: true, label: 'Draft id (ULID)' },
      { name: 'dismissedAt', type: 'string', widget: 'text', required: true, label: 'Dismissed at (ISO timestamp)' },
      { name: 'dismissedBy', type: 'string', widget: 'text', required: true, label: 'Dismissed by (user id)' },
    ],
    accessPatterns: [],
    presentation: {
      displayField: 'draftId',
      listColumns: ['draftId', 'dismissedAt'],
      defaultSort: { field: 'dismissedAt', direction: 'desc' },
    },
  },
  {
    // Advisor-firm channel (docs/25 §1) — the record that a firm is connected to
    // THIS (client) workspace. A LABEL, never an auth artifact: the advisor's actual
    // access is their viewer-role membership, created via the standard invite path
    // with roleIds pinned server-side, and appcore's membership check is the
    // boundary. Disconnect matches advisor members server-side by email against
    // `advisorEmails` unioned with the firm registry, revokes pending invites, and flips
    // `status` to revoked.
    //
    // ⚠️ **"never caller-supplied" used to be the end of that sentence and it was a
    // half-truth** (steward-app#766). The FAÇADE never takes an address off a request —
    // that part is true and stays true. The STORED attribute is a different question: this
    // type declares no `accessControl` (see the ⚠️ on `sharedFolderId` below), so appcore
    // gates its writes on `inst:write`, which the steward MEMBER role holds. A member could
    // therefore append any workspace address to `advisorEmails` and have the next admin's
    // Disconnect evict that person under `mem:manage` they do not themselves hold.
    // steward-app#766 closed it in the façade by SPLITTING the target set by source: an
    // address that comes from the environment's firm registry is acted on as before, while
    // one reached only through this stored attribute must also belong to a holder of
    // `STEWARD_ROLE_IDS.ADVISOR`. **That control lives in `handlers/advisor.ts`, not here**,
    // exactly like #765's. Do not restore the old reassurance, and do not add another field
    // on this type that the façade turns into a target set without reading that split first.
    typeCode: 'advisor-connection',
    name: 'Advisor Connection',
    // steward-app#742 step 3 — **the single most important line of that step.** Everything
    // above in this type's comments turns on the sentence "this type declares no
    // `accessControl`, so appcore gates its writes on `inst:write`". It now declares one.
    // The read axis is deliberately still open (no `readCapability`): a connected advisor
    // must be able to see the connection that names their own firm. The write axis is not:
    // `sharedFolderId` is an ACCESS-AIMING value — the façade's reconcile writes ACL
    // entries onto whatever folder this id names — so an `inst:write` holder without
    // `steward:write` must not be able to move it. #765's provenance gate remains the
    // control that bounds WHICH ACLs the façade will touch; this narrows WHO may aim it,
    // and neither replaces the other.
    //
    // ⚠️ **"Bounds who may aim it" is still an overclaim, but a smaller one than when this
    // comment was written.** It used to say that `screen()` called `hasBreakGlass` before
    // `missingChainCapability`, so every `workspace-admin` and every `steward-admin` passed
    // this declaration without holding the string. appcore#603 narrowed the short-circuiting
    // set to `['*:*']` (deployed by #605; read at source in the crossmarc/appcore checkout
    // at `0d18c78` on 2026-09-08, for steward-app#1254), so the exception is now a `*:*`
    // holder. On that same reading `platform-admin` is the only role in appcore's
    // `PLATFORM_ROLES` carrying `*:*`, and no steward pack role carries it — but that is a
    // ROLE CENSUS with a date on it, not a structural property: appcore#605's own F-5
    // records that `PackRepository.upsertRole` validates a pack role's `permissions`
    // against nothing. An ordinary workspace admin needs
    // `steward:write` spelled out; `steward-admin` grants it, `workspace-admin` does not.
    // So this declaration removes MORE than it used to: the `inst:write`-only principal it
    // was written for, and now the platform-admin-shaped membership as well. Correcting a
    // caveat upward like this is the one direction that is safe to get wrong, but do not
    // read it as "bounds who may aim it" either — `*:*` still passes.
    accessControl: { writeCapability: STEWARD_WRITE_CAPABILITY },
    fields: [
      { name: 'firmId',        type: 'string', widget: 'text',   required: true,  label: 'Firm' },
      { name: 'firmName',      type: 'string', widget: 'text',   required: true,  label: 'Firm name' },
      { name: 'status',        type: 'enum',   widget: 'select', required: true,  label: 'Status (active | revoked)' },
      { name: 'advisorEmails', type: 'array',  widget: 'tags',   required: true,  label: 'Advisor emails invited' },
      { name: 'connectedAt',   type: 'string', widget: 'text',   required: true,  label: 'Connected at (ISO timestamp)' },
      { name: 'connectedBy',   type: 'string', widget: 'text',   required: false, label: 'Connected by (user id)' },
      { name: 'revokedAt',     type: 'string', widget: 'text',   required: false, label: 'Revoked at (ISO timestamp)' },
      // steward-app#731 (docs/26 Phase 4a). The `document-folder` holding this
      // connection's "Shared with <firm>" folder, inside the CLIENT's own Documents
      // pillar — not a second store and not a second place to go.
      //
      // OPTIONAL and additive: rows written before this field existed simply lack it and
      // the next connect backfills them. It has to be DECLARED even so, because this type
      // is `schemaMode: 'strict'` — an undeclared attribute is a 400 from appcore, not a
      // silent strip.
      //
      // ⚠️ **THIS IS AN ACCESS-AIMING VALUE. It used to say the opposite** ("NOT an
      // access artifact ... this id grants nobody anything"), which was true only for the
      // slice that introduced it (steward-app#731, folder created with no ACL). Since
      // steward-app#732 the façade's `reconcileSharedFolderAcls` writes ACL entries onto
      // WHATEVER folder this id names — advisor `USR` reads, and advisor writes once
      // steward-app#742 lands.
      //
      // The consequence for anyone editing this type: `advisor-connection` declares no
      // `behaviors: ['access-controlled']`, so appcore gates its writes on `inst:write`,
      // which the steward MEMBER role holds. Writing this attribute is therefore the one
      // way a non-admin can cause an ACL grant, executed later under an admin's token.
      //
      // steward-app#765 closed that by refusing to touch an ACL the façade did not write:
      // every entry it writes carries an inert provenance marker verb, and a non-empty ACL
      // without one is left alone. **That control lives entirely in the façade, not here.**
      // Do not restore the old reassurance, and think about #765 before adding another
      // field on this type that the façade turns into a grant target — the type itself is
      // still ungated, so any such field is member-writable.
      // `carriedSharedFolderId` in the façade's `handlers/advisor.ts` carries the full
      // account and the bounds.
      { name: 'sharedFolderId', type: 'string', widget: 'text',  required: false, label: 'Shared folder id' },
      // ⚠️ **THE CONTRIBUTOR OPT-IN (steward-app#742 step 4).** `true` means the family has
      // asked that this firm be able to ADD and UPDATE documents inside the shared folder
      // named above. It is a REQUEST, never the grant: the grant is (a) the confinement
      // write verb on the firm's own `USR` entry on that folder and (b) the
      // `steward-advisor-contributor` role on the firm's membership, and only
      // `handlers/advisor.ts`' reconcile writes either, in that order, under a caller who
      // holds `mem:manage`.
      //
      // OPTIONAL and additive, exactly like `sharedFolderId`: rows written before this
      // field existed simply lack it, and absent reads as `false` everywhere. It has to be
      // DECLARED even so, because this type is `schemaMode: 'strict'` and an undeclared
      // attribute is a 400 from appcore.
      //
      // ⚠️ **It is member-writable on the generic entity plane, and that is the SAME class
      // of hazard `sharedFolderId` documents above** — read that ⚠️ first. Since step 3 this
      // type declares `writeCapability: STEWARD_WRITE_CAPABILITY`, so the principals who can
      // write it are the family's own ADMIN and MEMBER roles plus any `mem:manage` holder;
      // notably it is NOT writable by the contributor role itself, which holds `inst:write`
      // and not `steward:write` — a firm cannot ask for its own promotion. What a family
      // MEMBER can do is set this flag and have the next ADMIN page load act on it; the
      // façade route that sets it requires an admin (`assertWorkspaceAdmin`), so that path
      // is the ungated attribute and not the product. Bounded by what the flag can ever buy:
      // add and update inside ONE folder the family already shares with that firm, with
      // destroy and re-home held above it by `custodyVerb`.
      { name: 'contribute',     type: 'boolean', widget: 'checkbox', required: false, label: 'Firm may add and update documents in the shared folder' },
    ],
    accessPatterns: [{ name: 'by_firm', fields: ['firmId'] }],
    presentation: {
      displayField: 'firmName',
      listColumns: ['firmName', 'status', 'connectedAt'],
      defaultSort: { field: 'connectedAt', direction: 'desc' },
    },
  },
  {
    // ── THE ONBOARDING REQUEST (steward-app#1426, founder rulings 2026-09-16) ─────────────
    //
    // A firm's request that a connected family complete its onboarding. ONE row per firm and
    // family pair, `ENTITY#onboarding-request#<firmTenantId>` in the FAMILY's workspace, and it
    // is re-issued IN PLACE (ruling 3): `lastIssuedAt` moves, the rungs derive from answers made
    // after it, and past issuances live in the audit trail rather than in extra rows.
    //
    // ## Who writes it: the firm, through appcore's engagement-authorized write only
    //
    // `PUT /w/{ws}/engagements/{firmTenantId}/instances/onboarding-request`
    // (crossmarc/appcore#679, merged as appcore#683, read at appcore `19caa7c`). appcore reads
    // `engagementWrite` below off the PACK row, never a workspace-local shadow, and admits a
    // caller who is a live member of this family workspace AND holds `firmCapability` through an
    // active membership in the named firm's own tenant AND whose engagement pair with this family
    // is `active` on both sides. The instance id is the firm tenant id, derived by the server,
    // and the row carries a server stamp of it. The route refuses a type with an access parent,
    // a `readVerb`/`custodyVerb`, ANY behaviour, `versioned`, a schemaless mode, or a sensitive
    // or derived field, which is why this declaration has none of them.
    // `onboardingRequest.test.ts` pins each.
    //
    // ⚠️ The declaration is INERT until two things are true in an environment: appcore's route
    // is deployed there, and the seed has written this row and firm staff's grant. Neither
    // order hurts: without the route nothing reads the field, and without the grant the route
    // refuses every firm.
    //
    // ## No `firmId` attribute, deliberately
    //
    // The firm IS the instance id and the server stamp. A `firmId` attribute would be a second,
    // client-written copy of the one fact appcore already binds, and the only one that could be
    // forged. The façade reads the firm off the id.
    //
    // ## `readCapability`: none (ruling 4)
    //
    // Read-ungated at appcore, so every connected firm's advisors (`steward-advisor` holds
    // `inst:read`) can read every firm's request row in this family through the generic plane.
    // Isolation between two firms connected to the same family is explicitly NOT required at
    // appcore; the façade filters by firm, the `advisor-connection` shape. Listed in
    // `PILLAR_UNGATED_TYPE_CODES` for that reason.
    //
    // ## `writeCapability: onboarding-request:write`, a string NO role holds
    //
    // One of two deliberate exceptions to the uniform `steward:write` rule
    // (`stewardWriteCapability.test.ts`, which names both; the other is `advisor-client` in
    // `STEWARD_ADVISOR_FIRM_TYPE_DEFS`, crossmarc/steward-app#1781). The capability closes appcore's
    // GENERIC entity plane (`/w/{ws}/entities/onboarding-request[/{id}]`) to every principal
    // but a `*:*` holder, and leaves the engagement route open:
    //
    // - **The engagement route does not consult it.** appcore's handler applies neither
    //   `readCapability` nor `writeCapability` (they key on the caller's roles in the FAMILY
    //   workspace, which are not that route's authority), so the firm's write path is unaffected.
    // - **Why no role, not `steward:write`.** The rungs derive from answers made after
    //   `lastIssuedAt`. Under `steward:write` any family MEMBER could PUT that attribute and
    //   silently move the "requested" rung, which is the forgery the founder rejected when he
    //   ruled out a family-token write (option c3 on steward-app#1426). This type has no
    //   `readVerb` and no `parent`, so appcore's write confinement does not reach it
    //   (`confinementCovers`, appcore `access.ts`) and the capability is the ONLY generic-plane
    //   control: without it a firm's contributor advisor (`inst:write`, `writeConfined`) could
    //   also UPDATE or DELETE any firm's row, including another firm's.
    //   `onboardingRequest.test.ts` pins that no role in either pack holds the string, by
    //   wildcard or literal.
    // - **The arming check's exemption.** `scripts/lib/packArming.ts` blocks a release that arms a
    //   capability no declared role grants (`armed-capability-has-no-declared-holder`), because
    //   that is normally a deny-all accident. It exempts a WRITE capability armed only on types
    //   that also declare `engagementWrite`, where deny-all on the generic plane is the design,
    //   and (since crossmarc/steward-app#1781) a write capability listed in
    //   `SYSTEM_ONLY_WRITE_CAPABILITIES`. Every other unheld capability still blocks.
    // - **What remains (the residual).** The only generic-plane writer left is a family admin
    //   who SHADOWS the type with a workspace-local definition (`type:write`,
    //   crossmarc/appcore#313) that drops or changes this declaration, then edits or deletes the
    //   row. That is a family misreporting or discarding a request made to itself. The id and the
    //   server stamp are not client writable either way: generic create mints its own ids
    //   (`ulid()`, appcore `handlers/entities.ts`), a family shadowing the type as file-backed
    //   can pre-create a firm-keyed row through the documents route but the firm's update refuses
    //   a row it did not stamp (appcore#679 A4), and the engagement route reads the PACK row, so
    //   a shadow cannot change what the firm's write validates against.
    //
    // ## `auditLevel: 'strict'`, with both fields `auditSafe`
    //
    // Re-issue history lives in the audit trail, not in extra rows (ruling 3). `strict` puts a
    // type-level AUDIT row inside the same transaction as every create, update and delete, on
    // both planes. On the engagement route that row is appended AFTER the route's own guard and
    // audit items (appcore `entity-instance-repository.ts` `create`/`update`, read at `19caa7c`),
    // so the positions the route maps a cancellation by do not move. Not exercised against a
    // live route from this repo. `auditSafe` lets the audit delta carry the literal values
    // instead of `[REDACTED]`: neither is sensitive (a timestamp and a Cognito user id), and a
    // redacted `lastIssuedAt` would make the re-issue history unreadable.
    typeCode: ONBOARDING_REQUEST_TYPE_CODE,
    name: 'Onboarding Request',
    // Explicit rather than the seed's default: appcore's route refuses anything but `strict`,
    // and strict is also what bounds the attribute bag to the two fields below.
    schemaMode: 'strict',
    accessControl: { writeCapability: ONBOARDING_REQUEST_WRITE_CAPABILITY },
    auditLevel: 'strict',
    // Honoured by appcore on the PACK row only. `firmCapability` is held by
    // `steward-firm-staff` alone (`packRoles.ts`, `ONBOARDING_REQUEST_CAPABILITY`).
    engagementWrite: { firmCapability: ONBOARDING_REQUEST_CAPABILITY },
    fields: [
      // When the firm last (re)issued the request. The façade stamps it from its own clock;
      // appcore's `datetime` validation refuses a value `Date.parse` cannot read.
      { name: 'lastIssuedAt',      type: 'datetime', widget: 'date', required: true, label: 'Last issued at (ISO timestamp)',
        validation: { auditSafe: true } },
      // The firm principal who issued it. The façade sets it from the session, never from the
      // request body; appcore does not bind it to the caller, so it is a record, not a proof.
      { name: 'requestedByUserId', type: 'string',   widget: 'text', required: true, label: 'Requested by (user id)',
        validation: { minLength: 1, maxLength: 128, auditSafe: true } },
    ],
    accessPatterns: [],
    presentation: {
      displayField: 'lastIssuedAt',
      listColumns: ['lastIssuedAt'],
      defaultSort: { field: 'lastIssuedAt', direction: 'desc' },
    },
  },
  {
    // ── THE ONBOARDING CONFIRMATION (steward-app#1426, founder rulings 2026-09-16) ────────
    //
    // The family's answer to a firm's `onboarding-request`: "Done, send to my advisor"
    // (second set, ruling 2). Completion of a request needs one of these made AFTER the
    // request's latest `lastIssuedAt`, so a re-issue makes every earlier confirmation stale
    // without deleting it.
    //
    // ## Who writes it: the FAMILY, on the ordinary member plane
    //
    // Created by `POST /onboarding-requests/{firmId}/confirmations`
    // (`services/steward-app-api/src/handlers/onboardingRequest.ts`) under the family
    // caller's own token, and only once the façade has read the roster and found every person
    // carrying `ONBOARDING_REQUIRED_PERSON_FIELDS` (`onboarding.ts`). `writeCapability` is the
    // pack's uniform `steward:write`, which the family's ADMIN and MEMBER hold and no firm role
    // does, so a firm cannot write a confirmation for a family (the contributor advisor holds
    // `inst:write` without `steward:write`). A family member calling appcore's generic plane
    // directly could skip the façade's roster check; that is a family misreporting its own
    // readiness to its own advisor, and the firm's stage re-checks the roster whenever People
    // is shared with it.
    //
    // ## Several rows, and the façade reads the latest per firm
    //
    // Append-only by use: every confirmation is a new row, so the history of "the family said
    // done" survives a re-issue. Readers take the greatest `confirmedAt` among the rows whose
    // `firmTenantId` equals their firm.
    //
    // ## `readCapability`: none, like the request (ruling 4)
    //
    // Read-ungated at appcore, so every connected firm's advisors can read every firm's
    // confirmation rows through the generic plane. The façade filters by `firmTenantId`,
    // the `advisor-connection` shape, and `PILLAR_UNGATED_TYPE_CODES` lists it for that
    // reason.
    //
    // ## `firmTenantId` IS an attribute here, unlike the request's firm
    //
    // The request's firm is its instance id and appcore's server stamp. A confirmation is a
    // family row with a minted id, so the firm it answers has to be written down. It is set
    // by the façade from the firm directory (slug to tenant), never from the request body.
    //
    // ## `auditLevel: 'strict'`, all three fields `auditSafe`
    //
    // A tenant id, a timestamp and a Cognito user id: none sensitive, and a redacted
    // `confirmedAt` would make the audit trail of who confirmed what, when, unreadable.
    typeCode: ONBOARDING_CONFIRMATION_TYPE_CODE,
    name: 'Onboarding Confirmation',
    schemaMode: 'strict',
    accessControl: { writeCapability: STEWARD_WRITE_CAPABILITY },
    auditLevel: 'strict',
    fields: [
      // The firm's appcore tenant id, the same value the request row is keyed by.
      { name: 'firmTenantId',      type: 'string',   widget: 'text', required: true, label: 'Firm (tenant id)',
        validation: { minLength: 1, maxLength: 64, auditSafe: true } },
      // When the family confirmed. The façade stamps it from its own clock.
      { name: 'confirmedAt',       type: 'datetime', widget: 'date', required: true, label: 'Confirmed at (ISO timestamp)',
        validation: { auditSafe: true } },
      // The family principal who confirmed, from the session, never from the request body.
      { name: 'confirmedByUserId', type: 'string',   widget: 'text', required: true, label: 'Confirmed by (user id)',
        validation: { minLength: 1, maxLength: 128, auditSafe: true } },
    ],
    accessPatterns: [],
    presentation: {
      displayField: 'confirmedAt',
      listColumns: ['confirmedAt'],
      defaultSort: { field: 'confirmedAt', direction: 'desc' },
    },
  },
  // steward-app#2581 (docs/39 §8.2): the five cash flow types, granted in slice 2 and armed in
  // slice 4 (steward-app#2583). Read `cashFlowTypeDefs.ts` before touching their `accessControl`.
  ...CASH_FLOW_TYPE_DEFS,
];

/**
 * The **steward-advisor-firm** pack's TYPE facet.
 *
 * It lived as a local `const` inside `scripts/seed-steward-advisor-firm.ts` until
 * steward-app#965. It moved here for the reason `packRoles.ts` gives for the ROLE defs:
 * the seed script cannot be imported (it throws on a missing `TABLE_NAME` at module scope
 * and calls `main()` on load), so anything declared inside it is invisible to every other
 * consumer. `scripts/check-pack-arming.ts` is now one of those consumers — it has to know
 * what this pack arms in order to refuse a release that arms ahead of its grant, and a
 * second empty array copied into the checker would go stale the first time Phase 2 adds a
 * type, which is precisely the deploy the check exists to catch.
 *
 * ⚠️ **This array was `[]` until #1012, and its first entry changes two things that were
 * previously no-ops. Both were checked at source rather than assumed:**
 *
 *  1. **The pack manifest gains a `types` facet.** `seed-steward-advisor-firm.ts` derives
 *     `facets` from `STEWARD_ADVISOR_FIRM_TYPE_DEFS.length`, so the first run after this
 *     lands rewrites the manifest and bumps its `version`. That is the behaviour the seed
 *     script's own header anticipates ("when Phase 2 starts populating …"), not a
 *     surprise, and the facet list is descriptive metadata: appcore's
 *     `custom-type-resolver.ts` reads `ws.packs` directly and never consults the manifest.
 *  2. **No existing firm workspace needs re-mounting.** Verified in appcore
 *     (`packages/core/src/db/custom-type-resolver.ts` `resolveCustomType`, and
 *     `db/keys.ts` `packCtypeKey`): a type is resolved per request as workspace-local
 *     override → each mounted pack's `APP#<packId>` / `CTYPE#<typeCode>` row → not found.
 *     Nothing is copied into a workspace at mount time. So one pack-level upsert makes
 *     `intake-draft` resolvable in every workspace already mounting this pack, bounded by
 *     the resolver's 60-second in-process pack-type cache. A pack-level CTYPE seed is
 *     sufficient; there is no per-workspace write and no onboarding-runbook change.
 *
 * ⚠️ **"This pack arms nothing" stopped being true at steward-app#742 step 3.** It was true
 * while `intake-draft` declared no `accessControl`; it now declares
 * `writeCapability: STEWARD_WRITE_CAPABILITY`, so `scripts/lib/packArming.ts`'s
 * `armedCapabilitiesOf` collects it and the promotion check has an opinion about this pack
 * for the first time. Two consequences, both intended and both verified at source:
 *
 *  - **No `armed-capability-has-no-declared-holder`.** That rule asks whether ANY role the
 *    RELEASE declares, in any pack, grants the string — `check-pack-arming.ts` builds
 *    `ReleaseContext.declaredRoles` as `PACKS.flatMap(...)`. The steward-app pack's ADMIN
 *    and MEMBER grant it, so the finding does not fire even though neither firm role does.
 *    `advisor-client` arms a string NO role grants (`advisor-client:write`,
 *    crossmarc/steward-app#1781), and what keeps the finding quiet for it is a different
 *    thing: its listing in `SYSTEM_ONLY_WRITE_CAPABILITIES`, which that rule exempts.
 *  - **A firm workspace has no user-plane writer of this type, by design.** Nothing in the
 *    `steward-advisor-firm` pack grants `steward:write`, so on appcore's user plane this
 *    type is closed to everyone but a `*:*` holder. That is NARROWER than the "without
 *    break-glass" this line used to read: appcore#603 narrowed the set that short-circuits
 *    a `writeCapability` to exactly that one string. Its three real callers are on
 *    `/system/*`, which runs no gate from `access.ts` at all. See the type's own comment.
 *
 * `packArming.test.ts` pins this against the REAL defs rather than a hardcoded `[]`, which
 * is why the sentence above had to be corrected here rather than quietly going stale.
 */
export const STEWARD_ADVISOR_FIRM_TYPE_DEFS: readonly FoTypeDef[] = [
  {
    // Claimed intake (docs/29 §The claim, crossmarc/steward-app#1012) — a prospect's
    // PARTIAL "Financial Snapshot", saved before they are willing to submit it, so the
    // fifteen minutes of typing survive closing the tab and moving to another device.
    //
    // It lives in the ADVISOR-FIRM pack, not `steward-app`, and that placement is a
    // security decision rather than a filing convenience. appcore bounds the three intake
    // background actors with `{ requiredPack: 'steward-advisor-firm', typeCodes: [...] }`
    // (appcore `packages/core/src/auth/system-plane.ts`), and the advisor pack is the
    // discriminator precisely because `steward-app` is mounted by EVERY family workspace —
    // a `steward-app` scope would bound nothing. Declaring a prospect-disclosure type in
    // the `steward-app` pack would also mount it into every family workspace in the fleet
    // for no reason at all.
    //
    // Written and read ONLY over appcore's `/system/*` plane, by three background
    // processes: the intake writer (create + update), the claimed-intake draft reader
    // (single-instance GET), and the retention sweeper (LIST + DELETE at expiry). The
    // public façade route validates and enqueues; it holds no appcore access whatsoever.
    // The CONSTANT, not a second literal. `intakeDraft.ts` claims this string lives in
    // one place; that claim is only true if this is the one place it is read from.
    typeCode: INTAKE_DRAFT_TYPE_CODE,
    name: 'Intake Draft',
    // No `readCapability`, deliberately, and the absence is the honest declaration rather
    // than an omission. A READ capability here would buy nothing: appcore's
    // `handlers/system-entities.ts` runs no Tier-3 gate at all on `/system/*` and returns
    // raw attributes by design, so a `readCapability` declared on this type would not
    // constrain the only three callers it has (appcore `system-plane.ts` records exactly
    // this, on `SYSTEM_CAPABILITIES`). The bounds that DO apply are the per-actor scope at
    // appcore and the per-role `execute-api` statements in `deploy/lib/intake-stack.ts`.
    //
    // steward-app#742 step 3 arms the WRITE axis anyway, on the pack-wide uniform rule,
    // and for a reason that survives the paragraph above rather than contradicting it. The
    // three system callers are unaffected for exactly the reason given — no gate runs on
    // that plane. What it closes is the USER plane, which is a different plane with a
    // different threat: appcore's user API is publicly addressable, and a firm-workspace
    // member holding `inst:write` could otherwise create, edit or destroy a prospect's
    // partial financial disclosure by calling it directly. No role in the
    // `steward-advisor-firm` pack grants `steward:write`, so on the user plane this is a
    // deliberate deny-all-but-`*:*` (appcore#603 narrowed the short-circuiting set to that
    // one string, so this is narrower than the "deny-all-but-break-glass" it used to say)
    // — which is the correct posture for a type with no user-plane write route. It is ALSO
    // the answer to the ⚠️ below: a firm-side MEMBER
    // route mounted later now starts from closed rather than from open.
    // ⚠️ If a firm-side MEMBER route is ever mounted on this type, revisit the READ side —
    // it is still ungated, and this write declaration says nothing about it.
    //
    // steward-app#1080's argument, applied to a type it fits BETTER than the one it was
    // written for. `resolveAuditLevel` defaults to `off`, and every input it resolves from
    // was verified in prod to set nothing. A draft is destroyed more often than a
    // submission (every one the firm cannot see expires at 60 days under docs/29 D5) and is MUTATED
    // repeatedly before that, so both the update trail and the delete trail matter here.
    // `strict`, not `standard`, for the reason `advisor-intake` gives above: `standard` is
    // `void emitAuditStandard(...)`, fire-and-forget, so a failed audit write is silently
    // swallowed, while `strict` puts the AUDIT row inside the same transactWrite.
    auditLevel: 'strict',
    // Schemaless for the same reason as `advisor-intake`: the payload is the nested
    // 11-section object, and appcore's flat strict-mode field validation cannot express
    // it. The validation authority is `IntakeDraftSectionsSchema` (`intakeDraft.ts`),
    // itself DERIVED from `IntakeSubmissionShape` so the two cannot drift; the field list
    // below carries the display columns only.
    schemaMode: 'schemaless',
    accessControl: { writeCapability: STEWARD_WRITE_CAPABILITY },
    fields: [
      { name: 'firmId',            type: 'string', widget: 'text',     required: true,  label: 'Firm' },
      // sha256(throttleKey(email)). It exists so the writer can re-check the one-active-claim
      // binding. The plaintext of the address the link was mailed to is a separate attribute,
      // `claimEmail` (steward-app#1625), written only when it hashes to `claimExactEmailHash`;
      // the About You answer lives in `sections.aboutYou.email`. It is KEPT on a tombstoned row even though `sections` is emptied — it is
      // what proves which address demonstrated control of which submission (#1016).
      { name: 'claimEmailHash',    type: 'string', widget: 'text',     required: true,  label: 'Claim email hash' },
      // The partial draft. Schemaless mode makes this declaration presentational; the
      // stored value is the nested sections object.
      { name: 'sections',          type: 'array',  widget: 'textarea', required: true,  label: 'Draft sections' },
      // Stamped by the ROUTE from its own clock, and the writer refuses a write whose
      // `savedAt` is older than the stored value. The intake queue is a standard SQS
      // queue with no ordering, so this monotonic guard is required, not defensive.
      { name: 'savedAt',           type: 'string', widget: 'text',     required: true,  label: 'Saved at (ISO timestamp)' },
      // The WRITER's clock at write time — distinct from `savedAt` on purpose. This is
      // what an advisor's "stalled at section 4" derivation reads.
      { name: 'updatedAt',         type: 'string', widget: 'text',     required: true,  label: 'Updated at (ISO timestamp)' },
      { name: 'status',            type: 'enum',   widget: 'select',   required: true,  label: 'Status (draft | submitted | tombstoned)' },
      // docs/29 D5's 60 days, re-stamped from `updatedAt` on every save. The sweeper keys
      // on THIS, not on the firm's 24-month submission window — a second retention policy
      // in one Lambda.
      //
      // OPTIONAL since crossmarc/steward-app#2549 (Matt's ruling 2026-10-24): a draft the firm
      // can see (claimed, plus a sharing consent or an attached invitation, `intakeDraftFirmCanSee`) does
      // not expire, and the writer then writes the row WITHOUT this attribute rather than with a
      // far-future date. The invitation half reads the stored id only, so after a cancelled
      // invitation a no-consent draft stays unexpiring until its next save
      // (crossmarc/steward-app#2560). Absent means "does not expire". The type is schemaless, so appcore
      // never enforced `required` here; the flag is the declaration, not a gate.
      { name: 'expiresAt',         type: 'string', widget: 'text',     required: false, label: 'Expires at (ISO timestamp)' },
      // Set at submit, so draft → intake joins for #1016. Absent on a live draft.
      { name: 'submittedIntakeId', type: 'string', widget: 'text',     required: false, label: 'Submitted intake id' },
      // ⚠️ NOT DECLARED HERE, on purpose: `clientId` (crossmarc/steward-app#2551). The intake
      // writer stamps the firm's `advisor-client` record id on the row, from the record the
      // client-record ensure establishes for the row's proved address (the invited address once
      // an invitation attaches, else the login's), and never from a message. Absent on a row
      // written before #2551 and on one whose ensure did not succeed; the address joins
      // (`draftClientAddress`) still apply. It is left out of `fields` exactly as
      // `advisor-intake`'s writer-stamped `clientId` is: `clientPurgeManifest.test.ts` derives
      // the FAÇADE purge's scope from a declared `clientId` field, and this type's purge owner
      // is the S3 sweeper (the out-of-band manifest), so declaring it would claim the type for
      // the wrong deleter. The type is schemaless, so appcore stores it either way.
    ],
    accessPatterns: [],
    presentation: {
      displayField: 'firmId',
      listColumns: ['firmId', 'status', 'updatedAt', 'expiresAt'],
      defaultSort: { field: 'updatedAt', direction: 'desc' },
    },
  },
  {
    // `advisor-client` (crossmarc/steward-app#1763, docs/35 "The client record") — the
    // firm-side client record, one row per prospect/client, that the new profile page is
    // keyed by. Full rationale for the field list, the dedup approach, and this type's
    // placement in THIS pack rather than `STEWARD_APP_TYPE_DEFS` (a coordinated appcore#736
    // contract, not derived from the placement precedent the type's own file names) lives
    // in `advisorClient.ts`'s header, which this declaration mirrors rather than repeats.
    //
    // ## Written ONLY over `/system/*`, by the intake writer (crossmarc/steward-app#1781)
    //
    // The façade READS this type on the user plane, under the advisor's own bearer token
    // (`listAllEntities` and `getEntity` in
    // `services/steward-app-api/src/services/clientRecords.ts`), and never writes it: it
    // sends an `ensure-client` message, and the intake writer creates the row over
    // appcore's `/system/*` plane under a KMS-MAC id (`services/intake-writer/src/
    // clientRecord.ts`), so the id cannot be chosen and a duplicate create is a 409. (This
    // comment used to say the façade "creates and lists" it. It never created it on main.)
    //
    // So the write axis is armed with `advisor-client:write`, a string NO pack role holds
    // (`ADVISOR_CLIENT_WRITE_CAPABILITY`, `packRoles.ts`), the same shape as
    // `onboarding-request` in `STEWARD_APP_TYPE_DEFS`. Under the `steward:write` this type
    // declared before #1781, any firm member holding `steward-admin` or `steward-member` (a
    // firm workspace mounts `steward-app` too) could create, replace or delete these rows
    // through appcore's generic entity routes. The writer is unaffected: appcore enforces
    // `writeCapability` only in `access.ts`, which `/system/*` never runs. What still
    // bounds the writer is its actor scope at appcore and its `execute-api` statement in
    // `deploy/lib/intake-stack.ts` (POST on the collection path only).
    //
    // ⚠️ **What remains (the residual), and why this is not "only the writer can write".**
    // A workspace-local CTYPE row always wins over this pack row (appcore
    // `custom-type-resolver.ts`), and `steward-admin` holds `type:write`. A firm's
    // `steward-admin` can therefore shadow this type in the firm's own workspace with a
    // definition that drops the declaration, and write rows again. That is
    // crossmarc/appcore#313, open on 2026-09-25, and this declaration does not close it. It
    // stays inside that firm's workspace. The `onboarding-request` entry names the same
    // residual for itself.
    //
    // Unheld on purpose, so `SYSTEM_ONLY_WRITE_CAPABILITIES` lists it: that is what exempts
    // it from the release's no-declared-holder arming check (`scripts/lib/packArming.ts`).
    // `advisorClient.test.ts` pins both halves: the type declares the string, and no role
    // in either pack holds it.
    typeCode: ADVISOR_CLIENT_TYPE_CODE,
    name: 'Advisor Client',
    auditLevel: 'strict',
    accessControl: { writeCapability: ADVISOR_CLIENT_WRITE_CAPABILITY },
    fields: [
      { name: 'firmId',    type: 'string', widget: 'text', required: true, label: 'Firm' },
      // Dedup key — `normalizeClientEmailKey` (`advisorClient.ts`) applied to the address
      // this record was first seen under. See that file for what "first seen" covers.
      { name: 'emailKey',  type: 'string', widget: 'text', required: true, label: 'Email key' },
      { name: 'createdAt', type: 'string', widget: 'text', required: true, label: 'Created at (ISO timestamp)' },
      { name: 'createdBy', type: 'string', widget: 'text', required: true, label: 'Created by (user id)' },
    ],
    accessPatterns: [],
    presentation: {
      displayField: 'emailKey',
      listColumns: ['firmId', 'emailKey', 'createdAt'],
      defaultSort: { field: 'createdAt', direction: 'desc' },
    },
  },
  {
    // `client-about` (crossmarc/steward-app#1768, docs/35 R3 "the Fun List is a firm record")
    // — the firm's team notes about a client's family. Full design in `clientAbout.ts`: the
    // eleven-field closed list, the `{value, source}` shape, why the source is server-stamped
    // and why an unchanged field keeps its original source across a whole-page edit.
    //
    // Same write shape as `advisor-client` and `onboarding-invitation` immediately above:
    // written on the USER plane by the façade under the firm caller's own bearer token
    // (`services/steward-app-api/src/handlers/clientAbout.ts`), never a background role, and
    // armed on the SAME union those two document — no role in THIS pack carries
    // `steward:write`, but a firm workspace mounts `steward-app` too, and
    // `steward-admin`/`steward-member` there do (`packRoles.ts`).
    //
    // Schemaless, like `advisor-intake` and `client-steward-setup` (#1859). It first shipped
    // `strict`, and appcore compiles a strict `array` field to `z.array(z.string())` only
    // (`packages/core/src/db/zod-compiler.ts`; there is no object field kind). `fields` is an
    // OBJECT keyed by the eleven About keys, each `{value, source}`, so appcore rejected every
    // create and update with 400 VALIDATION_ERROR and the About tab could never save. There is
    // no appcore field kind that can express that shape, so validation belongs to the façade:
    // `ClientAboutAttributesSchema` (`clientAbout.ts`) is parsed on every write
    // (`upsertAbout`) and on the route's read (`findAboutRow`, via `readClientAbout`; the
    // purge alone also matches an unparseable row by raw `clientId`, so it can still delete
    // it). The façade's typed handler is the
    // only writer, and no façade route forwards a caller-chosen type code
    // (`appcoreTypeCodeIsClosed.test.ts`). `STRICT_ARRAY_FIELDS_OF_STRINGS` in
    // `packTypeDefs.test.ts` now pins that no strict type ships an array field that is not
    // a reviewed string array.
    //
    // `strict`, matching `onboarding-invitation`: the row is mutated repeatedly as the firm
    // adds to it over time, and the audit trail across those updates is what an AUDIT-level
    // write is for. No `sensitive` field (`auditLevel.test.ts`'s #1260 rule does not require
    // this), chosen because the mutation history itself is worth keeping, not because a
    // disclosure tier applies.
    typeCode: CLIENT_ABOUT_TYPE_CODE,
    name: 'Client About',
    auditLevel: 'strict',
    schemaMode: 'schemaless',
    accessControl: { writeCapability: STEWARD_WRITE_CAPABILITY },
    fields: [
      { name: 'firmId',    type: 'string', widget: 'text',     required: true, label: 'Firm' },
      { name: 'clientId',  type: 'string', widget: 'text',     required: true, label: 'Client record (advisor-client id)' },
      // The eleven-field object, each entry `{value, source: {kind, userId, name, at}}`.
      // See `clientAbout.ts`'s `ClientAboutFieldsSchema` for the enforced shape.
      { name: 'fields',    type: 'array',  widget: 'textarea', required: true, label: 'About fields (anniversary, dreams, favorite media, hobbies, sports played, favorite teams, milestones, dislikes, food/snacks/alcohol, allergies, alma mater), each {value, source}' },
      { name: 'updatedAt', type: 'string', widget: 'text',     required: true, label: 'Updated at (ISO timestamp)' },
      { name: 'updatedBy', type: 'string', widget: 'text',     required: true, label: 'Updated by (user id)' },
    ],
    accessPatterns: [],
    presentation: {
      displayField: 'clientId',
      listColumns: ['firmId', 'clientId', 'updatedAt'],
      defaultSort: { field: 'updatedAt', direction: 'desc' },
    },
  },
  {
    // `firm-client-document` (crossmarc/steward-app#1769, docs/35) — a firm's own document
    // library on one prospect's or client's profile. Full design in
    // `firmClientDocument.ts`'s header: why it is file-backed with its OWN prefix rather than
    // reusing `document`, why the team gate (admin or the client's assigned advisor, D1) is
    // the FAÇADE's job and not expressible as an `accessControl` declaration here, and the
    // "one row per document, found by clientId attribute" shape.
    //
    // Same write posture as `advisor-client` and `client-about` immediately above: written on
    // the USER plane by the façade under the firm caller's own bearer token
    // (`services/steward-app-api/src/handlers/firmClientDocuments.ts`), never a background
    // role, armed on the SAME union those two document — no role in THIS pack carries
    // `steward:write`, but a firm workspace mounts `steward-app` too, and
    // `steward-admin`/`steward-member` there do (`packRoles.ts`).
    //
    // `strict`, matching `client-about`: destroyed and recreated over a client relationship's
    // life (a firm replaces or removes documents), and the delete/create trail is what an
    // AUDIT-level write is for. `versioned` is deliberately NOT in `behaviors` — the façade
    // exposes no version-history route for this type (upload, list, download, delete only), so
    // declaring the behavior would advertise a capability nothing here uses.
    typeCode: FIRM_CLIENT_DOCUMENT_TYPE_CODE,
    name: 'Firm Client Document',
    behaviors: ['file-backed'],
    fileBacked: { keyPrefix: FIRM_CLIENT_DOCUMENT_KEY_PREFIX },
    auditLevel: 'strict',
    accessControl: { writeCapability: STEWARD_WRITE_CAPABILITY },
    fields: [
      { name: 'firmId',       type: 'string', widget: 'text', required: true,  label: 'Firm' },
      { name: 'clientId',     type: 'string', widget: 'text', required: true,  label: 'Client record (advisor-client id)' },
      { name: 'title',        type: 'string', widget: 'text', required: true,  label: 'Title' },
      { name: 'filename',     type: 'string', widget: 'text', required: true,  label: 'Filename' },
      { name: 'mimeType',     type: 'string', widget: 'text', required: true,  label: 'MIME type' },
      { name: 'fileSize',     type: 'number', widget: 'number', required: true, label: 'File size (bytes)' },
      { name: 's3Key',        type: 'string', widget: 'text', required: false, label: 'S3 object key' },
      { name: 'addedByName',  type: 'string', widget: 'text', required: true,  label: 'Added by (display name)' },
      { name: 'addedByUserId', type: 'string', widget: 'text', required: true, label: 'Added by (user id)' },
      { name: 'createdAt',    type: 'string', widget: 'text', required: true,  label: 'Added at (ISO timestamp)' },
      // Written by appcore's client document routes (`source: 'client'`, `requestId` from the
      // path), which refuse the type with 422 unless it declares both. Absent on firm-added rows.
      //
      // WHAT DECLARING THESE ARMS (read 2026-10-09 from appcore origin/main 997f437, its
      // `onboarding-client-document-repository.ts`): appcore's three client document routes
      // (mint an upload, commit the row, delete) stop answering 422 ONBOARDING_DOCUMENT_TYPE_INCOMPLETE
      // for this type. They are live at appcore for any SigV4 member-plane caller that also passes
      // appcore's client authority gate, behind appcore's own checks only; appcore does not check that `requestId` names an open request (its
      // grammar only). They are NOT reachable from a client's browser through this repo today: no
      // façade route relays them, and the member plane is SigV4 relay-only. Whether that is still
      // so is a fact about the code on the day you read this, so re-check it there.
      { name: 'source',       type: 'string', widget: 'text', required: false, label: 'Added by (client, when the client added it)' },
      { name: 'requestId',    type: 'string', widget: 'text', required: false, label: 'Information request it answers' },
    ],
    accessPatterns: [],
    presentation: {
      displayField: 'title',
      listColumns: ['clientId', 'title', 'addedByName', 'createdAt', 'fileSize'],
      defaultSort: { field: 'createdAt', direction: 'desc' },
    },
  },
  {
    // `onboarding-invitation` (crossmarc/steward-app#1764, docs/35 R2) — a firm invited a
    // client straight to its onboarding form. Full design in `onboardingInvitation.ts`.
    //
    // Written on the USER plane by the façade under the firm caller's own bearer token
    // (`services/steward-app-api/src/handlers/onboardingInvitation.ts`), exactly like
    // `intake-invitation`, and armed on the SAME union that type documents: no role in this
    // pack carries `steward:write`, but a firm workspace mounts `steward-app` too, and
    // `steward-admin`/`steward-member` there do. The façade's three
    // WRITE routes are gated on `mayProvisionAtFacade` (`engagement:provision`), because the
    // appcore signup grant a send mints right after this row is gated on that capability. Its
    // list route is open to any active member of the firm's own workspace
    // (crossmarc/steward-app#1965).
    //
    // ⚠️ **So any firm member holding `steward:write` can also create, replace or delete these
    // rows directly through appcore's generic entity routes, bypassing that gate.** Not
    // `advisor-client`'s fix (#1781): the façade writes this type as the CALLER, so an unheld
    // string would lock the façade out too. Closing it needs a system-plane writer and new
    // grants. The hazard (a tampered `state` or `email` retargets the grant mint and revoke)
    // is set out in crossmarc/steward-app#1781's first comment, 2026-09-25; the fix for this
    // type was split from that issue's and was still open when this was written.
    //
    // In THIS pack rather than `STEWARD_APP_TYPE_DEFS`, like `advisor-client`: it is firm
    // bookkeeping about a client record that lives here, and `steward-app` is mounted by
    // every family workspace, which has no use for it. No background role reads or writes it.
    typeCode: ONBOARDING_INVITATION_TYPE_CODE,
    name: 'Onboarding Invitation',
    // `strict`, like `intake-invitation`: a resend and a cancel MUTATE the row, and the audit
    // trail across those updates is what an AUDIT-level write is for.
    auditLevel: 'strict',
    accessControl: { writeCapability: STEWARD_WRITE_CAPABILITY },
    fields: [
      { name: 'firmId',            type: 'string',  widget: 'text',     required: true,  label: 'Firm' },
      { name: 'email',             type: 'string',  widget: 'text',     required: true,  label: 'Invited email (as the advisor typed it)' },
      { name: 'emailKey',          type: 'string',  widget: 'text',     required: true,  label: 'Email key' },
      { name: 'clientName',        type: 'string',  widget: 'text',     required: false, label: 'Client name (firm-entered, display only)' },
      { name: 'askSpouse',         type: 'boolean', widget: 'checkbox', required: true,  label: 'Ask for the spouse' },
      { name: 'askTrustedContact', type: 'boolean', widget: 'checkbox', required: true,  label: 'Ask for a trusted contact' },
      { name: 'askBeneficiaries',  type: 'boolean', widget: 'checkbox', required: true,  label: 'Ask for beneficiary wishes' },
      { name: 'askAboutYou',       type: 'boolean', widget: 'checkbox', required: true,  label: 'Ask About you' },
      { name: 'note',              type: 'string',  widget: 'textarea', required: false, label: 'Personal note' },
      { name: 'state',             type: 'enum',    widget: 'select',   required: true,  label: 'State (invited | cancelled)' },
      { name: 'createdAt',         type: 'string',  widget: 'text',     required: true,  label: 'Created at (ISO timestamp)' },
      { name: 'createdBy',         type: 'string',  widget: 'text',     required: true,  label: 'Created by (user id)' },
      { name: 'lastInvitedAt',     type: 'string',  widget: 'text',     required: true,  label: 'Last invited at (ISO timestamp)' },
      { name: 'hasAccount',        type: 'boolean', widget: 'checkbox', required: false, label: 'Already had a login at the last send' },
      { name: 'deliveredAt',       type: 'string',  widget: 'text',     required: false, label: 'Invitation email delivered at (ISO timestamp)' },
      { name: 'cancelledAt',       type: 'string',  widget: 'text',     required: false, label: 'Cancelled at (ISO timestamp)' },
      { name: 'cancelledBy',       type: 'string',  widget: 'text',     required: false, label: 'Cancelled by (user id)' },
    ],
    accessPatterns: [],
    presentation: {
      displayField: 'email',
      listColumns: ['email', 'state', 'createdAt'],
      defaultSort: { field: 'createdAt', direction: 'desc' },
    },
  },
  {
    // `onboarding-details` (crossmarc/steward-app#1765, design v2 grant 6) — the client's own
    // onboarding answers, one row per `advisor-client`, instanceId = that client id. Full design
    // in `onboardingForm.ts`.
    //
    // Written ONLY by appcore's `/me/onboarding-forms/{firmWorkspaceId}/...` routes, whose
    // authority is the client's onboarding signup grant; the writeCapability is held by no role
    // (`packRoles.ts`), so no firm member can write or forge an answer on the member plane.
    // No `readCapability` (design v2 grant 6, still open with Matt as of 2026-09-25). Firm
    // staff read the answers only through the façade's firm-side GET, which limits them to the
    // client's team, a firm admin or an assigned advisor (#1765 review F3). The SSN is never in
    // the row.
    //
    // MUST stay `strict` (appcore refuses a schemaless definition with 500
    // `ONBOARDING_TYPE_SCHEMALESS`), and its `fields` must declare every server-owned key appcore
    // writes, or send and the tax-id write fail validation after spending their quota.
    // `onboardingForm.test.ts` pins both. appcore reads this definition from the PACK row only
    // and refuses a workspace-local shadow (crossmarc/appcore#313 residual, named there).
    typeCode: ONBOARDING_DETAILS_TYPE_CODE,
    name: 'Onboarding Details',
    schemaMode: 'strict',
    auditLevel: 'strict',
    accessControl: { writeCapability: ONBOARDING_DETAILS_WRITE_CAPABILITY },
    fields: [...ONBOARDING_DETAILS_PACK_FIELDS],
    accessPatterns: [],
    presentation: {
      displayField: 'client_lastName',
      listColumns: ['client_firstName', 'client_lastName', 'sentAt'],
      defaultSort: { field: 'sentAt', direction: 'desc' },
    },
  },
  {
    // `onboarding-prefill` (crossmarc/steward-app#1765, design v2 §5 and grant 6) — what the
    // snapshot already said, for the client's form to start from. One row per `advisor-client`,
    // instanceId = that client id, written by the intake writer over `/system/*` at the
    // onboarding ensure and refreshed on each draft save or submit. Its write capability is
    // held by no role: the writer over `/system/*` does not consult it, and the client reads
    // through appcore's own form GET. No façade route exposes it to firm staff. It declares NO
    // read capability, unlike design v2 grant 6: see the note beside
    // `ONBOARDING_PREFILL_WRITE_CAPABILITY` in `packRoles.ts` for why and what that leaves.
    typeCode: ONBOARDING_PREFILL_TYPE_CODE,
    name: 'Onboarding Prefill',
    schemaMode: 'strict',
    auditLevel: 'strict',
    accessControl: { writeCapability: ONBOARDING_PREFILL_WRITE_CAPABILITY },
    fields: [...ONBOARDING_PREFILL_PACK_FIELDS],
    accessPatterns: [],
    presentation: {
      displayField: 'client_lastName',
      listColumns: ['client_firstName', 'client_lastName'],
      defaultSort: { field: 'client_lastName', direction: 'asc' },
    },
  },
  {
    // `client-purge` (crossmarc/steward-app#1770 S3, grant G6) — the journal a firm's
    // "permanently delete this client" writes before anything is deleted, and the only thing
    // the background purge acts on. Full design, including why the message alone can delete
    // nothing and why the finished row keeps no client id (decision D3), in `clientPurge.ts`.
    //
    // Written by the façade under the firm admin's own bearer token, so the ordinary appcore
    // gate (`inst:write` plus `steward:write`) decides who may create one, like every other
    // firm-authored type in this pack. Read and completed (one GET, one PUT) by the retention
    // sweeper's role over `/system/*`, as `system:intake-retention`, which needs this type in
    // that actor's appcore scope (G5, an appcore change).
    //
    // `strict` audit: an irreversible delete is exactly what the trail is for. The three id
    // fields carry no `auditSafe`, so the audit rows hold `[REDACTED]` markers for them rather
    // than the values the finished row itself drops.
    typeCode: CLIENT_PURGE_TYPE_CODE,
    name: 'Client Purge',
    schemaMode: 'strict',
    auditLevel: 'strict',
    accessControl: { writeCapability: STEWARD_WRITE_CAPABILITY },
    fields: [
      { name: 'firmId',            type: 'string',   widget: 'text',     required: true,  label: 'Firm',
        validation: { minLength: 1, maxLength: 32, pattern: '^[a-z0-9-]{1,32}$', auditSafe: true } },
      { name: 'clientId',          type: 'string',   widget: 'text',     required: false, label: 'Client record (dropped when done)',
        validation: { minLength: 1, maxLength: 200 } },
      { name: 'draftIds',          type: 'array',    widget: 'tags',     required: false, label: 'Drafts to remove (dropped when done)' },
      { name: 'intakeIds',         type: 'array',    widget: 'tags',     required: false, label: 'Submissions to remove (dropped when done)' },
      { name: 'requestedBy',       type: 'string',   widget: 'text',     required: true,  label: 'Requested by (user id)',
        validation: { minLength: 1, maxLength: 128, auditSafe: true } },
      { name: 'requestedAt',       type: 'datetime', widget: 'date',     required: true,  label: 'Requested at',
        validation: { auditSafe: true } },
      { name: 'state',             type: 'enum',     widget: 'select',   required: true,  label: 'State (running | done)',
        validation: { values: [...CLIENT_PURGE_STATES], auditSafe: true } },
      { name: 'completedAt',       type: 'datetime', widget: 'date',     required: false, label: 'Completed at',
        validation: { auditSafe: true } },
      { name: 'draftsPurged',      type: 'number',   widget: 'number',   required: false, label: 'Drafts removed',
        validation: { min: 0, auditSafe: true } },
      { name: 'intakesPurged',     type: 'number',   widget: 'number',   required: false, label: 'Submissions removed',
        validation: { min: 0, auditSafe: true } },
    ],
    accessPatterns: [],
    presentation: {
      displayField: 'requestedAt',
      listColumns: ['state', 'requestedAt', 'completedAt'],
      defaultSort: { field: 'requestedAt', direction: 'desc' },
    },
  },
  {
    // `onboarding-mark` (crossmarc/steward-app#1766 part 2, docs/35 R4 "Mark as onboarded") —
    // who marked a client onboarded, and when. Full design in `onboardingMark.ts`: why it
    // mirrors `client-about`'s placement and "one row per client, found by attribute" shape
    // exactly, why marking is immutable (a second mark is a no-op, not a re-stamp), and why
    // the write is gated more tightly than `client-about`'s at the FAÇADE (`requireTeamAccess`
    // plus `mayProvisionAtFacade`, `services/steward-app-api/src/handlers/onboardingMark.ts`)
    // while this type's own `accessControl` stays identical.
    //
    // Same write shape as `advisor-client` / `onboarding-invitation` / `client-about`: written
    // on the USER plane by the façade under the firm caller's own bearer token, never a
    // background role, and armed on the SAME union those document — no role in THIS pack
    // carries `steward:write`, but a firm workspace mounts `steward-app` too, and
    // `steward-admin`/`steward-member` there do (`packRoles.ts`).
    //
    // `strict` audit, matching `client-about`: one write, kept forever, and the audit trail is
    // the record of when the type-defined immutability guarantee above was ever tested.
    //
    // `schemaMode` strict, and every field a flat string (#1859). The marker was first a
    // nested `markedBy: {userId, name}` declared `type: 'array'`, which appcore compiles to
    // `z.array(z.string())`, so every mark 400'd. Flattened rather than made schemaless, so
    // appcore still validates the row. `packTypeDefs.test.ts` pins the array rule.
    typeCode: ONBOARDING_MARK_TYPE_CODE,
    name: 'Onboarding Mark',
    auditLevel: 'strict',
    accessControl: { writeCapability: STEWARD_WRITE_CAPABILITY },
    fields: [
      { name: 'firmId',    type: 'string', widget: 'text', required: true, label: 'Firm' },
      { name: 'clientId',  type: 'string', widget: 'text', required: true, label: 'Client record (advisor-client id)' },
      { name: 'markedByUserId', type: 'string', widget: 'text', required: true, label: 'Marked by (appcore userId)' },
      { name: 'markedByName',   type: 'string', widget: 'text', required: true, label: 'Marked by (display name)' },
      { name: 'markedAt',  type: 'string', widget: 'text', required: true, label: 'Marked at (ISO timestamp)' },
    ],
    accessPatterns: [],
    presentation: {
      displayField: 'clientId',
      listColumns: ['firmId', 'clientId', 'markedAt'],
      defaultSort: { field: 'markedAt', direction: 'desc' },
    },
  },
  {
    // `client-account-number-mark` (crossmarc/steward-app#2279, "Information requests 1") — the
    // firm's record that the full number for ONE account on a client's snapshot is already on
    // file, with who said so and when. Full design in `clientAccountNumberMark.ts`: why no field
    // can hold a number, how an account is keyed, and why a mark with no matching account is
    // kept under its stored label.
    //
    // Cloned from `onboarding-mark`: written on the USER plane by the façade under the firm
    // caller's own bearer token (gated there by the three `firmClientDocuments.ts` gates, ending
    // in `requireTeamAccess`), never a background role, and the SAME `accessControl`. Unlike
    // `onboarding-mark` there are many rows per client, one per marked account.
    //
    // `schemaMode` strict, every field a flat string (#1859), `strict` audit: who marked and
    // un-marked what is the record.
    typeCode: CLIENT_ACCOUNT_NUMBER_MARK_TYPE_CODE,
    name: 'Client Account Number Mark',
    auditLevel: 'strict',
    accessControl: { writeCapability: STEWARD_WRITE_CAPABILITY },
    fields: [
      { name: 'firmId',       type: 'string', widget: 'text', required: true, label: 'Firm' },
      { name: 'clientId',     type: 'string', widget: 'text', required: true, label: 'Client record (advisor-client id)' },
      { name: 'accountKey',   type: 'string', widget: 'text', required: true, label: 'Account key (see accountKey())' },
      { name: 'accountLabel', type: 'string', widget: 'text', required: true, label: 'Account label when marked' },
      { name: 'markedByUserId', type: 'string', widget: 'text', required: true, label: 'Marked by (appcore userId)' },
      { name: 'markedByName',   type: 'string', widget: 'text', required: true, label: 'Marked by (display name)' },
      { name: 'markedAt',     type: 'string', widget: 'text', required: true, label: 'Marked at (ISO timestamp)' },
    ],
    accessPatterns: [],
    presentation: {
      displayField: 'accountLabel',
      listColumns: ['clientId', 'accountLabel', 'markedAt'],
      defaultSort: { field: 'markedAt', direction: 'desc' },
    },
  },
  {
    // `client-info-requests` (crossmarc/steward-app#2280, "Information requests 2") — what the
    // firm is asking ONE client for. Full design in `clientInfoRequests.ts`; read it before
    // changing a flag below, because appcore's client-side read (`readRequestsRow`, crossmarc/appcore#826)
    // returns every stored attribute of this row to the CLIENT, unfiltered, and enforces none of
    // the declaration itself. `clientInfoRequests.test.ts` pins each flag in both directions.
    //
    //   - `clientId` is `unique`: the lookup item must name exactly one row, and a second row for
    //     a client is a 409 `UNIQUE_VALUE_TAKEN` at create (the façade re-reads and updates).
    //   - `accessPatterns` `by_clientId` on `clientId` is the name appcore's read looks up.
    //   - NO field is `sensitive` and the type is NOT `publishable`: neither is honoured on that
    //     read path (a publishable row rests at `draft`, which it refuses).
    //   - `schemaless`, NOT strict, on purpose: a strict `array` field compiles to
    //     `z.array(z.string())`, so these arrays of objects would 400 on every write (#1859).
    //     The façade's `ClientInfoRequestsAttributesSchema` is the validator, strict at every
    //     level, as `client-steward-setup` is. `unique` and `accessPatterns` do not depend on it.
    //
    // Same write shape and `accessControl` as `onboarding-mark`: the façade, under the firm
    // caller's own bearer token, only after `requireTeamAccess` (the team rule lives in the
    // façade, not appcore; a firm member holding `steward:write` could otherwise create the row
    // for a client they are not assigned to and hold the unique slot).
    typeCode: CLIENT_INFO_REQUESTS_TYPE_CODE,
    name: 'Client Information Requests',
    auditLevel: 'strict',
    schemaMode: 'schemaless',
    accessControl: { writeCapability: STEWARD_WRITE_CAPABILITY },
    fields: [
      { name: 'clientId', type: 'string', widget: 'text',     required: true, unique: true, label: 'Client record (advisor-client id)' },
      { name: 'accounts', type: 'array',  widget: 'textarea', required: true, label: 'Accounts the advisor added' },
      { name: 'requests', type: 'array',  widget: 'textarea', required: true, label: 'Requests' },
      { name: 'sends',    type: 'array',  widget: 'textarea', required: true, label: 'Sends' },
      { name: 'lastReminderAt',     type: 'string', widget: 'text', required: false, label: 'Last reminder sent at (ISO timestamp)' },
      { name: 'lastReminderByName', type: 'string', widget: 'text', required: false, label: 'Last reminder sent by (display name)' },
    ],
    accessPatterns: [{ name: CLIENT_INFO_REQUESTS_LOOKUP_PATTERN, fields: ['clientId'] }],
    presentation: {
      displayField: 'clientId',
      listColumns: ['clientId'],
      defaultSort: { field: 'clientId', direction: 'asc' },
    },
  },
  {
    // `client-archive` (crossmarc/steward-app#1863) — the firm's record that it removed a
    // sponsored client ("Remove client"), with the name it knew them by. Full design in
    // `clientArchive.ts`. Same write shape and the SAME `accessControl` as `onboarding-mark`:
    // the façade under the firm admin's own bearer token (gated there by `mem:manage` plus
    // `mayProvisionAtFacade`, `services/steward-app-api/src/handlers/clientRemove.ts`), never a
    // background role. No new capability, role or actor rule.
    //
    // `schemaMode` strict, and every field a flat string (#1859): appcore compiles a strict
    // `array` as `z.array(z.string())`. `packTypeDefs.test.ts` pins the rule. `strict` audit: who
    // removed a client, and when, is a record to keep.
    typeCode: CLIENT_ARCHIVE_TYPE_CODE,
    name: 'Client Archive',
    auditLevel: 'strict',
    accessControl: { writeCapability: STEWARD_WRITE_CAPABILITY },
    fields: [
      { name: 'firmId',            type: 'string', widget: 'text', required: true, label: 'Firm' },
      { name: 'clientId',          type: 'string', widget: 'text', required: true, label: 'Client record (advisor-client id)' },
      { name: 'clientWorkspaceId', type: 'string', widget: 'text', required: true, label: 'Family workspace whose sponsorship ended' },
      { name: 'displayName',       type: 'string', widget: 'text', required: true, label: 'Client name at removal' },
      { name: 'archivedAt',        type: 'string', widget: 'text', required: true, label: 'Archived at (ISO timestamp)' },
      { name: 'archivedByUserId',  type: 'string', widget: 'text', required: true, label: 'Archived by (appcore userId)' },
      { name: 'archivedByName',    type: 'string', widget: 'text', required: true, label: 'Archived by (display name)' },
    ],
    accessPatterns: [],
    presentation: {
      displayField: 'displayName',
      listColumns: ['displayName', 'archivedAt', 'archivedByName'],
      defaultSort: { field: 'archivedAt', direction: 'desc' },
    },
  },
  {
    // `client-steward-setup` (crossmarc/steward-app#1767) — the firm's record of the sponsored
    // Steward workspace it set up for one client record, and where it stands. Full design in
    // `clientStewardSetup.ts`: why a type of its own (a client may have no intake, and the
    // façade cannot write `advisor-client`), why it is found by `clientId` like
    // `onboarding-mark`, and why a duplicate row can never mean a second workspace (appcore's
    // provision is idempotent on `sourceRef` = the client id).
    //
    // Same write shape and the SAME `accessControl` as `onboarding-mark`: the façade under the
    // firm caller's own bearer token, gated there by `requireTeamAccess` plus
    // `mayProvisionAtFacade`, never a background role. No new capability, role or actor rule.
    //
    // Schemaless, like `advisor-intake`: `link` is the nested `IntakeClientWorkspaceLink`, and
    // `ClientStewardSetupAttributesSchema` is the validation authority (the façade parses on
    // every write and read). `strict` audit: who set up, cancelled or ended a family's
    // sponsorship is a record to keep.
    typeCode: CLIENT_STEWARD_SETUP_TYPE_CODE,
    name: 'Client Steward Setup',
    auditLevel: 'strict',
    schemaMode: 'schemaless',
    accessControl: { writeCapability: STEWARD_WRITE_CAPABILITY },
    fields: [
      { name: 'firmId',   type: 'string', widget: 'text',     required: true, label: 'Firm' },
      { name: 'clientId', type: 'string', widget: 'text',     required: true, label: 'Client record (advisor-client id)' },
      { name: 'link',     type: 'array',  widget: 'textarea', required: true, label: 'Workspace link ({workspaceId, state, ...})' },
    ],
    accessPatterns: [],
    presentation: {
      displayField: 'clientId',
      listColumns: ['firmId', 'clientId'],
      defaultSort: { field: 'clientId', direction: 'asc' },
    },
  },
  {
    // `client-form-closure` (crossmarc/steward-app#2550, crossmarc/appcore#910) — the firm's
    // record that it closed one of a client's two forms. Full design in `clientFormClosure.ts`.
    // Same write shape and the SAME `accessControl` as `client-archive`: the façade under the
    // firm caller's own bearer token, gated there by `requireTeamAccess`, never a background
    // role. No new capability, role or actor rule.
    //
    // 🚨 `closureKey` is `unique: true` and must stay so: appcore's two readers (the onboarding
    // gate and the intake writer's system read) find a closure ONLY through the uniqueness slot
    // that flag makes appcore write. Without it every closure reads as open, silently.
    // `clientFormClosure.test.ts` pins it.
    typeCode: CLIENT_FORM_CLOSURE_TYPE_CODE,
    name: 'Client Form Closure',
    auditLevel: 'strict',
    accessControl: { writeCapability: STEWARD_WRITE_CAPABILITY },
    fields: [
      { name: 'firmId',         type: 'string', widget: 'text', required: true, label: 'Firm' },
      { name: 'clientId',       type: 'string', widget: 'text', required: true, label: 'Client record (advisor-client id)' },
      { name: 'form',           type: 'string', widget: 'text', required: true, label: 'Form (snapshot or onboarding)' },
      { name: 'closureKey',     type: 'string', widget: 'text', required: true, unique: true, label: 'Closure key (<form>.<clientId>)' },
      { name: 'closedAt',       type: 'string', widget: 'text', required: true, label: 'Closed at (ISO timestamp)' },
      { name: 'closedByUserId', type: 'string', widget: 'text', required: true, label: 'Closed by (appcore userId)' },
      { name: 'closedByName',   type: 'string', widget: 'text', required: true, label: 'Closed by (display name)' },
    ],
    accessPatterns: [],
    presentation: {
      displayField: 'closureKey',
      listColumns: ['form', 'closedAt', 'closedByName'],
      defaultSort: { field: 'closedAt', direction: 'desc' },
    },
  },
  // crossmarc/steward-app#1889: the nightly firm net worth job's three types
  // (`firmNetWorthTypeDefs.ts`).
  ...FIRM_NW_TYPE_DEFS,
  // crossmarc/steward-app#2699 (docs/43 §3.2): the five client-record types (`clientRecordTypeDefs.ts`).
  ...CLIENT_RECORD_TYPE_DEFS,
];

/**
 * Maps the defs to the `POST /w/{ws}/types/seed` request item shape, defaulting
 * `schemaMode` to `strict` (the appcore default for an unspecified mode). Returns
 * plain objects ready to JSON-serialize as `{ types: [...] }`.
 */
export function toApiTypeDefs(): Array<Record<string, unknown>> {
  return STEWARD_APP_TYPE_DEFS.map((def) => {
    const { schemaMode = 'strict', ...rest } = def;
    return { schemaMode, ...rest };
  });
}

/**
 * The `status` a freshly-created instance of `typeCode` rests at, mirroring appcore's
 * `restingStatus` (appcore docs/02 §52).
 *
 * Only the direct-to-DynamoDB seed scripts need this. Everything else in steward-app
 * creates instances through appcore (the façade, or the `/system/*` plane for background
 * services), and appcore stamps the status itself — callers neither send nor control it.
 * The seed scripts bypass appcore entirely, so they are the one place that has to
 * reproduce the rule, and the one place that can get it wrong.
 *
 * The rule: a `publishable` type has a real editorial lifecycle and rests at `draft`;
 * every other type has no lifecycle at all, nothing will ever move the row again, and it
 * rests at `active`. Getting this backwards is not cosmetic — it is what made the AVM
 * refresher (which required `active`) value zero properties in every environment (#229).
 *
 * Derived from `STEWARD_APP_TYPE_DEFS` rather than hardcoded to `'active'`. No steward type
 * declares `publishable` today, so a hardcode would be correct right now and silently wrong
 * the first time one does — the exact shape of the bug this function exists to prevent.
 *
 * An unknown `typeCode` throws rather than defaulting. A seed script asking about a type it
 * has not declared is a bug in the script, and guessing "not publishable" would stamp
 * `active` on something that might have a publish lifecycle.
 *
 * ⚠️ These read the LOCAL declarations, which can lag the pack actually deployed to an
 * environment. `seed-steward-app.ts` upserts the pack in the same process, so it is
 * self-consistent; `seed-sample-data.ts` does not, so against a stale checkout it could in
 * principle stamp `active` on a type the deployed pack has since marked `publishable`. Not
 * reachable today (no steward type is publishable) but it is the honest caveat, and the
 * reason these are seed-script-only helpers rather than a general API.
 */

/** Reads the behaviors off a def, refusing to interpret a malformed value. */
function behaviorsOf(def: FoTypeDef): string[] {
  const raw = def['behaviors'];
  if (raw === undefined) return [];
  if (!Array.isArray(raw)) {
    // Deliberately a throw, not a `?? []`. `FoTypeDef`'s index signature accepts
    // `behaviors: 'publishable'` (a plausible typo) with no compiler complaint, and
    // swallowing it into `[]` would silently answer `active` — precisely the guess the
    // unknown-typeCode branch refuses to make. It would also DISAGREE with appcore, whose
    // `isPublishableType` calls `.includes` and so reads that same string as publishable.
    throw new Error(
      `steward type "${def.typeCode}" declares a non-array \`behaviors\` (${typeof raw}) — ` +
      `refusing to guess whether it is publishable.`,
    );
  }
  return raw as string[];
}

/** The resting status for an already-resolved def. Pure — the unit-testable core. */
export function restingStatusForDef(def: FoTypeDef): 'draft' | 'active' {
  return behaviorsOf(def).includes('publishable') ? 'draft' : 'active';
}

function defFor(typeCode: string, fn: string): FoTypeDef {
  const def = STEWARD_APP_TYPE_DEFS.find((d) => d.typeCode === typeCode);
  if (!def) {
    throw new Error(
      `${fn}: no steward type declares typeCode "${typeCode}" — ` +
      `cannot determine its behaviors, and guessing would risk marking ` +
      `unpublished content live. Add it to STEWARD_APP_TYPE_DEFS or fix the caller.`,
    );
  }
  return def;
}

export function restingStatusFor(typeCode: string): 'draft' | 'active' {
  return restingStatusForDef(defFor(typeCode, 'restingStatusFor'));
}

/**
 * The `currentVersion` a freshly-created instance rests at, mirroring appcore's
 * `create` (`currentVersion: versioned ? 1 : 0`).
 *
 * Same bug class as `restingStatusFor`, found in the same object literal: the seed scripts
 * hardcoded `0`, but `document` declares `versioned`, and appcore starts a versioned type at
 * 1 so its first update snapshots `#ver#00001`. A seeded document at 0 makes appcore's first
 * update write history to `#ver#0` while every appcore-created document starts at 1 — and the
 * seeded `s3Key` already says `/1/`, so the row was self-inconsistent at seed time.
 */
export function currentVersionFor(typeCode: string): 0 | 1 {
  return behaviorsOf(defFor(typeCode, 'currentVersionFor')).includes('versioned') ? 1 : 0;
}
export * from './clientRecordAccess.js';
export * from './clientRecordTypeDefs.js';
export * from './clientQuestionManifest.js';
export * from './clientAnswerScope.js';
export * from './clientAnswerVectors.js';
export * from './firmClientAnswers.js';
export * from './staffEntryConsent.js';
